// API client. Types come from openapi-typescript codegen against FastAPI's
// /openapi.json — see frontend/scripts/gen-api.mjs and the auto-generated
// `api.generated.ts`. Re-export the codegen types here under stable public
// names so consumer files don't depend on the schema's exact naming. Fetch
// wrappers + sessionStorage helpers stay hand-written.

import type { NavigateFunction } from "react-router-dom";

import type { components, operations } from "./api.generated";
import type { AnticheatConfig } from "./anticheatConfig";
import { clearAttemptAnswerDrafts } from "./answerDraft";
import type { IntakeField } from "@/lib/candidateIntake";
import type { CompletionNotificationMode } from "@/lib/completionNotifications";
import { SESSION_QUERY_KEY, type RecruiterSession } from "./auth";
import { parseContentDispositionFilename } from "./download";
import { captureApiError, captureSilentFailure, redactUrl } from "./observability";
import { queryClient } from "./queryClient";
import { isPlanRequired, markWalled, planRequiredMessage } from "./planWall";
import { isRoleDenied, type TeamRole } from "./roles";
import { queryKeys } from "./queryKeys";

import type { Language } from "@/lib/runtimes/registry";

type Schemas = components["schemas"];

// ── Domain & enums ──────────────────────────────────────────────────────
export type QuestionType = Schemas["QuestionType"];
export type Difficulty = Schemas["Difficulty"];
export const DIFFICULTIES: ReadonlyArray<Difficulty> = ["junior", "mid", "senior"];
export type AttemptStatus = Schemas["AttemptStatus"];
// RiskLevel is a Pydantic `Literal["clean", "flagged"]` so the codegen inlines
// it on every usage rather than emitting a named schema. Hand-aliased here so
// consumers can still import the name.
export type RiskLevel = "clean" | "flagged";
export type TestCase = Schemas["TestCase"];
export type QuestionSnapshot = Schemas["QuestionSnapshot"];

// ── Candidate flow ──────────────────────────────────────────────────────
export type StartResponse = Schemas["StartResponse"];
export type QuestionResponse = Schemas["QuestionResponse"];
export type SubmitResponse = Schemas["SubmitResponse"];
export type PreviewResponse = Schemas["PreviewResponse"];
// EP-20: the recruiter's full screening question (WITH the answer key) vs the
// candidate's copy (`/preview`, no key).
export type ScreeningQuestion = Schemas["ScreeningQuestion"];
export type ScreeningQuestionPublic = Schemas["ScreeningQuestionPublic"];
export type ReviewResponse = Schemas["ReviewResponse"];
export type ReviewAnswerItem = Schemas["ReviewAnswerItem"];
export type CandidateResult = Schemas["CandidateResultResponse"];
// Build B (OPS-14): EF-8 camera snapshots + EF-11 file answers.
export type CameraSnapshotConfig = Schemas["CameraSnapshotConfig"];
export type SavedFileInfo = Schemas["SavedFileInfo"];
export type FileUploadRules = Schemas["FileUploadRules"];
export type CameraSnapshotsInfo = Schemas["CameraSnapshotsInfo"];
export type SnapshotListResponse = Schemas["SnapshotListResponse"];
export type SnapshotItem = Schemas["SnapshotItem"];
// FEAT-31: code replay — the frozen shape, the attempt page's info block and
// one question's chunks.
export type CodeReplayConfig = Schemas["CodeReplayConfig"];
export type CodeReplayInfo = Schemas["CodeReplayInfo"];
export type CodeReplayResponse = Schemas["CodeReplayResponse"];
export type UploadedFileInfo = Schemas["UploadedFileInfo"];
// MVP 3.0 test sections. QuestionSection rides on QuestionResponse.section
// (null when non-sectioned); AdvanceSectionResponse comes back from
// advanceSection() at each section boundary.
export type QuestionSection = Schemas["QuestionSectionInfo"];
export type AdvanceSectionResponse = Schemas["AdvanceSectionResponse"];
// CP-18: finishSection() closes a section and starts a break (the pointer stays
// on the finished section; advanceSection() ends the break).
export type FinishSectionResponse = Schemas["FinishSectionResponse"];
// EF-2: pauseAttempt() freezes the clock on any question; resumeAttempt() ends
// the pause and shifts the deadline by exactly its length. ResumeInfo rides
// /preview for a PAUSED invitation attempt — the "come back with the same
// link, on any device" path.
export type PauseResponse = Schemas["PauseResponse"];
export type ResumeResponse = Schemas["ResumeResponse"];
export type ResumeInfo = Schemas["ResumeInfo"];
// fetchAttemptPosition(): where a RUNNING attempt re-opens (the gate's Begin
// after a reload or a reopened link).
export type PositionResponse = Schemas["PositionResponse"];

// ── Anti-cheat ──────────────────────────────────────────────────────────
// The codegen `EventIn.payload` is a strict discriminated union of every
// payload variant. The anticheat module emits payloads with a relaxed shape
// (typed inside the module itself) — re-typing it here as the strict union
// would force a refactor of the whole emitter. The backend validates the
// payload at ingest time, so the wrapper accepts the broader shape.
export interface AntiCheatEventIn {
  client_event_id: string;
  payload: Record<string, unknown>;
  client_t?: string;
  question_id?: string;
}

export type RiskSignals = Schemas["RiskSignals"];

// ── Admin — attempts ────────────────────────────────────────────────────
export type AttemptListItem = Schemas["AttemptListItem"];
export type PendingGradeJob = Schemas["PendingGradeJob"];
// FEAT-12: a runner-graded answer still waiting (never the browser's job).
export type ServerGradeJob = Schemas["ServerGradeJob"];
export type CandidateRunResponse = Schemas["CandidateRunResponse"];
export type RunCodeResponse = Schemas["RunCodeResponse"];
export type RegradeResponse = Schemas["RegradeResponse"];
export type AttemptListResponse = Schemas["AttemptListResponse"];
export type AttemptDetail = Schemas["AttemptDetail"];
export type AttemptDetailResponse = Schemas["AttemptDetailResponse"];
// MVP 3.0 "report a problem" — a candidate-submitted issue, shown read-only to
// the recruiter on the attempt-detail page.
export type ProblemReportOut = Schemas["ProblemReportOut"];
export type AnswerDetail = Schemas["AnswerDetail"];
export type EventOut = Schemas["EventOut"];
// FEAT-3 AI candidate summary. The rollup is deterministic and always present;
// `summary` is the persisted narrative and is null until a recruiter generates
// one (or when the model's output was refused by the server's trust boundary).
export type AttemptAiSummaryResponse = Schemas["AttemptAiSummaryResponse"];
export type AttemptSkillRollup = Schemas["AttemptSkillRollup"];
export type SkillBucket = Schemas["SkillBucket"];
export type AttemptAiSummary = Schemas["AttemptAiSummary"];
export type AiSummaryPoint = Schemas["AiSummaryPoint"];
// Hand-written: openapi-typescript INLINES a Pydantic Literal union rather than
// naming it (same as RiskLevel above), so there is no Schemas[...] to alias.
export type AiSummaryBlockedReason =
  | "not_submitted"
  | "no_signal"
  | "compliance_gate"
  | "unusable_output";
export type TestStats = Schemas["TestStats"];
export type QuestionComposition = Schemas["QuestionComposition"];
export type TestSnapshot = Schemas["TestSnapshot"];
export type TestSnapshotQuestion = Schemas["TestSnapshotQuestion"];

// ── Test creation ───────────────────────────────────────────────────────
export type McqQuestionInput = Schemas["McqQuestionIn"];
export type McqMultiQuestionInput = Schemas["McqMultiQuestionIn"];
export type TrueFalseQuestionInput = Schemas["TrueFalseQuestionIn"];
export type CodeQuestionInput = Schemas["CodeQuestionIn"];
export type ReferenceQuestionInput = Schemas["ReferenceQuestionIn"];
// EF-10: a prose answer, scored by hand (EF-5). Recruiter-authored only — the
// importers, the back-office console and the AI generator never emit it.
export type OpenEndedQuestionInput = Schemas["OpenEndedQuestionIn"];
// EF-11 (Build B): one file per answer, scored by hand.
export type FileUploadQuestionInput = Schemas["FileUploadQuestionIn"];
export type QuestionInput =
  | McqQuestionInput
  | McqMultiQuestionInput
  | TrueFalseQuestionInput
  | OpenEndedQuestionInput
  | CodeQuestionInput
  | FileUploadQuestionInput
  | ReferenceQuestionInput;
export type CreateTestRequest = Schemas["CreateTestRequest"];
export type CreateTestResponse = Schemas["CreateTestResponse"];

// ── AI test creation from a job description (MVP 4.0 item 4) ─────────────
// The assembler LLM maps a pasted JD onto our own controlled vocabulary
// (Global-Library tag names + the Difficulty enum); per-level availability
// counts are computed from our DB, never the model. `seniority` is null only
// on the degraded / empty-corpus responses (both still 200).
export type AnalyzeJdResponse = Schemas["AnalyzeJdResponse"];
export type TechnologyAvailability = Schemas["TechnologyAvailability"];
export type LevelCounts = Schemas["LevelCounts"];
export type BuildTestRequest = Schemas["BuildTestRequest"];
// Ready-to-Send Composer: one call drafts the test description + invitation
// intro in the workspace's voice AND carries the workspace pass-mark defaults
// (returned on every path, including degraded — the composer can't read them
// from /company-settings, which is owner-only).
export type DraftTestMessagingResponse = Schemas["DraftTestMessagingResponse"];

// ── AI question authoring (MVP 4.0 — generative authoring) ───────────────
// The second LLM surface: the model AUTHORS content (distractors / code tests /
// full question drafts) rather than mapping onto a closed vocabulary. All three
// degrade to `llm_available:false` (200) and return editable suggestions the
// recruiter reviews before anything is saved. Code tests are self-verified
// server-side against a reference solution (never sent back).
export type GenerateDistractorsResponse = Schemas["GenerateDistractorsResponse"];
export type GenerateTestsResponse = Schemas["GenerateTestsResponse"];
export type GeneratedTestCase = Schemas["GeneratedTestCase"];
export type GenerateQuestionsResponse = Schemas["GenerateQuestionsResponse"];
export type GeneratedMcqDraft = Schemas["GeneratedMcqDraft"];
export type GeneratedCodeDraft = Schemas["GeneratedCodeDraft"];
export type GeneratedQuestionDraft = GeneratedMcqDraft | GeneratedCodeDraft;

// Code-question review (FEAT-1b). Unlike the three above this AUTHORS nothing —
// it returns a report the recruiter reads. `findings[].source` says which layer
// produced each one: "static"/"execution" are facts we computed, "ai" is
// advisory (the server clamps model findings so they can never be severity
// "error"), so the UI can weight them honestly.
export type ReviewCodeResponse = Schemas["ReviewCodeResponse"];
export type ReviewFinding = Schemas["ReviewFinding"];
export type ExecutionReport = Schemas["ExecutionReport"];

// ── Workspace overview ──────────────────────────────────────────────────
export type OverviewResponse = Schemas["OverviewResponse"];

// ── Statistics dashboard (MVP 3.0 — "Stats" tab) ────────────────────────
// Company-wide rollup across all of a company's tests (activity + performance).
export type StatisticsResponse = Schemas["StatisticsResponse"];
export type StatsActivity = Schemas["StatsActivity"];
export type StatsPerformance = Schemas["StatsPerformance"];
export type ScoreBucket = Schemas["ScoreBucket"];
export type StatsTimeseriesPoint = Schemas["StatsTimeseriesPoint"];
export type StatsFunnel = Schemas["StatsFunnel"];
export type StatsPerTest = Schemas["StatsPerTest"];
// `window` is an inlined Literal in the schema (no named StatsWindow type) —
// derive the union from the response so the toggle + fetch share one source.
export type StatsWindow = StatisticsResponse["window"];

// ── Billing (MVP 2.0 #10) ───────────────────────────────────────────────
// Placeholder for the eventual Stripe-driven shape. Today every recruiter
// is on plan="Beta", status="active". The real-Stripe task (parked in
// FUTURE-MVP.md) replaces both the schema and the helper below.
export type BillingMeRead = Schemas["BillingMeRead"];
export type BillingConfigRead = Schemas["BillingConfigRead"];
export type BillingPublicConfigRead = Schemas["BillingPublicConfigRead"];
export type BillingPlanOption = Schemas["BillingPlanOption"];
export type BillingPortalRead = Schemas["BillingPortalRead"];
// FEAT-2 (MVP 5.5) — in-app plan change.
export type BillingSubscriptionRead = Schemas["BillingSubscriptionRead"];
export type PlanChangePreviewRead = Schemas["PlanChangePreviewRead"];
export type PlanChangeResultRead = Schemas["PlanChangeResultRead"];
export type PlanChangeLimitRead = Schemas["PlanChangeLimitRead"];
export type MoneyRead = Schemas["MoneyRead"];
export type PlanChangeHistoryRead = Schemas["PlanChangeHistoryRead"];

// ── Pending follow-ups (MVP 2.0 #5) ─────────────────────────────────────
export type PendingFollowUpItem = Schemas["PendingFollowUpItem"];
export type PendingFollowUpsResponse = Schemas["PendingFollowUpsResponse"];
// PendingInvitationItem is now embedded in AttemptListResponse as a sibling
// array — the unified lifecycle table on /admin/<test_id> renders attempts +
// pending invitations from a single fetch. The standalone per-test endpoint
// was removed.
export type PendingInvitationItem = Schemas["PendingInvitationItem"];
// UX-7: the deterministic per-test summary behind the results-page stat strip.
// Distinct from the company-wide StatisticsResponse below — same metric rules,
// one test, plus the pass/fail counts and average time the company rollup
// doesn't compute.
export type TestStatisticsResponse = Schemas["TestStatisticsResponse"];
// CP-9: one row of that response's per-question breakdown.
export type QuestionStat = Schemas["QuestionStat"];

// ── Questions library ───────────────────────────────────────────────────
// Backend uses `QuestionListItem` / `QuestionDetailResponse` / `QuestionListResponse`
// for the library endpoints; the frontend has historically prefixed them with
// `Library*`. Keep the public names so the 18 consumer files don't churn.
export type AttachedTest = Schemas["AttachedTest"];
export type LibraryQuestionListItem = Schemas["QuestionListItem"];
export type LibraryQuestionDetail = Schemas["QuestionDetailResponse"];
export type LibraryListResponse = Schemas["QuestionListResponse"];
export type TestQuestionsResponse = Schemas["TestQuestionsResponse"];
export type TestSettings = Schemas["TestSettingsResponse"];
export type UpdateTestRequest = Schemas["UpdateTestRequest"];

// ── Tags library ────────────────────────────────────────────────────────
export type TagRef = Schemas["TagRef"];
export type TagOut = Schemas["TagOut"];
export type TagListResponse = Schemas["TagListResponse"];
export type CreateTagRequest = Schemas["CreateTagRequest"];
export type UpdateTagRequest = Schemas["UpdateTagRequest"];

// ── Jobs (recruitment-process grouping) ─────────────────────────────────
export type JobRef = Schemas["JobRef"];
export type JobOut = Schemas["JobOut"];
export type JobListResponse = Schemas["JobListResponse"];
export type JobStatsResponse = Schemas["JobStatsResponse"];
export type CreateJobRequest = Schemas["CreateJobRequest"];
export type UpdateJobRequest = Schemas["UpdateJobRequest"];

// MVP 3.0 multi-tenancy: the protected "Unassigned" job is now PER-COMPANY —
// each company has exactly one job with `is_protected === true`, and its UUID
// is no longer a shared constant. Identify it by the `is_protected` flag on a
// loaded jobs list (the API returns it on every JobRef/JobOut). Callers that
// previously sent the hardcoded id now send an empty job set and let the
// backend default to the caller company's own Unassigned bucket.

// ── Frontend-only (NOT in OpenAPI schema) ───────────────────────────────
export interface CandidateMeta {
  candidate_name: string;
  test_name: string;
  // FEAT-25: the checks this attempt FROZE at /start (camera / fullscreen /
  // activity). Cached here because the screens that need it before any
  // response arrives have nothing else to read: a mid-test reload picks its
  // Gate step synchronously, and the break screen re-enters fullscreen from a
  // click. Read through `resolveAnticheat` — absent means all on.
  anticheat?: AnticheatConfig;
  // EF-8 (photo cadence, 2026-10-10): the snapshot schedule the attempt FROZE
  // at /start, or null. Review / SectionReview hold the attempt's one 30 s
  // schedule and show its caption, and GET /review does not carry it: the
  // Gate writes it and Question.tsx refreshes it from the question payload.
  camera_snapshots?: Schemas["CameraSnapshotConfig"] | null;
  // FEAT-38: whether /preview said interaction context is available for
  // this attempt (the platform flag ∧ the legal gate), cached at /start so
  // the anti-cheat controller can arm its fold synchronously on every
  // question mount. Never frozen — the server decides per summary.
  interaction_context?: boolean;
}

// ── Internal helpers ────────────────────────────────────────────────────
const ATTEMPT_HEADER = "X-Attempt-Id";

function attemptHeaders(attemptId: string): HeadersInit {
  return { [ATTEMPT_HEADER]: attemptId };
}

/**
 * Error carrying the numeric HTTP status of a failed API response. Lets callers
 * branch on `err.status` instead of substring-matching the message — a response
 * body that echoed a status-like token (e.g. "...API 409...") could otherwise
 * spoof the classification (see answerQueue.isTerminal). `message` keeps the
 * "API <status>: <body>" shape so existing message-based consumers + Sentry are
 * unaffected.
 */
export class ApiError extends Error {
  readonly status: number;
  /**
   * The response body's parsed `detail`, when the body was JSON. FastAPI's
   * structured errors (`{"detail": {"reason": ..., ...}}`) carry the machine
   * -readable discriminator a caller needs to tell one 410 from another — e.g.
   * the candidate Done screen distinguishing "your time ran out" (an expected
   * terminal state, friendly card) from a genuine submit failure. `undefined`
   * for a non-JSON body or a body with no `detail`; never throws while parsing.
   */
  readonly detail?: unknown;
  constructor(message: string, status: number, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

/** Best-effort `body.detail` for a JSON error body; undefined for anything else. */
function parseErrorDetail(body: string): unknown {
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed && typeof parsed === "object" && "detail" in parsed) {
      return (parsed as { detail: unknown }).detail;
    }
  } catch {
    // Non-JSON body (HTML error page, plain text) — no structured detail.
  }
  return undefined;
}

/** A recognised terminal time-limit 410. `timeLimitMessage` is the company's
 *  custom copy, or null for "render the default time-up text". */
export type TimeLimitReached = { timeLimitMessage: string | null };

/**
 * Recognise the TERMINAL time-limit 410 (backend `_time_limit_reached_error`):
 * the candidate's clock ran out, which is an EXPECTED end to an attempt, not a
 * failure. Returns null for every other error — a 500, a network error, or any
 * other 410 (including the NON-terminal `section_time_up`, which drives a
 * section transition) — so a genuine submit failure is never masked as a calm
 * "time's up" screen. An object rather than a bare string because null is a
 * MEANINGFUL match ("use the default copy") and must not read as "no match".
 */
export function parseTimeLimitReached(err: unknown): TimeLimitReached | null {
  if (!(err instanceof ApiError) || err.status !== 410) return null;
  const detail: unknown = err.detail;
  if (!detail || typeof detail !== "object") return null;
  const { reason, time_limit_message: message } = detail as {
    reason?: unknown;
    time_limit_message?: unknown;
  };
  if (reason !== "time_limit_reached") return null;
  return { timeLimitMessage: typeof message === "string" ? message : null };
}

/**
 * CP-6: was this refused because the question belongs to a higher plan?
 *
 * Returns the required tier (e.g. "Core") or null. Same narrowing shape as
 * `parseTimeLimitReached` — a 403 whose `detail.code` is `<TIER>_REQUIRED`,
 * which is what `entitlements.tiered_question_403` and `require_plan` both
 * emit. Callers use it to render an upgrade panel instead of a generic error;
 * the refusal itself is always the server's, never the UI's.
 */
export function parsePlanRequired(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 403) return null;
  const detail: unknown = err.detail;
  if (!detail || typeof detail !== "object") return null;
  const { code } = detail as { code?: unknown };
  if (typeof code !== "string") return null;
  const m = /^([A-Z]+)_REQUIRED$/.exec(code);
  if (!m) return null;
  // "CORE" -> "Core", matching entitlements.PLAN_ORDER's display casing.
  return m[1].charAt(0) + m[1].slice(1).toLowerCase();
}

/**
 * The server's own sentence for a structured refusal — `{"detail": {"code",
 * "message"}}`, e.g. EF-11's 409 FILE_UPLOAD_NOT_AVAILABLE or FEAT-12's 409
 * LANGUAGE_NOT_AVAILABLE — or null when the body carried none. A form shows
 * it instead of the raw `API 409: {…}` body that `ApiError.message` holds.
 */
export function serverErrorMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const detail: unknown = err.detail;
  if (!detail || typeof detail !== "object") return null;
  const { message } = detail as { message?: unknown };
  return typeof message === "string" && message.trim() ? message : null;
}

// Shared error path for jsonOrThrow + okOrThrow. redactUrl strips
// /api/admin/<token>/ + ?admin_tokens= patterns from the raw body (which may
// echo the failing URL via #0b's path-echo catch-all) and from the message
// itself before it lands in Sentry's exception value.
async function throwIfError(res: Response): Promise<void> {
  if (res.ok) return;
  const body = await res.text();
  const detail = parseErrorDetail(body);
  // FEAT-34 verify (2026-10-06): the plan wall's 402 carries a sentence for
  // people. Every caller toasts `err.message`, and the raw form read
  // `Couldn't duplicate: API 402: {"detail":{"code":"PLAN_REQUIRED",…`.
  const message = isPlanRequired(res.status, detail)
    ? planRequiredMessage(detail)
    : redactUrl(`API ${res.status}: ${body || res.statusText}`);
  const err = new ApiError(message, res.status, detail);
  captureApiError(err, { url: res.url, status: res.status });
  throw err;
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  await throwIfError(res);
  return (await res.json()) as T;
}

async function okOrThrow(res: Response): Promise<void> {
  await throwIfError(res);
}

/**
 * Like okOrThrow, but treats 404 as success: an already-gone DELETE converges
 * instead of erroring (double-click, or a stale list from a parallel session).
 * Each caller keeps its own "why 404 is benign here" comment at the call site.
 * Any other failure takes throwIfError's path, so a refusal keeps its parsed
 * `detail` (FEAT-24: the share-link bin's 409 sentence was dropped here).
 */
async function okOr404(res: Response): Promise<void> {
  if (res.status === 404) return;
  await throwIfError(res);
}

// Transient gateway statuses (Fly proxy in front of a cold-starting machine /
// Neon connection reset). These are infra blips, not app bugs — worth one or two
// quick retries on a read before surfacing anything to the user or Sentry.
const TRANSIENT_STATUS = new Set([502, 503, 504]);
// Fixed backoffs; length also caps the retry count (2 retries → 3 attempts max).
const RETRY_BACKOFF_MS = [300, 800];

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * `fetch` with automatic retry of transient gateway failures on IDEMPOTENT
 * requests only. A momentary 502/503/504 (or a network-level throw) on a GET/HEAD
 * self-heals here — so it never reaches `throwIfError`/`captureApiError`, keeping
 * Sentry's backlog to real errors. A persistent failure still returns/throws after
 * the retries are exhausted, so a real outage is not hidden.
 *
 * Non-idempotent methods (POST/DELETE/PATCH/PUT) are NEVER retried: a write that
 * got a 502 may have already landed server-side, so replaying it risks a
 * double-submit. Those pass through as a single attempt.
 */
export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const method = (init?.method ?? "GET").toUpperCase();
  const idempotent = method === "GET" || method === "HEAD";
  for (let attempt = 0; ; attempt++) {
    const canRetry = idempotent && attempt < RETRY_BACKOFF_MS.length;
    try {
      const res = await fetch(input, init);
      if (canRetry && TRANSIENT_STATUS.has(res.status)) {
        await sleep(RETRY_BACKOFF_MS[attempt]);
        continue;
      }
      return res;
    } catch (err) {
      // A thrown fetch is a network-level failure (TypeError: offline, DNS,
      // connection reset). Retry idempotent reads; otherwise rethrow.
      if (canRetry) {
        await sleep(RETRY_BACKOFF_MS[attempt]);
        continue;
      }
      throw err;
    }
  }
}

// ── Candidate flow wrappers ─────────────────────────────────────────────

export async function startAttempt(
  token: string,
  body: {
    name: string;
    email: string;
    consent_accepted: boolean;
    // CP-12: answers to the company's configurable sign-in fields, keyed by
    // field key, every value the raw input string. Omitted entirely when the
    // company asks nothing, so the wire body is unchanged for those tenants.
    intake?: Record<string, string>;
    // EF-4: the gate's browser-setup acknowledgement ("I have turned off my
    // browser extensions, or I'm using a private window"). The gate always
    // sends true — the step's Continue is disabled until it is ticked.
    extensions_confirmed?: boolean;
    // S1: this tab's start nonce (`startNonce`) — a retried click must carry
    // it for the server to hand back the attempt the lost response created.
    start_nonce?: string;
    // EF-3: sent only when the test offers the identity check. True = the
    // candidate pressed "I don't want to do this check". It never blocks a
    // start; it only decides what the recruiter reads when no vendor decision
    // exists. The check itself is found server-side — nothing here can claim
    // a result.
    identity_declined?: boolean;
  },
): Promise<StartResponse> {
  const res = await fetch(`/api/t/${token}/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

// EF-3 — the candidate's identity check (ID + selfie on the vendor's page).
// Deliberately coarse: the Gate never learns "failed" or "name differs" — the
// vendor's page has shown the person their result, and the recruiter reads
// ours. `url` is the vendor's hosted page while the check can still be done.
export type IdentityCheckStatus =
  | "not_started"
  | "pending"
  | "completed"
  | "unavailable";

export interface IdentityCheckView {
  status: IdentityCheckStatus;
  url: string | null;
}

// On a per-invitation link the server ignores both fields (the link is the
// identity); on a shared link the check is keyed by the typed EMAIL — the
// address /start binds the attempt to — and the typed name says which vendor
// comparison.
export interface IdentityCheckKey {
  name: string;
  email: string;
}

async function identityRequest(
  token: string,
  path: "session" | "status",
  key: IdentityCheckKey,
): Promise<IdentityCheckView> {
  const res = await fetch(`/api/t/${token}/identity/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // Pre-ship 2026-10-08 EF3-b/c: this tab's start nonce — the one /start
    // sends next — makes the checks it opens its own; the server answers a
    // shared-link check's page and status only to the tab that opened it.
    body: JSON.stringify({ ...key, start_nonce: startNonce(token) }),
  });
  const view = await jsonOrThrow<{ status: string; url?: string | null }>(res);
  return {
    status: view.status as IdentityCheckStatus,
    url: view.url ?? null,
  };
}

/** Create (or re-open) the check and get the vendor page to open. */
export function openIdentityCheck(
  token: string,
  key: IdentityCheckKey,
): Promise<IdentityCheckView> {
  return identityRequest(token, "session", key);
}

/** Where the check stands — polled from the Gate while it is pending. */
export function fetchIdentityCheckStatus(
  token: string,
  key: IdentityCheckKey,
): Promise<IdentityCheckView> {
  return identityRequest(token, "status", key);
}

export async function fetchQuestion(
  token: string,
  position: number,
  attemptId: string,
  // #122: a warm-up read that must not start the question's clock.
  opts: { prefetch?: boolean } = {},
): Promise<QuestionResponse> {
  const qs = opts.prefetch ? "?prefetch=1" : "";
  const res = await apiFetch(`/api/t/${token}/question/${position}${qs}`, {
    headers: attemptHeaders(attemptId),
  });
  return jsonOrThrow(res);
}

export async function submitAnswer(
  token: string,
  position: number,
  attemptId: string,
  body: {
    selected_answer?: string;
    selected_answers?: string[];
    final_code?: string;
    final_test_results?: Array<{ name: string; passed: boolean }>;
    // EF-10: the prose answer to an open_ended question.
    free_text?: string;
  },
  // The background save queue passes a per-attempt AbortSignal so a request that
  // stalls past its timeout is torn down — a detached slow POST must never land
  // late and clobber a newer answer for the same position. See answerQueue.ts.
  signal?: AbortSignal,
  // The save a few seconds before the deadline: stored, not finalized, so on a
  // forward-only test the candidate's own Submit can still land after it.
  opts: { provisional?: boolean } = {},
): Promise<{ ok: boolean }> {
  const qs = opts.provisional ? "?provisional=1" : "";
  const res = await fetch(`/api/t/${token}/answer/${position}${qs}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...attemptHeaders(attemptId) },
    body: JSON.stringify(body),
    signal,
  });
  return jsonOrThrow(res);
}

// ── EF-11: the candidate's file answer ──────────────────────────────────
// Its own routes beside /answer/{position}: the upload IS the save. No
// Content-Type on the multipart POST (the browser writes the boundary), and
// no retry wrapper — a repeated upload is harmless but wasteful.

export async function uploadAnswerFile(
  token: string,
  position: number,
  attemptId: string,
  file: File,
): Promise<SavedFileInfo | null> {
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await fetch(`/api/t/${token}/answer/${position}/file`, {
    method: "POST",
    headers: attemptHeaders(attemptId),
    body: form,
  });
  const body = await jsonOrThrow<{ ok: boolean; saved_file: SavedFileInfo | null }>(res);
  return body.saved_file;
}

export async function deleteAnswerFile(
  token: string,
  position: number,
  attemptId: string,
): Promise<void> {
  const res = await fetch(`/api/t/${token}/answer/${position}/file`, {
    method: "DELETE",
    headers: attemptHeaders(attemptId),
  });
  await jsonOrThrow(res);
}

// ── EF-8: one camera photo ───────────────────────────────────────────────
// Raw JPEG body. `keepalive` only under Chromium's 64 KiB in-flight budget
// (a busy 640-wide frame can exceed it, and a keepalive request over the
// budget is rejected outright). Never through apiFetch: a POST must not be
// retried by a generic wrapper — the monitor owns its one retry.
export type SnapshotUploadResult = "ok" | "stop" | "retry";

export async function uploadSnapshot(
  token: string,
  attemptId: string,
  blob: Blob,
  trigger: string,
  capturedAt: string,
  seq: number,
): Promise<SnapshotUploadResult> {
  let res: Response;
  try {
    res = await fetch(`/api/t/${token}/snapshots`, {
      method: "POST",
      headers: {
        "Content-Type": "image/jpeg",
        ...attemptHeaders(attemptId),
        "X-Snapshot-Trigger": trigger,
        "X-Snapshot-Captured-At": capturedAt,
        "X-Snapshot-Seq": String(seq),
      },
      body: blob,
      keepalive: blob.size <= 60_000,
    });
  } catch {
    return "retry";
  }
  if (res.ok) return "ok";
  // 404 = no photos for this attempt (or the platform switched off), 410 =
  // the attempt is over; 413 / 415 = a client bug no retry can fix.
  if (res.status === 404 || res.status === 410 || res.status === 413 || res.status === 415) {
    return "stop";
  }
  if (res.status >= 500) return "retry";
  // 429 (this photo is skipped), 409 (paused) and the rest: carry on.
  return "ok";
}

export async function submitAttempt(
  token: string,
  attemptId: string,
): Promise<SubmitResponse> {
  const res = await fetch(`/api/t/${token}/submit`, {
    method: "POST",
    headers: attemptHeaders(attemptId),
  });
  return jsonOrThrow(res);
}

// MVP 3.0 test sections — move a sectioned attempt to the next section. The
// candidate calls this when they finish a section or its timer hits 0 (after a
// best-effort save of the current answer). `finished: true` means there is no
// next section and the candidate should POST /submit instead.
export async function advanceSection(
  token: string,
  attemptId: string,
  expectedSectionIndex: number,
): Promise<AdvanceSectionResponse> {
  const res = await fetch(`/api/t/${token}/advance-section`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...attemptHeaders(attemptId) },
    body: JSON.stringify({ expected_section_index: expectedSectionIndex }),
  });
  return jsonOrThrow(res);
}

// CP-18: close the current section and start a between-section BREAK. Only
// legal on a test with `break_between_sections`; the server stamps the pause and
// every question read / answer write 409s `section_paused` until advanceSection()
// ends it. Same idempotency key as advanceSection. Callers must
// `await flushAnswerSaves()` FIRST — a save landing after the pause is refused
// and the queue drops it.
export async function finishSection(
  token: string,
  attemptId: string,
  expectedSectionIndex: number,
): Promise<FinishSectionResponse> {
  const res = await fetch(`/api/t/${token}/finish-section`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...attemptHeaders(attemptId),
    },
    body: JSON.stringify({ expected_section_index: expectedSectionIndex }),
  });
  return jsonOrThrow(res);
}

// EF-2: freeze the clock on `position` (the question on screen, where /resume
// lands the candidate). Only legal when the attempt froze `pause_enabled`
// (invitation link + the test's switch + the Core plan) — 409 `pause_unavailable`
// otherwise, 409 `section_paused` on a CP-18 break. Idempotent. Callers must
// `await flushAnswerSaves()` FIRST — a save landing after the pause is refused
// (409 `attempt_paused`) and the queue drops it.
export async function pauseAttempt(
  token: string,
  attemptId: string,
  position: number,
): Promise<PauseResponse> {
  const res = await fetch(`/api/t/${token}/pause`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...attemptHeaders(attemptId) },
    body: JSON.stringify({ position }),
  });
  return jsonOrThrow(res);
}

// EF-2: end the pause. The server measures it and shifts the ACTIVE deadline
// by exactly that length; past the resume window it answers 410
// `resume_window_expired` (the attempt is over). Idempotent (`resumed: false`
// when it was not paused). Called from the gate's Begin gesture, after the
// camera is back, so the clock restarts at the moment of re-entry.
export async function resumeAttempt(
  token: string,
  attemptId: string,
): Promise<ResumeResponse> {
  const res = await fetch(`/api/t/${token}/resume`, {
    method: "POST",
    headers: attemptHeaders(attemptId),
  });
  return jsonOrThrow(res);
}

// The question a RUNNING attempt re-opens on: the furthest one opened in the
// section the candidate is in now (its first, when none is). Read-only. The
// gate calls it on Begin when it found a live attempt id at mount — question 1
// is a locked section for anyone past section 1.
export async function fetchAttemptPosition(
  token: string,
  attemptId: string,
): Promise<PositionResponse> {
  const res = await apiFetch(`/api/t/${token}/position`, {
    headers: attemptHeaders(attemptId),
  });
  return jsonOrThrow(res);
}

export async function markCameraEnabled(
  token: string,
  attemptId: string,
): Promise<{ ok: boolean; camera_enabled: boolean }> {
  const res = await fetch(`/api/t/${token}/camera-enabled`, {
    method: "POST",
    headers: attemptHeaders(attemptId),
  });
  return jsonOrThrow(res);
}

// MVP 3.0 "report a problem" — a candidate flags an issue (preset categories +
// optional free text) before/at submit. Reachable post-submit too (the Done
// screen), so the backend deliberately doesn't gate on attempt status.
export async function reportProblem(
  token: string,
  attemptId: string,
  body: { categories: string[]; description?: string | null },
): Promise<{ ok: boolean }> {
  const res = await fetch(`/api/t/${token}/report-problem`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...attemptHeaders(attemptId) },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

// CP-20 candidate feedback — a submitted candidate's free text from the Done
// screen. One shot server-side (a second send 409s `feedback_already_given`);
// the per-test flag is read LIVE, so a 409 `feedback_disabled` means "closed",
// not "broken" — see lib/candidateFeedback.ts for the classifier.
export async function sendCandidateFeedback(
  token: string,
  attemptId: string,
  text: string,
): Promise<{ ok: boolean }> {
  const res = await fetch(`/api/t/${token}/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...attemptHeaders(attemptId) },
    body: JSON.stringify({ text }),
  });
  return jsonOrThrow(res);
}

// ── Pictures (EP-19 in questions, EP-15 on support reports) ──────────────
// The upload returns a NAME (`<sha256>.<ext>`) — the only thing a question or
// a report references it by. The bytes are re-encoded server-side and served
// back through our own origin; nothing here ever sees a storage URL.
export type UploadedImage = Schemas["UploadedImageResponse"];
export type ImageInfo = Schemas["ImageInfo"];

async function uploadPicture(url: string, file: File): Promise<UploadedImage> {
  const form = new FormData();
  form.append("file", file, file.name);
  // No Content-Type header on purpose — the browser writes the multipart
  // boundary (the `uploadBrandingLogo` rule).
  const res = await adminFetch(url, { method: "POST", body: form });
  return jsonOrThrow(res);
}

export function uploadQuestionImage(file: File): Promise<UploadedImage> {
  return uploadPicture(`/api/admin/question-images`, file);
}

export function uploadSupportAttachment(file: File): Promise<UploadedImage> {
  return uploadPicture(`/api/admin/support-attachments`, file);
}

/** A recruiter's own support-report screenshot (reporter-scoped). */
export function supportAttachmentUrl(reportId: string): string {
  return `/api/admin/support-requests/${reportId}/attachment`;
}

/** The same screenshot, for an operator in the back office. */
export function backofficeReportAttachmentUrl(reportId: string): string {
  return `/api/backoffice/problem-reports/${reportId}/attachment`;
}

// ── Recruiter "report a problem" (→ platform creator) — MVP 4.0 ───────────
export type PlatformProblemReportBody = Schemas["PlatformProblemReportIn"];
export type PlatformProblemReportResult = Schemas["PlatformProblemReportResponse"];

// Any logged-in recruiter can flag a platform problem; it's emailed to the
// creator AND stored for the Back office. Session-aware (adminFetch).
export async function reportPlatformProblem(
  body: PlatformProblemReportBody,
): Promise<PlatformProblemReportResult> {
  const res = await adminFetch(`/api/admin/report-problem`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

// The recruiter's own platform problem reports (with status + any operator
// reply) — the read-back half of the two-way support loop (MVP 4.0).
export type MySupportRequestRow = Schemas["MySupportRequestRow"];

export async function fetchMySupportRequests(): Promise<MySupportRequestRow[]> {
  const res = await adminFetch(`/api/admin/support-requests`);
  const body = (await jsonOrThrow(res)) as Schemas["MySupportRequestsResponse"];
  return body.items;
}

export async function fetchPreview(token: string): Promise<PreviewResponse> {
  const res = await apiFetch(`/api/t/${token}/preview`);
  return jsonOrThrow(res);
}

export async function fetchReview(
  token: string,
  attemptId: string,
): Promise<ReviewResponse> {
  const res = await apiFetch(`/api/t/${token}/review`, {
    headers: attemptHeaders(attemptId),
  });
  return jsonOrThrow(res);
}

export async function fetchCandidateResult(
  token: string,
  attemptId: string,
): Promise<CandidateResult> {
  const res = await apiFetch(`/api/t/${token}/result`, {
    headers: attemptHeaders(attemptId),
  });
  return jsonOrThrow(res);
}

// ── Anti-cheat events wrapper ───────────────────────────────────────────

export async function submitEvents(
  token: string,
  attemptId: string,
  events: AntiCheatEventIn[],
): Promise<{ ok: boolean; accepted: number; deduped: number }> {
  // Token-bound route (F2): the attempt is resolved against the token's test
  // server-side, so possession of a foreign attempt UUID alone can no longer
  // inject events.
  const res = await fetch(`/api/t/${token}/events`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...attemptHeaders(attemptId),
    },
    body: JSON.stringify({ events }),
  });
  return jsonOrThrow(res);
}

// Fire-and-forget a single anti-cheat event. Wraps the submitEvents envelope
// (fresh client_event_id + client_t) that the candidate-flow screens repeated
// inline, and swallows failures — anti-cheat telemetry must never block or
// interrupt the candidate flow. For the batched pipeline (page-unload
// sendBeacon, etc.) keep using submitEvents directly.
//
// Bounded retry: the attempt_started capability telemetry rides this path, and
// a single dropped POST used to freeze the recruiter's capability chips at a
// misleading "unknown / recorded before this check existed" for the whole
// attempt. The SAME client_event_id is reused across tries, so the server's
// (attempt_id, client_event_id) unique constraint dedupes an at-least-once
// duplicate; 429 honors Retry-After like the batched flusher
// (anticheat/index.ts); 409/410 mean the attempt is closed and retrying can
// never succeed. Still fully fire-and-forget — it never throws or blocks —
// but OPS-28 #15: giving up is no longer silent. An `attempt_started` probe or
// a `camera_started` lost after the three tries left the recruiter's chips at
// "unknown" / "Off" with nothing anywhere saying why; the exhaustion now
// reaches Sentry (event type + reason only — never the payload).
export function emitEvent(
  token: string,
  attemptId: string,
  payload: Record<string, unknown>,
): void {
  const body = JSON.stringify({
    events: [
      {
        client_event_id: crypto.randomUUID(),
        payload,
        client_t: new Date().toISOString(),
      },
    ],
  });
  const send = async (): Promise<void> => {
    const MAX_TRIES = 3;
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    for (let attempt = 1; ; attempt++) {
      try {
        const res = await fetch(`/api/t/${token}/events`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...attemptHeaders(attemptId),
          },
          body,
          // Survives a navigation away mid-retry (e.g. Gate → camera step).
          keepalive: true,
        });
        if (res.ok || res.status === 409 || res.status === 410) return;
        if (attempt >= MAX_TRIES) {
          captureSilentFailure("anticheat_event_dropped", {
            event_type: String(payload.type ?? ""),
            reason: "http",
            status: res.status,
            tries: attempt,
          });
          return;
        }
        const retryAfter =
          res.status === 429
            ? parseInt(res.headers.get("Retry-After") ?? "5", 10)
            : NaN;
        await sleep(
          Number.isFinite(retryAfter) ? retryAfter * 1000 : attempt * 2500,
        );
      } catch {
        if (attempt >= MAX_TRIES) {
          captureSilentFailure("anticheat_event_dropped", {
            event_type: String(payload.type ?? ""),
            reason: "network",
            tries: attempt,
          });
          return;
        }
        await sleep(attempt * 2500);
      }
    }
  };
  void send().catch(() => {});
}

// ── Admin wrappers ──────────────────────────────────────────────────────
//
// MVP 2.0 #2 / PR 3: every `/api/admin/*` route is session-required (cookie).
// `adminFetch` wraps fetch with `credentials: "include"` so we don't repeat
// it on every call. Admin tokens passed as path params accept either UUID
// test_id (preferred) or legacy plaintext admin_token — the backend dual-
// mode resolver tolerates both.

async function adminFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  // Route through apiFetch so a transient 502/503/504 on an admin GET (Fly proxy
  // in front of a cold-starting machine) self-heals with a retry. apiFetch only
  // retries idempotent methods, so admin writes (POST/DELETE/PATCH) still make a
  // single attempt — the 401 handling below is unaffected (401 is not transient).
  const res = await apiFetch(input, { ...init, credentials: "include" });
  // A 401 from any /api/admin/* route means the session is gone — the recruiter
  // logged out elsewhere, was removed from the team, or the cookie expired.
  // Null the cached session so RequireAuth/AdminLayout bounce to /login on the
  // next render instead of waiting out useSession's staleTime (the remove-member
  // dialog promises the session ends immediately). Safe: a logged-in recruiter
  // never gets a 401 from an admin route, so this only fires when truly signed out.
  if (res.status === 401) {
    queryClient.setQueryData(SESSION_QUERY_KEY, null);
  }
  // FEAT-28: a ROLE refusal means this tab's idea of the person's role is
  // stale — an owner changed it while the page was open, and `useSession`
  // would otherwise keep offering the old role's buttons for up to its
  // 5-minute staleTime. Refetch the session so the UI catches up; the caller
  // still gets the 403 it must handle. Read from a clone, never awaited.
  if (res.status === 403) {
    void refreshSessionOnRoleDenial(res.clone());
  }
  // FEAT-34: the plan wall is the server's. A 402 `PLAN_REQUIRED` while this
  // tab still believes the workspace has a plan means the free trial ran out
  // (or the plan lapsed) with the page open — refetch the session so
  // `RequirePlan` raises the wall now instead of after `useSession`'s
  // 5-minute staleTime. The caller still gets the 402 it must handle.
  if (res.status === 402) {
    void refreshSessionOnPlanRequired(res.clone());
  }
  return res;
}

async function refreshSessionOnPlanRequired(res: Response): Promise<void> {
  try {
    const body = (await res.json()) as { detail?: unknown };
    if (!isPlanRequired(res.status, body.detail)) return;
    // Only while the cached session says otherwise: a session that already
    // knows it is walled must not refetch on every refused call.
    const cached = queryClient.getQueryData<RecruiterSession | null>(
      SESSION_QUERY_KEY,
    );
    if (cached && cached.needs_plan !== true) {
      // Raise the wall NOW, from the server's own answer — waiting for the
      // refetch let the page's query retry and paint "Couldn't load …" first
      // (FEAT-34 verify, 2026-10-06). The refetch then brings the real copy.
      queryClient.setQueryData(SESSION_QUERY_KEY, markWalled(cached));
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    }
  } catch {
    /* not JSON — not the plan wall */
  }
}

async function refreshSessionOnRoleDenial(res: Response): Promise<void> {
  try {
    const body = (await res.json()) as { detail?: unknown };
    if (isRoleDenied(res.status, body.detail)) {
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    }
  } catch {
    /* not JSON — not a role refusal */
  }
}

// GET /api/admin/{id}/stats has no frontend consumer. The dashboard card used
// to call it once per card; those counts now ride the workspace list. The
// endpoint stays server-side (it is the single-test source of truth the list
// aggregate is tested against) — this fetcher was removed so the N+1 is not
// re-introduced by accident.

export async function archiveTest(testRef: string): Promise<void> {
  const res = await adminFetch(`/api/admin/${testRef}/archive`, {
    method: "POST",
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body || res.statusText}`);
  }
}

export async function unarchiveTest(testRef: string): Promise<void> {
  const res = await adminFetch(`/api/admin/${testRef}/unarchive`, {
    method: "POST",
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body || res.statusText}`);
  }
}

// UX-15 — open a draft to candidates. A separate route rather than a settings
// PATCH field, like CP-5's publishJob: publish state moves only through this
// door, which is also where a Basic draft spends its test slot (a 409
// TEST_LIMIT_REACHED, which `limitReachedMessage` renders). Idempotent.
export async function publishTest(testRef: string): Promise<void> {
  const res = await adminFetch(`/api/admin/${testRef}/publish`, {
    method: "POST",
  });
  await okOrThrow(res);
}

export async function deleteTest(testRef: string): Promise<void> {
  const res = await adminFetch(`/api/admin/${testRef}/settings`, {
    method: "DELETE",
  });
  // 404 on an already-deleted test is a no-op from the caller's perspective —
  // the test is "gone" either way, so we converge instead of bubbling an error
  // that would block the optimistic localStorage cleanup.
  await okOr404(res);
}

// FEAT-20: delete several tests in one request. Partial-success like
// `questionBulkAction` — every id lands in `accepted` or `rejected`, and the
// only reason is `not_found` (gone, a template, or outside the caller's
// recruitments: one answer for all, by design). An already-deleted test is
// accepted. Never touches the tests' questions. The server caps a batch at
// `TESTS_BULK_MAX` ids.
export const TESTS_BULK_MAX = 200;
export interface BulkDeleteTestsResponse {
  accepted: string[];
  rejected: { id: string; reason: "not_found" }[];
}

export async function deleteTestsBulk(
  ids: string[],
): Promise<BulkDeleteTestsResponse> {
  const res = await adminFetch("/api/admin/tests/delete-bulk", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ test_ids: ids }),
  });
  return jsonOrThrow(res);
}

export async function fetchTestSettings(
  testRef: string,
): Promise<TestSettings> {
  const res = await adminFetch(`/api/admin/${testRef}/settings`);
  return jsonOrThrow(res);
}

export async function updateTestSettings(
  testRef: string,
  body: UpdateTestRequest,
): Promise<TestSettings> {
  const res = await adminFetch(`/api/admin/${testRef}/settings`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

// ── Company settings (MVP 3.0, owner-only) ──────────────────────────────
// Per-company preferences surfaced on /admin/settings. `invitation_ttl_hours`
// is the validity window (in hours) stamped onto new candidate invitation
// links; `invitation_auto_resend_after_days` is the staleness threshold the
// auto-resend cron uses before sending one reminder. Hand-typed (mirrors
// TeamResponse) so typecheck doesn't depend on regenerating api.generated.ts;
// shapes match the backend Pydantic models.
export interface CompanySettings {
  company_id: string;
  company_name: string;
  invitation_ttl_hours: number;
  invitation_auto_resend_after_days: number;
  // MVP 4.0 Security tab: whether every recruiter in this workspace must use 2FA.
  require_two_factor: boolean;
  // MVP 4.5 (UX-6) Results tab: the pass-mark defaults a NEW test starts with.
  // Copy-at-create — changing these never touches an existing test.
  default_pass_mark_enabled: boolean;
  default_pass_threshold_pct: number;
  // MVP 5.5 (FEAT-6) AI tab: whether the AI test-builder sidebar shows on
  // /admin/new for everyone in this workspace. Mirrored to every member's
  // session as `ai_suggestions_enabled` (see lib/auth.ts).
  ai_suggestions_enabled: boolean;
  // MVP 5.0 (CP-2) Branding tab: the company's legal identity, collected at
  // signup. NULL on every company that predates CP-2 — this card is how they
  // fill it in, and how a tax-number typo gets fixed.
  tax_number: string | null;
  address_street: string | null;
  address_city: string | null;
  address_postal_code: string | null;
  address_country: string | null; // ISO 3166-1 alpha-2, upper-case
  // MVP 5.0 (CP-12) Candidate form tab: the extra details every candidate
  // must give before starting a test. [] = name + email only.
  candidate_intake_fields: IntakeField[];
  // MVP 5.0 (CP-24) Notifications tab: what the team is emailed when a
  // candidate completes a test. `completion_digest_last_sent_at` is read-only
  // (a naive-UTC ISO string, no Z): the digest CLOCK — where the next daily
  // summary's window starts (set on entering daily mode, moved by every
  // run) — or null before either.
  completion_notification_mode: CompletionNotificationMode;
  completion_digest_last_sent_at: string | null;
}

// Partial update — send only the field that changed; the backend requires at
// least one and validates bounds per-field.
export interface UpdateCompanySettingsRequest {
  invitation_ttl_hours?: number;
  invitation_auto_resend_after_days?: number;
  require_two_factor?: boolean;
  default_pass_mark_enabled?: boolean;
  default_pass_threshold_pct?: number;
  // FEAT-6 — not plan-gated in either direction (off must always work; on
  // grants nothing, the sidebar itself renders only when the plan allows).
  ai_suggestions_enabled?: boolean;
  // CP-2. EP-2 (2026-09-17) retired the tri-state this used to carry: omitted =
  // "leave it alone", anything else = set it, and "" is now REFUSED. The tax
  // number is required at registration and printed on invoices, so it can be
  // corrected but not removed — the same shape the address has. Send no key at
  // all for a pre-CP-2 workspace that has none.
  tax_number?: string;
  // The four address fields are one card and must be sent together — the
  // backend rejects a partial address rather than storing a city with no
  // street, which would only surface at invoicing.
  address_street?: string;
  address_city?: string;
  address_postal_code?: string;
  address_country?: string;
  // CP-12 — PUT semantics for this one field: the card always sends the whole
  // list, and [] means "ask nothing" (omit the key to leave it untouched).
  candidate_intake_fields?: IntakeField[];
  // CP-24 — a closed set; anything else is a 422. Switching INTO daily_digest
  // resets the digest clock server-side, so the first summary covers only
  // what happens after the save.
  completion_notification_mode?: CompletionNotificationMode;
}

export async function fetchCompanySettings(): Promise<CompanySettings> {
  const res = await adminFetch(`/api/admin/company-settings`);
  return jsonOrThrow(res);
}

export async function updateCompanySettings(
  body: UpdateCompanySettingsRequest,
): Promise<CompanySettings> {
  const res = await adminFetch(`/api/admin/company-settings`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

export type CompanyLegalDetailsRequest = Schemas["CompanyLegalDetailsRequest"];

// FEAT-45 — the owner records the company's legal identity (tax number + the
// four address fields, all required) in one go, from the plan wall's
// company-details step. Sign-up no longer collects them, and the billing
// config withholds the checkout link until they exist. Owner-only, and the
// one settings write the server leaves OPEN behind the plan wall.
export async function putCompanyDetails(
  body: CompanyLegalDetailsRequest,
): Promise<CompanySettings> {
  const res = await adminFetch(`/api/admin/company-details`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

// Owner renames their own workspace. The name is globally unique, so this can
// fail with WORKSPACE_NAME_TAKEN even though the caller owns the workspace.
// Callers must refresh the session query — the sidebar label reads from it.
export async function renameWorkspace(
  name: string,
): Promise<{ name: string }> {
  const res = await adminFetch(`/api/admin/workspace/name`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  return jsonOrThrow(res);
}

// Owner self-serve workspace closure (MVP 4.0). Reversible suspend of the owner's
// OWN company; the server kills every session, so the caller hard-redirects to
// the sign-in screen on success.
export async function closeWorkspace(
  reason: string,
): Promise<{ closed: boolean }> {
  const res = await adminFetch(`/api/admin/workspace/close`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason, confirm: true }),
  });
  return jsonOrThrow(res);
}

// ── Email branding (MVP 4.0, owner-only) ────────────────────────────────
// Per-company email branding configured on /admin/settings: logo, brand colour,
// signature, sender identity (from-name + reply-to), per-message custom text,
// and the candidate Done-screen completion message. Types come straight from the
// generated OpenAPI schema; the logo bytes are served by the public
// /api/branding/{id}/logo endpoint (URL returned as `logo_url`, ?v=hash busts).
export type BrandingSettings = Schemas["BrandingResponse"];
export type UpdateBrandingRequest = Schemas["UpdateBrandingRequest"];
export type LogoUploadResult = Schemas["LogoUploadResponse"];
export type BrandingPreviewResponse = Schemas["BrandingPreviewResponse"];
// A preview draft is a PATCH body plus an optional, preview-only
// `invitation_ttl_hours` — the Messages tab passes the owner's in-progress
// (unsaved) link-expiry so the previewed "expires in N days" line tracks the
// edit live. Never persisted; the server falls back to the saved window when
// it's omitted.
export type BrandingPreviewRequest = Schemas["BrandingPreviewRequest"];
// The email kinds the preview endpoint accepts — read off the codegen, not
// retyped: a kind the backend adds reaches the preview tabs and the Messages
// tab the moment the types are regenerated, and renaming the FastAPI handler
// breaks this build loudly, which is the point (SIMP-17, 2026-08-18).
export type EmailPreviewType =
  operations["preview_branding_api_admin_branding_preview_post"]["parameters"]["query"]["email_type"];

export async function fetchBranding(): Promise<BrandingSettings> {
  const res = await adminFetch(`/api/admin/branding`);
  return jsonOrThrow(res);
}

export async function updateBranding(
  body: UpdateBrandingRequest,
): Promise<BrandingSettings> {
  const res = await adminFetch(`/api/admin/branding`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

export async function uploadBrandingLogo(file: File): Promise<LogoUploadResult> {
  const form = new FormData();
  form.append("file", file);
  // Deliberately NO Content-Type header — the browser sets the multipart
  // boundary. adminFetch adds credentials: include.
  const res = await adminFetch(`/api/admin/branding/logo`, {
    method: "POST",
    body: form,
  });
  return jsonOrThrow(res);
}

export async function deleteBrandingLogo(): Promise<BrandingSettings> {
  const res = await adminFetch(`/api/admin/branding/logo`, { method: "DELETE" });
  return jsonOrThrow(res);
}

// ── EF-1: the candidate welcome (video + company message) ──────────────────
// Its own resource beside the email branding (Pro, not Core), so an upload
// never refetches the whole branding card and the two tiers stay apart.
export type WelcomeSettings = Schemas["WelcomeSettingsResponse"];
export type UpdateWelcomeRequest = Schemas["UpdateWelcomeRequest"];
// Same-origin, cookie-authed; the server appends `?v=<hash>` on its own copy.
export const WELCOME_VIDEO_ADMIN_URL = "/api/admin/branding/welcome-video";

export async function fetchWelcomeSettings(): Promise<WelcomeSettings> {
  const res = await adminFetch(`/api/admin/branding/welcome`);
  return jsonOrThrow(res);
}

export async function updateWelcomeSettings(
  body: UpdateWelcomeRequest,
): Promise<WelcomeSettings> {
  const res = await adminFetch(`/api/admin/branding/welcome`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

export async function uploadWelcomeVideo(file: File): Promise<WelcomeSettings> {
  const form = new FormData();
  form.append("file", file);
  // No Content-Type header on purpose — the browser writes the multipart
  // boundary (the `uploadBrandingLogo` rule). The body is never read into
  // memory on the server: it streams from the spooled part to the store.
  const res = await adminFetch(WELCOME_VIDEO_ADMIN_URL, {
    method: "POST",
    body: form,
  });
  return jsonOrThrow(res);
}

export async function deleteWelcomeVideo(): Promise<WelcomeSettings> {
  const res = await adminFetch(WELCOME_VIDEO_ADMIN_URL, { method: "DELETE" });
  return jsonOrThrow(res);
}

// ── EF-8: the recruiter's snapshot gallery ──────────────────────────────
// The list is JSON; each photo is a same-origin, cookie-authed `<img src>`
// (the URL the list carries) — no adminFetch needed for the bytes.
export async function fetchAttemptSnapshots(
  adminToken: string,
  attemptId: string,
): Promise<SnapshotListResponse> {
  const res = await adminFetch(
    `/api/admin/${adminToken}/attempts/${attemptId}/snapshots`,
  );
  return jsonOrThrow(res);
}

// ── FEAT-31: one question's code replay, for the attempt page's player ────
// Fetched on demand (the recruiter opens the player); `no-store` on the server.
export async function fetchAttemptReplay(
  adminToken: string,
  attemptId: string,
  position: number,
): Promise<CodeReplayResponse> {
  const res = await adminFetch(
    `/api/admin/${adminToken}/attempts/${attemptId}/replay/${position}`,
  );
  return jsonOrThrow(res);
}

export async function fetchBrandingPreview(
  emailType: EmailPreviewType,
  draft: BrandingPreviewRequest,
): Promise<BrandingPreviewResponse> {
  const res = await adminFetch(
    `/api/admin/branding/preview?email_type=${emailType}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(draft),
    },
  );
  return jsonOrThrow(res);
}

// Export field customization (MVP 4.0). Owner-configured, per-company selection
// of which columns the candidate-results export includes, per report level.
// A JSON endpoint (unlike the binary results export), hand-typed here alongside
// the other export types. Applies to the CSV + Excel exports only.
export type ExportFieldInfo = {
  key: string;
  label: string;
  group: string; // "candidate" | "integrity" | "question"
  mandatory: boolean;
};
export type ExportSettings = {
  summary_fields: string[];
  detailed_fields: string[];
  summary_catalog: ExportFieldInfo[];
  detailed_catalog: ExportFieldInfo[];
  default_summary_fields: string[];
  default_detailed_fields: string[];
};
export type UpdateExportSettingsRequest = {
  summary_fields?: string[];
  detailed_fields?: string[];
};

export async function fetchExportSettings(): Promise<ExportSettings> {
  const res = await adminFetch(`/api/admin/export-settings`);
  return jsonOrThrow(res);
}

export async function updateExportSettings(
  body: UpdateExportSettingsRequest,
): Promise<ExportSettings> {
  const res = await adminFetch(`/api/admin/export-settings`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

// ── Integrations (MVP 4.0 item 7) — per-company API keys + signed webhooks.
// Owner-only Settings → Integrations tab. Hand-typed (like ExportSettings) so
// typecheck doesn't depend on regenerating api.generated.ts. Secrets
// (plaintext_key / signing_secret) are returned by the server ONCE, on
// create/rotate only — never by the list endpoints.
const INTEGRATIONS_BASE = "/api/admin/integrations";

export type ApiKeySummary = {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  /** 10-07 S4: who minted it (keys are personal); null/absent = unknown. */
  created_by_name?: string | null;
};
export type ApiKeyCreated = ApiKeySummary & { plaintext_key: string };

/**
 * Consumer discriminator; drives the outbound message format at delivery.
 * "make" signs exactly like "generic"/"zapier" — it exists purely so adoption
 * can tell a Make scenario from a hand-rolled webhook.
 */
export type WebhookKind = "generic" | "slack" | "zapier" | "make";

export type WebhookSummary = {
  id: string;
  /**
   * Widen on read: rows created before a kind existed keep their old value, and
   * a removed consumer ("teams", dropped 2026-08-04) leaves readable rows
   * behind. Narrowing this to WebhookKind would let a legacy row fall through
   * every filter and become unmanageable in the UI.
   */
  kind: WebhookKind | (string & {});
  url: string;
  subscribed_events: string[];
  description: string | null;
  created_at: string;
  revoked_at: string | null;
  /** 10-07 S4: who added it (for a REST hook, who minted its key);
   *  null/absent = unknown. */
  created_by_name?: string | null;
};
export type WebhookCreated = WebhookSummary & { signing_secret: string };

export type WebhookDeliverySummary = {
  id: string;
  event_type: string;
  event_id: string;
  status: string;
  attempt_count: number;
  response_status: number | null;
  error: string | null;
  created_at: string;
  delivered_at: string | null;
  next_attempt_at: string | null;
};
export type WebhookTestResult = { delivery_id: string; status: string };

export async function fetchWebhookEventTypes(): Promise<string[]> {
  const res = await adminFetch(`${INTEGRATIONS_BASE}/event-types`);
  const data = await jsonOrThrow<{ events: string[] }>(res);
  return data.events;
}

export async function fetchApiKeys(): Promise<ApiKeySummary[]> {
  const res = await adminFetch(`${INTEGRATIONS_BASE}/keys`);
  return jsonOrThrow(res);
}
export async function createApiKey(
  name: string,
  scopes?: string[],
): Promise<ApiKeyCreated> {
  const res = await adminFetch(`${INTEGRATIONS_BASE}/keys`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(scopes ? { name, scopes } : { name }),
  });
  return jsonOrThrow(res);
}
export async function rotateApiKey(id: string): Promise<ApiKeyCreated> {
  const res = await adminFetch(`${INTEGRATIONS_BASE}/keys/${id}/rotate`, {
    method: "POST",
  });
  return jsonOrThrow(res);
}
export async function revokeApiKey(id: string): Promise<ApiKeySummary> {
  const res = await adminFetch(`${INTEGRATIONS_BASE}/keys/${id}/revoke`, {
    method: "POST",
  });
  return jsonOrThrow(res);
}

// ── FEAT-47 (MVP 8.0): the MCP connector ─────────────────────────────────
// Hand-written like ApiKeySummary (the codegen is not re-run on the branch).
// The card: `GET /api/admin/mcp/connections` (own rows; the owner: every
// row of the company) and `DELETE …/{id}`. The consent page: the three
// `/api/mcp/oauth/requests/{rid}` routes (session cookie + same-origin CSRF).
export type McpConnectionSummary = {
  id: string;
  client_name: string;
  client_kind: "cimd" | "dynamic" | "confidential" | string;
  /** R1: the operator vouches for this app's identity (a CIMD host in
   *  `MCP_CIMD_TRUSTED_HOSTS`, or the confidential client). */
  client_verified: boolean;
  /** FEAT-47f: the host of a CIMD `client_id` URL (`chatgpt.com`,
   *  `claude.ai`), null for a dynamic or partner client — how the card files
   *  the row under its app (`assistantAppFor`). */
  client_id_host: string | null;
  person: { id: string; name: string; email: string };
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  calls_today: number;
  mine: boolean;
};

export async function fetchMcpConnections(): Promise<McpConnectionSummary[]> {
  return jsonOrThrow(await adminFetch(`/api/admin/mcp/connections`));
}

// FEAT-47c: one connection's last tool calls — the activity rows every call
// writes before it runs (metadata only: tool, when, how long, ok / error code).
export type McpToolCallSummary = {
  id: string;
  tool: string;
  rate_class: string;
  called_at: string;
  duration_ms: number | null;
  ok: boolean | null;
  error_code: string | null;
  target_type: string | null;
  target_id: string | null;
};

export async function fetchMcpConnectionActivity(
  id: string,
  limit = 50,
): Promise<McpToolCallSummary[]> {
  return jsonOrThrow(
    await adminFetch(`/api/admin/mcp/connections/${id}/activity?limit=${limit}`),
  );
}

export async function revokeMcpConnection(id: string): Promise<void> {
  const res = await adminFetch(`/api/admin/mcp/connections/${id}`, {
    method: "DELETE",
  });
  await okOrThrow(res);
}

export type McpAuthorizeRequest = {
  rid: string;
  client_name: string;
  client_kind: "cimd" | "dynamic" | "confidential" | string;
  /** R1: the host of a CIMD client's `client_id` URL — the one identity fact
   *  the app could not choose; null for a dynamic or the confidential client. */
  client_id_host: string | null;
  /** R1: true only when CandidCheck vouches for the identity shown
   *  (`MCP_CIMD_TRUSTED_HOSTS` names the host, or the confidential client);
   *  `client_name` and `client_uri_host` are the app's own words. */
  client_verified: boolean;
  client_uri_host: string | null;
  redirect_host: string;
  loopback_only: boolean;
  scopes_requested: string[];
  scopes_grantable: string[];
  person: { name: string; email: string; role: string };
  workspace: { name: string };
  available: boolean;
  refusal: string | null;
  expires_at: string;
};

export async function fetchMcpAuthorizeRequest(
  rid: string,
): Promise<McpAuthorizeRequest> {
  return jsonOrThrow(
    await adminFetch(`/api/mcp/oauth/requests/${encodeURIComponent(rid)}`),
  );
}

export async function approveMcpAuthorizeRequest(
  rid: string,
): Promise<{ redirect_to: string }> {
  return jsonOrThrow(
    await adminFetch(`/api/mcp/oauth/requests/${encodeURIComponent(rid)}/approve`, {
      method: "POST",
    }),
  );
}

export async function denyMcpAuthorizeRequest(
  rid: string,
): Promise<{ redirect_to: string }> {
  return jsonOrThrow(
    await adminFetch(`/api/mcp/oauth/requests/${encodeURIComponent(rid)}/deny`, {
      method: "POST",
    }),
  );
}

export async function fetchWebhooks(): Promise<WebhookSummary[]> {
  const res = await adminFetch(`${INTEGRATIONS_BASE}/webhooks`);
  return jsonOrThrow(res);
}
export async function createWebhook(body: {
  url: string;
  subscribed_events: string[];
  description?: string | null;
  kind?: WebhookKind;
}): Promise<WebhookCreated> {
  const res = await adminFetch(`${INTEGRATIONS_BASE}/webhooks`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}
export async function updateWebhook(
  id: string,
  body: { url?: string; subscribed_events?: string[]; description?: string | null },
): Promise<WebhookSummary> {
  const res = await adminFetch(`${INTEGRATIONS_BASE}/webhooks/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}
export async function rotateWebhookSecret(id: string): Promise<WebhookCreated> {
  const res = await adminFetch(
    `${INTEGRATIONS_BASE}/webhooks/${id}/rotate-secret`,
    { method: "POST" },
  );
  return jsonOrThrow(res);
}
export async function revokeWebhook(id: string): Promise<WebhookSummary> {
  const res = await adminFetch(`${INTEGRATIONS_BASE}/webhooks/${id}/revoke`, {
    method: "POST",
  });
  return jsonOrThrow(res);
}
export async function sendWebhookTest(id: string): Promise<WebhookTestResult> {
  const res = await adminFetch(`${INTEGRATIONS_BASE}/webhooks/${id}/test`, {
    method: "POST",
  });
  return jsonOrThrow(res);
}
export async function fetchWebhookDeliveries(
  id: string,
  limit = 50,
): Promise<WebhookDeliverySummary[]> {
  const res = await adminFetch(
    `${INTEGRATIONS_BASE}/webhooks/${id}/deliveries?limit=${limit}`,
  );
  return jsonOrThrow(res);
}
export async function retryWebhookDelivery(
  id: string,
): Promise<WebhookTestResult> {
  const res = await adminFetch(
    `${INTEGRATIONS_BASE}/deliveries/${id}/retry`,
    { method: "POST" },
  );
  return jsonOrThrow(res);
}

// ── Slack "Add to Slack" OAuth connector (FEAT-4) ───────────────────────
//
// Deliberately a RELATIVE path used as an <a href>, not a fetch. Two reasons:
// the endpoint 302s to slack.com, which XHR can't usefully follow, and the CSP
// `connect-src 'self'` would block the request anyway — a top-level navigation
// is unrestricted. Relative works in dev too: vite.config.ts proxies /api to
// the backend container, so this needs no VITE_API_BASE handling.
export const SLACK_OAUTH_START_URL = `${INTEGRATIONS_BASE}/slack/oauth/start`;

/** Whether the Slack app credentials are configured server-side. Can only
 *  change on redeploy, so the caller caches it indefinitely. */
export async function fetchSlackOAuthStatus(): Promise<{ available: boolean }> {
  const res = await adminFetch(`${INTEGRATIONS_BASE}/slack/oauth/status`);
  return jsonOrThrow(res);
}

// ── ATS integration (Traffit, MVP 4.0 item 7) ───────────────────────────
// Owner-only. Hand-typed like the integrations block above. The connect
// response never carries any secret (client secret / tokens live encrypted
// server-side; the inbound webhook token is registered with Traffit
// server-side and never displayed).
const ATS_BASE = "/api/admin/ats";

export type AtsConnectionSummary = {
  id: string;
  provider: string;
  instance_name: string;
  client_id: string;
  created_at: string;
  last_error: string | null;
};

export type AtsTriggerType = "on_apply" | "on_stage_move";

export type AtsMappingSummary = {
  id: string;
  recruitment_id: string;
  recruitment_name: string | null;
  state_id: string | null;
  state_name: string | null;
  trigger_type: AtsTriggerType;
  test_id: string;
  test_name: string | null;
  // False when the mapped test was archived/deleted — the rule matches events
  // but every auto-invite skips `test_unavailable`, so the UI flags it.
  test_available: boolean;
  active: boolean;
  created_at: string;
};

export type AtsStatus = {
  connection: AtsConnectionSummary | null;
  mappings: AtsMappingSummary[];
};

export type TraffitRecruitment = {
  id: string;
  name: string;
  is_closed: boolean;
};
export type TraffitState = { id: string; name: string; type: string | null };

export async function fetchAtsStatus(): Promise<AtsStatus> {
  const res = await adminFetch(ATS_BASE);
  return jsonOrThrow(res);
}
export async function connectTraffit(body: {
  instance_name: string;
  client_id: string;
  client_secret: string;
}): Promise<AtsConnectionSummary> {
  const res = await adminFetch(`${ATS_BASE}/traffit/connect`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}
export async function disconnectTraffit(): Promise<void> {
  const res = await adminFetch(`${ATS_BASE}/traffit`, { method: "DELETE" });
  await okOrThrow(res);
}
export async function fetchTraffitRecruitments(): Promise<
  TraffitRecruitment[]
> {
  const res = await adminFetch(`${ATS_BASE}/traffit/recruitments`);
  return jsonOrThrow(res);
}
export async function fetchTraffitStates(
  recruitmentId: string,
): Promise<TraffitState[]> {
  const res = await adminFetch(
    `${ATS_BASE}/traffit/recruitments/${encodeURIComponent(recruitmentId)}/states`,
  );
  return jsonOrThrow(res);
}
export async function createAtsMapping(body: {
  recruitment_id: string;
  recruitment_name?: string | null;
  trigger_type: AtsTriggerType;
  state_id?: string | null;
  state_name?: string | null;
  test_id: string;
}): Promise<AtsMappingSummary> {
  const res = await adminFetch(`${ATS_BASE}/mappings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}
export async function updateAtsMapping(
  id: string,
  body: { test_id?: string; active?: boolean },
): Promise<AtsMappingSummary> {
  const res = await adminFetch(`${ATS_BASE}/mappings/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}
export async function deleteAtsMapping(id: string): Promise<void> {
  const res = await adminFetch(`${ATS_BASE}/mappings/${id}`, {
    method: "DELETE",
  });
  await okOrThrow(res);
}

// Activity & health feed on the dedicated Traffit page. Read-only, company-
// scoped, capped server-side. Events = inbound (auto-invites + skips); notes =
// outbound completion-note delivery status.
export type AtsEvent = {
  id: string;
  event_type: string;
  received_at: string;
  processed: boolean;
  invited: boolean;
  skip_reason: string | null;
  candidate_email: string | null;
  test_name: string | null;
};
export type AtsNote = {
  id: string;
  status: string;
  attempt_count: number;
  error: string | null;
  created_at: string;
  delivered_at: string | null;
  candidate_email: string | null;
  test_name: string | null;
};
export async function fetchAtsEvents(limit = 25): Promise<AtsEvent[]> {
  const res = await adminFetch(`${ATS_BASE}/events?limit=${limit}`);
  return jsonOrThrow(res);
}
export async function fetchAtsNotes(limit = 25): Promise<AtsNote[]> {
  const res = await adminFetch(`${ATS_BASE}/notes?limit=${limit}`);
  return jsonOrThrow(res);
}

export async function fetchAttempts(
  testRef: string,
): Promise<AttemptListResponse> {
  const res = await adminFetch(`/api/admin/${testRef}/attempts`);
  return jsonOrThrow(res);
}

// --- Cross-test candidate directory (CP-23) --------------------------------
//
// ONE row per candidate (keyed on lower(email)) across every test in the
// workspace — `fetchAttempts` above answers "who took THIS test", this answers
// "have we ever seen this person". Server-side search + paging, because a
// workspace-wide list grows without bound. Contract: backend/api/candidates.py.
export type CandidateDirectoryResponse = Schemas["CandidateDirectoryResponse"];
export type CandidateDirectoryItem = Schemas["CandidateDirectoryItem"];
export type CandidateAttemptRef = Schemas["CandidateAttemptRef"];

import type { InvitationSource } from "@/lib/invitationSource";

export type CandidateStatus = CandidateDirectoryItem["status"];
export type CandidatePendingRef = Schemas["CandidatePendingRef"];

export interface CandidateListParams {
  q?: string;
  // EP-23, all server-side: the directory is server-paged, so a client-side
  // filter would only ever narrow the page in front of you, never the set.
  status?: CandidateStatus[];
  source?: InvitationSource;
  invited_from?: string;
  invited_to?: string;
  completed_from?: string;
  completed_to?: string;
  // FEAT-21: the directory of ONE test — the server narrows the engagements
  // themselves, so every number on a row then describes that test.
  test_id?: string;
  limit?: number;
  offset?: number;
}

export async function fetchCandidates(
  params: CandidateListParams = {},
): Promise<CandidateDirectoryResponse> {
  return jsonOrThrow(
    await adminFetch(`/api/admin/candidates${buildParams(params)}`),
  );
}

export type CandidatesExportFormat = "csv" | "xlsx";

/** FEAT-21 — download the ticked candidates, one line per candidate per test.
 * The addresses ride the BODY (never the URL). Binary attachment, hence the
 * hand-typed request + blob read (see exportResults). */
export async function exportCandidates(body: {
  format: CandidatesExportFormat;
  emails: string[];
  test_id?: string | null;
}): Promise<{ blob: Blob; filename: string }> {
  const res = await adminFetch(`/api/admin/candidates/export`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (res.status === 404) {
    // Ticked on another filter, or erased / retaken since: nothing of the
    // selection is in this view any more.
    throw new Error("None of the selected candidates are in this view.");
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  const blob = await res.blob();
  const filename =
    parseContentDispositionFilename(res.headers.get("Content-Disposition")) ??
    `candidates.${body.format}`;
  return { blob, filename };
}

// Per-test statistics strip (UX-7). Takes the same `testRef` as fetchAttempts
// — the backend's dual-mode resolver accepts a test UUID or a legacy admin
// token — so the results page can fire both in parallel off one URL slug.
export async function fetchTestStatistics(
  testRef: string,
): Promise<TestStatisticsResponse> {
  const res = await adminFetch(`/api/admin/${testRef}/statistics`);
  return jsonOrThrow(res);
}

export async function fetchAttemptDetail(
  testRef: string,
  attemptId: string,
): Promise<AttemptDetailResponse> {
  const res = await adminFetch(`/api/admin/${testRef}/attempts/${attemptId}`);
  return jsonOrThrow(res);
}

// FEAT-3. The deterministic per-skill rollup plus any narrative already written
// for this attempt. Free and read-only — no model call happens here, so the card
// can fetch it unconditionally on every attempt.
export async function fetchAttemptAiSummary(
  testRef: string,
  attemptId: string,
): Promise<AttemptAiSummaryResponse> {
  const res = await adminFetch(
    `/api/admin/${testRef}/attempts/${attemptId}/ai-summary`,
  );
  return jsonOrThrow(res);
}

// Write the narrative. A bare POST with no body and NO auto-retry (adminFetch
// only retries idempotent GETs) — this one spends real money, so a slow provider
// must never fan out into duplicate calls. Returns the SAME shape as the GET, so
// the caller drops it straight into the query cache instead of refetching.
// Degraded/refused responses come back 200 with `llm_available:false` or
// `blocked_reason` set; only a 403 (compliance gate / Pro tier) and a 429 (rate
// limit or daily cap) throw.
export async function generateAttemptAiSummary(
  testRef: string,
  attemptId: string,
): Promise<AttemptAiSummaryResponse> {
  return withAiUsageRefresh(async () => {
    const res = await adminFetch(
      `/api/admin/${testRef}/attempts/${attemptId}/ai-summary`,
      { method: "POST" },
    );
    return jsonOrThrow(res);
  });
}

// FEAT-38 — the AI reading of an attempt's interaction counts (Part B). The
// same two-call shape as the FEAT-3 summary: a free GET (any reading already
// written + whether one may be written) and a POST on the recruiter's click
// that spends real money (no auto-retry, `withAiUsageRefresh` so the FEAT-9
// meter moves). Degraded / refused responses come back 200 with
// `llm_available:false` or `blocked_reason`; a 403 (platform gate / Pro tier)
// and a 429 (rate limit or allowance) throw.
export type InteractionReadingResponse = Schemas["InteractionReadingResponse"];
export type InteractionContext = Schemas["InteractionContext"];
export type AutomationTraits = Schemas["AutomationTraits"];

export async function fetchInteractionReading(
  testRef: string,
  attemptId: string,
): Promise<InteractionReadingResponse> {
  const res = await adminFetch(
    `/api/admin/${testRef}/attempts/${attemptId}/interaction-reading`,
  );
  return jsonOrThrow(res);
}

export async function generateInteractionReading(
  testRef: string,
  attemptId: string,
): Promise<InteractionReadingResponse> {
  return withAiUsageRefresh(async () => {
    const res = await adminFetch(
      `/api/admin/${testRef}/attempts/${attemptId}/interaction-reading`,
      { method: "POST" },
    );
    return jsonOrThrow(res);
  });
}

// CP-22 recruiter rating + private team note. One object on the attempt
// detail (`AttemptDetailResponse.review`) and the SAME object back from the
// PATCH, so the card drops the response straight into the cached detail.
// Body is tri-state per field: omit = preserve, null = clear, value = set.
export type AttemptReview = Schemas["AttemptReviewOut"];
export type AttemptReviewPatch = Schemas["AttemptReviewPatch"];

export async function updateAttemptReview(
  testRef: string,
  attemptId: string,
  body: AttemptReviewPatch,
): Promise<AttemptReview> {
  const res = await adminFetch(
    `/api/admin/${testRef}/attempts/${attemptId}/review`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return jsonOrThrow(res);
}

// FEAT-5 candidate result email. Three calls behind the send dialog.
export type ResultEmailContext = Schemas["ResultEmailContextResponse"];
// The reasons a send can be refused — the codegen's union, so the UI's copy
// map (lib/resultEmail.ts) is exhaustive by type (SIMP-9).
export type ResultEmailBlockedReason = NonNullable<ResultEmailContext["blocked_reason"]>;
export type ResultEmailPreview = Schemas["ResultEmailPreviewResponse"];
export type SendResultEmailBody = Schemas["SendResultEmailRequest"];
export type SendResultEmailResult = Schemas["SendResultEmailResult"];
export type ResultEmailKind = SendResultEmailBody["kind"];

export async function fetchResultEmailContext(
  testRef: string,
  attemptId: string,
): Promise<ResultEmailContext> {
  const res = await adminFetch(
    `/api/admin/${testRef}/attempts/${attemptId}/result-email`,
  );
  return jsonOrThrow(res);
}

export async function previewResultEmail(
  testRef: string,
  attemptId: string,
  body: SendResultEmailBody,
): Promise<ResultEmailPreview> {
  const res = await adminFetch(
    `/api/admin/${testRef}/attempts/${attemptId}/result-email/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return jsonOrThrow(res);
}

// NO auto-retry (adminFetch only retries idempotent GETs), and that is
// load-bearing here rather than incidental: a retried POST is a second email in
// a real person's inbox. The dialog's confirm is disabled while this is pending
// for the same reason.
export async function sendResultEmail(
  testRef: string,
  attemptId: string,
  body: SendResultEmailBody,
): Promise<SendResultEmailResult> {
  const res = await adminFetch(
    `/api/admin/${testRef}/attempts/${attemptId}/result-email`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return jsonOrThrow(res);
}

// R8 (pre-ship 2026-10-10): settle a result email the provider never
// confirmed — `arrived` makes it the record of a send, otherwise it is cleared
// and Send is armed again. 409 NO_UNCONFIRMED_SEND when a colleague already did.
export async function settleUnconfirmedResultEmail(
  testRef: string,
  attemptId: string,
  arrived: boolean,
): Promise<Schemas["SettleUnconfirmedResultEmailResult"]> {
  const res = await adminFetch(
    `/api/admin/${testRef}/attempts/${attemptId}/result-email/unconfirmed`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ arrived }),
    },
  );
  return jsonOrThrow(res);
}

// Results export (MVP 4.0). Server-built CSV/XLSX comparison table or PDF
// report; returned as a binary attachment, NOT JSON — hence the hand-typed
// request + blob read rather than the codegen path. `attempt_ids` omitted/null
// exports every attempt for the test (server-capped); a list (incl. one id for
// a single-candidate export) restricts to those attempts.
export type ResultsExportFormat = "csv" | "xlsx" | "pdf";
// "summary" = one row per candidate; "detailed" = adds each candidate's
// per-question answers (Excel "Answers" sheet, long-format CSV, PDF Q&A block).
export type ResultsExportDetail = "summary" | "detailed";
// Row order — mirrors the results table's Recent | Ranked toggle. Omitted ⇒ the
// backend defaults to "recent", so the field is optional on the wire.
export type ResultsExportView = "recent" | "rank";

export async function exportResults(
  testRef: string,
  body: {
    format: ResultsExportFormat;
    // Required to match the generated ResultsExportRequest (the backend defaults
    // it to "summary", but openapi-typescript emits a defaulted field as
    // required; callers always pass it — see ExportResultsMenu).
    detail: ResultsExportDetail;
    attempt_ids?: string[] | null;
    // Export in the recruiter's current view order (see ResultsExportView).
    view?: ResultsExportView;
  },
): Promise<{ blob: Blob; filename: string; truncated: boolean }> {
  const res = await adminFetch(`/api/admin/${testRef}/results/export`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  const blob = await res.blob();
  const filename =
    parseContentDispositionFilename(res.headers.get("Content-Disposition")) ??
    `results.${body.format}`;
  // The server caps an unfiltered whole-test export at 500 rows and sets this
  // header when it actually truncated. Surface it — otherwise the file silently
  // drops rows while the button still shows the full count (same-origin, so the
  // header is readable). See ExportResultsMenu.run.
  const truncated = res.headers.get("X-Export-Truncated") === "true";
  return { blob, filename, truncated };
}

// Printable test definition (CP-15). Built server-side from the same
// candidate projection "Preview as candidate" uses, so the answer key is
// absent unless `include_answer_key` is explicitly true — the dialog's
// checkbox, never a remembered preference. Binary attachment, hence the
// hand-typed request + blob read (see exportResults).
export async function exportPrintableTest(
  testRef: string,
  body: { include_answer_key: boolean },
): Promise<{ blob: Blob; filename: string }> {
  const res = await adminFetch(`/api/admin/${testRef}/printable`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  const blob = await res.blob();
  const filename =
    parseContentDispositionFilename(res.headers.get("Content-Disposition")) ??
    "printable-test.pdf";
  return { blob, filename };
}

// EF-5: set (`score` as a 0–1 fraction) or clear (`score: null`) a recruiter's
// per-answer score override. The response carries the refreshed answer card
// plus the attempt facts the write moved; callers STILL invalidate the detail
// and list queries, because rank, percentile and other attempts' verdicts can
// move too (unlike the CP-22 review, which changes nothing derived).
export type AnswerScorePatch = Schemas["AnswerScorePatch"];
export type AnswerScoreOut = Schemas["AnswerScoreOut"];

export async function updateAnswerScore(
  testRef: string,
  attemptId: string,
  position: number,
  body: AnswerScorePatch,
): Promise<AnswerScoreOut> {
  const res = await adminFetch(
    `/api/admin/${testRef}/attempts/${attemptId}/answers/${position}/score`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return jsonOrThrow(res);
}

// ── FEAT-12: code that runs on our servers ─────────────────────────────
// A Run for C / C++ / Go goes to the code runner through our API. The caller
// needs the status AND the Retry-After of a refusal (busy / throttled / in
// flight / capped each read differently), so these return a result instead
// of throwing an ApiError.

export type ServerRunHttp<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; detail: unknown; retryAfterS: number | null };

async function serverRunResult<T>(res: Response): Promise<ServerRunHttp<T>> {
  if (res.ok) return { ok: true, data: (await res.json()) as T };
  const text = await res.text();
  const header = Number(res.headers.get("Retry-After"));
  return {
    ok: false,
    status: res.status,
    detail: parseErrorDetail(text),
    retryAfterS: Number.isFinite(header) && header > 0 ? header : null,
  };
}

/** The candidate's Run: the question's frozen EXAMPLE tests on the runner.
 *  FEAT-29: `ownTests` (≤ 5) are the candidate's own cases — the same job,
 *  the same one Run; they come back apart, in `own_results`. Left out of the
 *  body entirely when there are none. */
export async function runCandidateCode(
  token: string,
  attemptId: string,
  position: number,
  code: string,
  signal?: AbortSignal,
  ownTests?: Array<{ input: string; expected_output?: string }>,
): Promise<ServerRunHttp<CandidateRunResponse>> {
  const res = await fetch(`/api/t/${token}/run`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...attemptHeaders(attemptId) },
    body: JSON.stringify({
      position,
      code,
      ...(ownTests && ownTests.length > 0 ? { own_tests: ownTests } : {}),
    }),
    signal,
  });
  return serverRunResult(res);
}

/**
 * FEAT-18: tell the server a Run happened IN THE BROWSER (Python, JavaScript,
 * TypeScript, SQL), so every Run is recorded — as the server languages' Runs
 * already are by `/run` itself. Metadata only: which position, whether every
 * example test passed, how long it took. The code is never sent.
 *
 * Fire-and-forget on purpose: it returns nothing, never throws and is never
 * awaited, so a slow, refused (429) or offline log can neither delay a Run
 * nor show the candidate an error. `keepalive` lets it finish if they move on.
 */
export function logBrowserRun(
  token: string,
  attemptId: string,
  position: number,
  run: { passed: boolean; wallMs: number },
): void {
  const wallMs = Number.isFinite(run.wallMs) ? Math.max(0, Math.round(run.wallMs)) : 0;
  try {
    void fetch(`/api/t/${token}/run-log`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...attemptHeaders(attemptId) },
      body: JSON.stringify({ position, passed: run.passed, wall_ms: wallMs }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // fetch itself threw (no network stack in a test, a blocked request).
  }
}

/** The recruiter's Run tests (editor, preview, back-office library). */
export async function runRecruiterCode(
  body: {
    language: string;
    code: string;
    tests: Array<{ name: string; input: string; expected_output: string }>;
    /** FEAT-12 phase 2: a typed question's signature (Java / PHP). */
    function_signature?: string | null;
  },
  signal?: AbortSignal,
): Promise<ServerRunHttp<RunCodeResponse>> {
  const res = await adminFetch(`/api/admin/questions/run-code`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  return serverRunResult(res);
}

/**
 * FEAT-12 (prod proof, 2026-10-16): the recruiter picked a server language or
 * opened the editor — wake the pool it runs on now, so the first Run does not
 * have to (a sleeping heavy machine whose image Fly re-pulled outlived the
 * proxy's wake and the Run read "busy"). Fire-and-forget: returns nothing,
 * never throws, never awaited; the server throttles it per pool.
 */
export function warmRunner(language: string): void {
  try {
    void adminFetch(`/api/admin/questions/warm-runner`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ language }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // fetch itself threw (no network stack in a test, a blocked request).
  }
}

/** "Retry now" — re-enter server grading for an attempt's runner answers. */
export async function regradeAttempt(
  testRef: string,
  attemptId: string,
): Promise<RegradeResponse> {
  const res = await adminFetch(`/api/admin/${testRef}/attempts/${attemptId}/regrade`, {
    method: "POST",
  });
  return jsonOrThrow(res);
}

export async function gradeHiddenTests(
  testRef: string,
  attemptId: string,
  position: number,
  results: Array<{ name: string; passed: boolean }>,
): Promise<void> {
  const res = await adminFetch(
    `/api/admin/${testRef}/attempts/${attemptId}/answers/${position}/grade-hidden`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ results }),
    },
  );
  await okOrThrow(res);
}

export class DuplicateTestError extends Error {
  existingTestId: string;
  existingTestName: string;
  constructor(message: string, existingTestId: string, existingTestName: string) {
    super(message);
    this.name = "DuplicateTestError";
    this.existingTestId = existingTestId;
    this.existingTestName = existingTestName;
  }
}

export async function createTest(
  body: CreateTestRequest,
): Promise<CreateTestResponse> {
  const res = await adminFetch(`/api/admin/tests`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  // Intercept 409 before jsonOrThrow swallows the structured detail into a
  // plain Error message. Lets Create.tsx render a "View existing →" link
  // instead of the raw "API 409: { ... }" text.
  if (res.status === 409) {
    try {
      const payload = (await res.clone().json()) as {
        detail?: {
          message?: string;
          existing_test_id?: string;
          existing_test_name?: string;
        };
      };
      const detail = payload?.detail;
      if (detail?.existing_test_id) {
        throw new DuplicateTestError(
          detail.message ?? "A test with that name already exists in this job.",
          detail.existing_test_id,
          detail.existing_test_name ?? "",
        );
      }
    } catch (e) {
      if (e instanceof DuplicateTestError) throw e;
      // fall through to jsonOrThrow for any other 409 shape
    }
  }
  return jsonOrThrow(res);
}

// Duplicate a test → a fresh, independent copy (new tokens, same config +
// question list, same jobs, auto-named "<name> (Copy)"). Returns the same
// shape as createTest so the caller can record it in localStorage and
// navigate into the new test's editor. `testRef` is a UUID test_id OR a
// legacy plaintext admin_token (the dual-mode resolver accepts both).
export async function duplicateTest(
  testRef: string,
): Promise<CreateTestResponse> {
  const res = await adminFetch(
    `/api/admin/tests/${testRef}/duplicate`,
    { method: "POST" },
  );
  return jsonOrThrow(res);
}

// ── AI test creation from a job description (MVP 4.0 item 4) ─────────────

// Analyze a pasted job description → detected technologies (each with per-level
// library availability counts) + a suggested seniority, all constrained to our
// own vocabulary. This is the platform's one recruiter-facing LLM call: a bare
// POST with NO auto-retry (adminFetch only retries idempotent GETs) so a slow /
// flaky provider never fans out into duplicate calls. The degraded + empty
// responses come back 200 with `llm_available:false` / `technologies:[]` — the
// caller falls back to manual creation rather than treating them as errors.
export async function analyzeJd(
  jobDescription: string,
): Promise<AnalyzeJdResponse> {
  try {
    return await withAiUsageRefresh(async () => {
      const res = await adminFetch(`/api/admin/analyze-jd`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job_description: jobDescription }),
      });
      return jsonOrThrow(res);
    });
  } finally {
    // FEAT-13: a running trial counts its AI job drafts here, and the account
    // menu reads "N of 3 left" from the 5-minute session — refresh it too.
    void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
  }
}

// FEAT-41 (Sheet #194): read a job offer from its URL as text for the paste
// box. No AI runs and nothing is spent — Analyze / Draft stay the one click —
// so this does NOT go through `withAiUsageRefresh`. A page that refuses bots,
// needs JavaScript or holds no offer answers 422 with a code; the field turns
// it into "paste the text" (`jobOfferFetchMessage`).
export interface FetchJobOfferResponse {
  title: string | null;
  text: string;
  source_host: string;
  truncated: boolean;
}

export type JobOfferFetchCode =
  | "invalid_url"
  | "blocked_host"
  | "unreachable"
  | "blocked_by_site"
  | "not_found"
  | "not_html"
  | "too_large"
  | "too_short";

export class JobOfferFetchError extends Error {
  readonly code: JobOfferFetchCode | "unknown";
  constructor(code: JobOfferFetchError["code"]) {
    super(`job offer fetch failed: ${code}`);
    this.name = "JobOfferFetchError";
    this.code = code;
  }
}

export async function fetchJobOffer(url: string): Promise<FetchJobOfferResponse> {
  const res = await adminFetch(`/api/admin/fetch-job-offer`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  });
  if (res.status === 422) {
    let code: JobOfferFetchError["code"] = "unknown";
    try {
      const body = (await res.json()) as { detail?: { code?: string } };
      if (typeof body.detail?.code === "string") {
        code = body.detail.code as JobOfferFetchCode;
      }
    } catch {
      // A non-JSON 422 is still a refusal with no code.
    }
    throw new JobOfferFetchError(code);
  }
  return jsonOrThrow(res);
}

// Placeholder copy — the wording is Emilia's (owed.md).
export function jobOfferFetchMessage(code: JobOfferFetchError["code"]): string {
  switch (code) {
    case "invalid_url":
      return "That doesn't look like a web address. Paste the full link, starting with https://.";
    case "blocked_host":
      return "We can only read public job pages. Paste the offer's text instead.";
    case "blocked_by_site":
      return "That site doesn't let us read the page. Copy the offer's text and paste it below.";
    case "not_found":
      return "That page isn't there any more. Check the link, or paste the offer's text.";
    case "not_html":
      return "That link isn't a web page. Paste the offer's text instead.";
    case "too_short":
      return "We couldn't find an offer on that page — it may need you to sign in. Paste the text below.";
    case "too_large":
      return "That page is too large to read. Paste the offer's text instead.";
    case "unreachable":
    default:
      return "We couldn't reach that page. Try again, or paste the offer's text below.";
  }
}

// Thrown when build-test rejects one or more seed ids (422). `missing` holds the
// offending ids so the caller can drop them from the mix and retry. Mirrors
// DuplicateTestError's structured-detail interception. Defensive — in the normal
// flow every id we send was just fetched as a live seed.
export class BuildTestError extends Error {
  missing: string[];
  constructor(message: string, missing: string[]) {
    super(message);
    this.name = "BuildTestError";
    this.missing = missing;
  }
}

// Fork a recruiter-confirmed ordered list of Global-Library seed questions into
// the caller's tenant and assemble a draft test. Returns the same shape as
// createTest/cloneTemplate so the caller lands in the editor. A 422 carries a
// structured `{ message, missing[] }` detail — intercept it before jsonOrThrow
// swallows it into a plain Error (mirrors createTest's 409 handling).
export async function buildTestFromJd(
  body: BuildTestRequest,
): Promise<CreateTestResponse> {
  const res = await adminFetch(`/api/admin/build-test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (res.status === 422) {
    try {
      const payload = (await res.clone().json()) as {
        detail?: { message?: string; missing?: string[] };
      };
      const detail = payload?.detail;
      if (Array.isArray(detail?.missing)) {
        throw new BuildTestError(
          detail.message ?? "Some suggested questions are no longer available.",
          detail.missing,
        );
      }
    } catch (e) {
      if (e instanceof BuildTestError) throw e;
      // fall through to jsonOrThrow for any other 422 shape
    }
  }
  return jsonOrThrow(res);
}

// ── AI question authoring (MVP 4.0) ──────────────────────────────────────
// Bare POSTs (no auto-retry — each spends real $) that AUTHOR content. Degraded
// responses come back 200 with `llm_available:false`; callers surface a toast and
// fall back to manual authoring rather than treating it as an error.

// Suggest plausible wrong MCQ options from the prompt + correct answer.
export async function generateDistractors(body: {
  prompt_md: string;
  correct_answer: string;
  existing_distractors?: string[];
  difficulty?: Difficulty;
  count?: number;
}): Promise<GenerateDistractorsResponse> {
  return withAiUsageRefresh(async () => {
    const res = await adminFetch(`/api/admin/questions/generate-distractors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return jsonOrThrow(res);
  });
}

// Suggest example/hidden tests for a code question, self-verified server-side
// against a reference solution (only passing tests come back).
export async function generateTests(body: {
  prompt_md: string;
  starter_code?: string;
  reference_solution?: string | null;
  difficulty?: Difficulty;
  n_example?: number;
  n_hidden?: number;
  /** FEAT-6 (Build 3): the question's language — which engine drafts and
   *  verifies the tests (default Python); a SQL question sends the
   *  database setup the tests are rows of. Pro at authoring. */
  language?: Language;
  setup_code?: string | null;
}): Promise<GenerateTestsResponse> {
  return withAiUsageRefresh(async () => {
    const res = await adminFetch(`/api/admin/questions/generate-tests`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return jsonOrThrow(res);
  });
}

// Review an authored code question and report what would break for candidates.
// Three layers server-side: a static mirror of the grading sandbox's rules, a
// gated run of the reference through the real grader, then an AI pass for what
// execution can't see. Reviews only — nothing is saved or changed.
export async function reviewCode(body: {
  prompt_md: string;
  starter_code?: string;
  reference_solution?: string | null;
  difficulty?: Difficulty;
  example_tests?: TestCase[];
  hidden_tests?: TestCase[];
  /** FEAT-6: the engine that checks it (default Python). */
  language?: Language;
  setup_code?: string | null;
}): Promise<ReviewCodeResponse> {
  return withAiUsageRefresh(async () => {
    const res = await adminFetch(`/api/admin/questions/review-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return jsonOrThrow(res);
  });
}

// Draft N MCQ + M code questions from a JD — the analyze-jd thin-library bridge.
// Code drafts' tests are self-verified; reference solutions are never returned.
export async function generateQuestions(body: {
  job_description: string;
  technologies?: string[];
  seniority?: Difficulty;
  n_mcq?: number;
  n_code?: number;
}): Promise<GenerateQuestionsResponse> {
  return withAiUsageRefresh(async () => {
    const res = await adminFetch(`/api/admin/generate-questions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return jsonOrThrow(res);
  });
}

// Ready-to-Send Composer — draft the test description + invitation intro in
// the workspace's voice. Bare POST, NO auto-retry (real $ per call, same as
// every AI call above). Degraded comes back 200 with `llm_available:false` and
// both drafts null — but the pass-mark defaults are populated on EVERY path,
// so callers read those regardless.
export async function draftTestMessaging(body: {
  job_description: string;
  test_name?: string | null;
  technologies?: string[];
  seniority?: Difficulty;
}): Promise<DraftTestMessagingResponse> {
  return withAiUsageRefresh(async () => {
    const res = await adminFetch(`/api/admin/draft-test-messaging`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return jsonOrThrow(res);
  });
}

// MVP 2.0 #4 — POST a personalised invitation. Server returns 202 on success
// (and on Resend-side failures — `_send()` swallows + logs `email_send_failed`,
// the recruiter sees a positive toast). 429 surfaces via `jsonOrThrow` as a
// thrown Error; the modal catches and renders a "try again later" toast.
export async function sendInvitation(
  testRef: string,
  // `job_id` (MVP 3.0) attributes the candidate to a job; null/omitted =
  // "Unassigned". The Resend button omits it so the existing attribution is
  // preserved server-side.
  body: {
    candidate_email: string;
    candidate_name: string;
    job_id?: string | null;
    // MVP 4.5 per-candidate deadline. ISO-8601 instant; omit (or null) to
    // inherit the workspace window. Once set, resends will NOT move it.
    expires_at?: string | null;
  },
): Promise<{ status: "accepted" }> {
  const res = await adminFetch(`/api/admin/${testRef}/invite`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

// MVP 4.5 — move a pending invitation's deadline WITHOUT re-issuing the link.
// Deliberately not the Resend button: no token rotation, no email, so the link
// already in the candidate's inbox keeps working. `expires_at: null` reverts to
// the workspace window. The field must be present — `{}` is a 422, so that a
// caller bug can't look like a successful save.
export async function updateInvitationExpiry(
  testRef: string,
  invitationId: string,
  expiresAt: string | null,
): Promise<{
  invitation_id: string;
  expires_at: string;
  expires_at_is_custom: boolean;
}> {
  const res = await adminFetch(
    `/api/admin/${testRef}/invitations/${invitationId}/expiry`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expires_at: expiresAt }),
    },
  );
  return jsonOrThrow(res);
}

// MVP 3.0 — recruiter kill-switch for a per-invitation link. Sets revoked_at
// so the candidate-entry gate serves 410 on the next /preview or /start.
// Idempotent server-side. A subsequent Resend re-arms the link.
export async function revokeInvitation(
  testRef: string,
  invitationId: string,
): Promise<{ invitation_id: string; revoked_at: string }> {
  const res = await adminFetch(
    `/api/admin/${testRef}/invitations/${invitationId}/revoke`,
    { method: "POST" },
  );
  return jsonOrThrow(res);
}

// FEAT-24 — named shared links: extra shareable links for one test, each named
// after where it is posted, so a result can say which posting the candidate
// came through. The list's first entry is always the test's own general link
// (`is_default`, no id) — it cannot be renamed, switched off or deleted here.
// Contract: backend/api/share_links.py.
export type ShareLink = Schemas["ShareLinkOut"];
export type ShareLinkListResponse = Schemas["ShareLinkListResponse"];

export async function listShareLinks(
  testRef: string,
): Promise<ShareLinkListResponse> {
  const res = await adminFetch(`/api/admin/${testRef}/share-links`);
  return jsonOrThrow(res);
}

// FEAT-26: `jobId` is the job the posting recruits for (one of the test's own
// jobs); null = none. A candidate who starts through the link then counts for
// that job.
export async function createShareLink(
  testRef: string,
  name: string,
  jobId: string | null = null,
): Promise<ShareLink> {
  const res = await adminFetch(`/api/admin/${testRef}/share-links`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, job_id: jobId }),
  });
  return jsonOrThrow(res);
}

// `job_id` is TRI-STATE on the server: leave the key out to keep the link's
// job, send null to clear it. Never spread a default into this body.
export type ShareLinkUpdate = {
  name?: string;
  enabled?: boolean;
  job_id?: string | null;
};

export async function updateShareLink(
  testRef: string,
  linkId: string,
  body: ShareLinkUpdate,
): Promise<ShareLink> {
  const res = await adminFetch(`/api/admin/${testRef}/share-links/${linkId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

export async function deleteShareLink(
  testRef: string,
  linkId: string,
): Promise<void> {
  const res = await adminFetch(`/api/admin/${testRef}/share-links/${linkId}`, {
    method: "DELETE",
  });
  // 404 on already-deleted is benign — same posture as deleteTag. A 409
  // (`share_link_in_use`) still throws: the dialog offers "switch off" instead.
  await okOr404(res);
}

// MVP 3.0 — candidate data deletion on request (GDPR Art. 17). Hard-deletes a
// candidate's attempt + answers + code snapshots + anti-cheat events + the
// matching invitation for this test, and writes a no-PII compliance record.
// Hand-typed (mirrors the backend CandidateDeletionReceipt) so typecheck
// doesn't depend on regenerating api.generated.ts.
export interface CandidateDeletionReceipt {
  deleted_attempts: number;
  deleted_answers: number;
  deleted_events: number;
  deleted_invitations: number;
  deletion_id: string;
  deleted_at: string;
}

export async function deleteAttemptCandidateData(
  testRef: string,
  attemptId: string,
): Promise<CandidateDeletionReceipt> {
  const res = await adminFetch(
    `/api/admin/${testRef}/attempts/${attemptId}/candidate-data`,
    { method: "DELETE" },
  );
  return jsonOrThrow(res);
}

// GDPR Art. 17 for a candidate who was INVITED but never started — they have no
// attempt, so the attempt-keyed helper above cannot reach them. Same receipt
// shape; only `deleted_invitations` can be non-zero. A 409 (ApiError.detail
// `.reason === "candidate_has_attempt"`) means they started between the list
// read and the click: the server wrote NOTHING, and the caller must send the
// recruiter to the result row instead. Never a partial delete.
export async function deleteInvitationCandidateData(
  testRef: string,
  invitationId: string,
): Promise<CandidateDeletionReceipt> {
  const res = await adminFetch(
    `/api/admin/${testRef}/invitations/${invitationId}/candidate-data`,
    { method: "DELETE" },
  );
  return jsonOrThrow(res);
}

// MVP 3.0 — candidate rectification (GDPR Art. 16) + "allow retake". Companions
// to the deletion helper above. Hand-typed (mirror the backend response models)
// so typecheck doesn't depend on regenerating api.generated.ts.
export interface CandidateRectificationResult {
  attempt_id: string;
  candidate_name: string;
  candidate_email: string;
  invitation_synced: boolean;
}

export interface InvitationRectificationResult {
  invitation_id: string;
  candidate_name: string;
  candidate_email: string;
}

export interface AllowRetakeResult {
  superseded: number;
  invitation_rearmed: boolean;
  // True once the candidate's retake link was handed to the email provider
  // (or stub-logged in dev); false if Resend was degraded.
  emailed: boolean;
}

// Correct a candidate's name/email on an attempt. Email edits sync the matching
// invitation row; a collision with another live attempt/invitation → 409.
export async function updateAttemptCandidate(
  testRef: string,
  attemptId: string,
  body: { candidate_name?: string; candidate_email?: string },
): Promise<CandidateRectificationResult> {
  const res = await adminFetch(
    `/api/admin/${testRef}/attempts/${attemptId}/candidate`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return jsonOrThrow(res);
}

// Void the candidate's current attempt(s) (kept as history) + re-arm their
// invitation link so they can start fresh.
export async function allowCandidateRetake(
  testRef: string,
  attemptId: string,
): Promise<AllowRetakeResult> {
  const res = await adminFetch(
    `/api/admin/${testRef}/attempts/${attemptId}/allow-retake`,
    { method: "POST" },
  );
  return jsonOrThrow(res);
}

// Correct a pending invitation's name/email before the candidate starts. The
// link is unchanged; a collision with another invitation on the test → 409.
export async function updateInvitationCandidate(
  testRef: string,
  invitationId: string,
  body: { candidate_name?: string; candidate_email?: string },
): Promise<InvitationRectificationResult> {
  const res = await adminFetch(
    `/api/admin/${testRef}/invitations/${invitationId}/candidate`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return jsonOrThrow(res);
}

// MVP 2.0 #6 — bulk invitation endpoint. Partial-success contract: malformed
// rows or upsert failures land in `rejected[]` with a reason; valid rows still
// process. The modal parses the CSV client-side (see frontend/src/lib/csv.ts)
// and sends only structurally-valid rows; the server re-validates every row
// regardless.
export type BulkInviteRejectReason =
  | "invalid_email"
  | "blank_name"
  // A row whose pinned per-candidate deadline (MVP 4.5) has already lapsed: a
  // bulk resend can't re-arm it, so the row is rejected (set a new date first).
  | "deadline_passed"
  // FEAT-2: the plan's monthly candidate cap (Basic: 20) ran out before this
  // row. Earlier rows were sent; this one and the rest are reported.
  | "candidate_limit_reached"
  | "upsert_failed"
  | "email_send_failed"
  | "internal_error"
  // FEAT-40: the address already holds a live attempt on this test —
  // `attempt_id` names it so the outcome can offer "Send as a retake"…
  | "already_completed"
  // …or is mid-test through its invitation: held, the token never rotated.
  | "in_progress"
  // h04: never sent by the server — `awaitInviteBatch` folds a row the
  // recruiter revoked while it was queued into this, so no retry re-arms it.
  | "revoked";

export type BulkInviteAccepted = {
  row_index: number;
  invitation_id: string;
  candidate_email: string;
  is_resend: boolean;
};

export type BulkInviteRejected = {
  row_index: number;
  candidate_email: string | null;
  reason: BulkInviteRejectReason;
  /** FEAT-40: set with `already_completed` / `in_progress`. */
  attempt_id?: string | null;
};

export type BulkInviteResponse = {
  accepted: BulkInviteAccepted[];
  rejected: BulkInviteRejected[];
  total: number;
  /** OPS-22: `accepted` rows are saved and QUEUED — their emails go out after
   *  the answer. Poll `fetchInviteBatch` with this; null when nothing was
   *  accepted. (Absent on the merged outcome `awaitInviteBatch` returns.) */
  batch_id?: string | null;
};

/** Where one queued row's email is (OPS-22). `failed` = saved, but its email
 *  did not (or may not have) gone out — shown as `email_send_failed`.
 *  `revoked` (h04) = the recruiter killed the link: done, never retried. */
export type InviteBatchRowState = "queued" | "sending" | "sent" | "failed" | "revoked";

export type InviteBatchStatus = {
  batch_id: string;
  rows: { invitation_id: string; candidate_email: string; state: InviteBatchRowState }[];
  done: boolean;
};

export async function sendInvitationsBulk(
  testRef: string,
  // `job_id` (MVP 3.0) applies to the WHOLE batch; null/omitted = "Unassigned".
  body: {
    rows: { candidate_email: string; candidate_name: string }[];
    job_id?: string | null;
  },
): Promise<BulkInviteResponse> {
  const res = await adminFetch(`/api/admin/${testRef}/invite-bulk`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

/** OPS-22: the progress of a bulk invite's emails, sent after the POST. */
export async function fetchInviteBatch(
  testRef: string,
  batchId: string,
): Promise<InviteBatchStatus> {
  const res = await adminFetch(`/api/admin/${testRef}/invite-bulk/${batchId}`);
  return jsonOrThrow(res);
}

// --- Bulk question import (MVP 3.0) --------------------------------------
// The ImportQuestionsModal parses the CSV client-side (see lib/questionsCsv.ts)
// and POSTs only structurally-valid rows; the server re-validates every row
// through the same models the single-create path uses. mcq + mcq_multi only.
export type QuestionImportRowInput = {
  type: string;
  title: string;
  prompt_md: string;
  difficulty: string;
  tags: string[];
  correct: string[]; // correct option texts
  other: string[]; // wrong option texts
  shuffle_answers: boolean;
};

export type QuestionImportAccepted = {
  row_index: number;
  question_id: string;
  title: string;
  type: string;
};

export type QuestionImportRejected = {
  row_index: number;
  title: string | null;
  reason: string;
};

export type QuestionImportResponse = {
  accepted: QuestionImportAccepted[];
  rejected: QuestionImportRejected[];
  total: number;
};

export async function importQuestionsBulk(body: {
  rows: QuestionImportRowInput[];
}): Promise<QuestionImportResponse> {
  const res = await adminFetch(`/api/admin/questions/import-bulk`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

// --- Bulk CODE question import (MVP 3.0) ---------------------------------
// Sibling of importQuestionsBulk. ImportCodeQuestionsModal parses a JSON file
// client-side (see lib/codeQuestionsJson.ts) and POSTs structurally-valid rows;
// the server re-validates each through the same CodeQuestionIn the single-create
// path uses. Code questions only. Reuses the MCQ importer's accepted[]/rejected[]
// response shape (QuestionImportResponse).
export type CodeImportTestCase = {
  name: string;
  input: string;
  expected_output: string;
};

export type CodeQuestionImportRowInput = {
  type: string;
  title: string;
  prompt_md: string;
  difficulty: string;
  tags: string[];
  /** FEAT-6: python / javascript / sql; omitted → python. */
  language?: string;
  /** FEAT-6: a SQL row's schema + seed rows. */
  setup_code?: string | null;
  starter_code: string;
  example_tests: CodeImportTestCase[];
  hidden_tests: CodeImportTestCase[];
};

export async function importCodeQuestionsBulk(body: {
  rows: CodeQuestionImportRowInput[];
}): Promise<QuestionImportResponse> {
  const res = await adminFetch(`/api/admin/questions/import-bulk-code`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

export async function fetchOverview(): Promise<OverviewResponse> {
  // Session-required (PR 3 cleanup). Workspace-wide aggregates.
  const res = await adminFetch(`/api/admin/overview`, { credentials: "include" });
  return jsonOrThrow(res);
}

// MVP 3.0 — company-wide statistics rollup (session required). `window` is one
// of 7d/30d/90d/all and drives the server-side cutoff; the FE keys its query on
// it so the window toggle refetches + caches per window.
export async function fetchStatistics(
  window: StatsWindow = "30d",
  testId?: string | null,
): Promise<StatisticsResponse> {
  // EP-5: `test_id` narrows every block to one test. Omitted (not sent empty)
  // when absent, so the unfiltered request is byte-identical to the historical
  // one and shares its cache entry with Home.tsx's `fetchStatistics("all")`.
  const qs = new URLSearchParams({ window });
  if (testId) qs.set("test_id", testId);
  const res = await adminFetch(`/api/admin/statistics?${qs.toString()}`, {
    credentials: "include",
  });
  return jsonOrThrow(res);
}

export type StatsExportFormat = "csv" | "xlsx";

/** EP-5 — download the statistics view. Binary attachment, hence the
 * hand-typed request + blob read (see exportResults). */
export async function exportStatistics(body: {
  format: StatsExportFormat;
  window: StatsWindow;
  test_id?: string | null;
}): Promise<{ blob: Blob; filename: string }> {
  const res = await adminFetch(`/api/admin/statistics/export`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  const blob = await res.blob();
  const filename =
    parseContentDispositionFilename(res.headers.get("Content-Disposition")) ??
    `statistics.${body.format}`;
  return { blob, filename };
}

// MVP 2.0 #10 — current recruiter's plan + status. `/api/billing/*` is
// session-required exactly like `/api/admin/*`, so we reuse adminFetch (the
// name is historical — it really just means "send the session cookie").
export async function fetchBillingMe(): Promise<BillingMeRead> {
  const res = await adminFetch(`/api/billing/me`);
  return jsonOrThrow(res);
}

// ── AI usage meter (FEAT-9) ──────────────────────────────────────────────
// This month's AI allowance as the customer may see it: a percentage and a
// reset date, never a figure (backend/ai_allowance.py). The same object rides
// on `BillingMeRead.ai_usage`; this light read is what the sidebar polls.
export type AiUsageRead = Schemas["AiUsageRead"];

export async function fetchAiUsage(): Promise<AiUsageRead> {
  const res = await adminFetch(`/api/billing/ai-usage`);
  return jsonOrThrow(res);
}

// Every AI wrapper settles through this: the meter is stale the moment a
// spend was ATTEMPTED — success moved it, and a 429 is exactly when it must
// jump to 100%. Hence `finally`, not `then`. Fire-and-forget: the refetch
// must never delay or fail the recruiter's own result.
async function withAiUsageRefresh<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } finally {
    void queryClient.invalidateQueries({ queryKey: queryKeys.aiUsage() });
    void queryClient.invalidateQueries({ queryKey: queryKeys.billingMe() });
  }
}

// FEAT-2 — what the admin app needs to open a Paddle checkout. Returns
// `billing_enabled: false` (and nothing else) while the dark flag is off OR the
// deploy is half-configured, so the caller has exactly one thing to check.
export async function fetchBillingConfig(): Promise<BillingConfigRead> {
  const res = await adminFetch(`/api/billing/config`);
  return jsonOrThrow(res);
}

// The public `/pay` page (Paddle's default payment link) — no session, so NOT
// adminFetch: a signed-out customer arriving from a Paddle email is the case.
export async function fetchBillingPublicConfig(): Promise<BillingPublicConfigRead> {
  const res = await apiFetch(`/api/billing/public-config`);
  return jsonOrThrow(res);
}

// FEAT-2 — mint a one-time link into Paddle's hosted customer portal (invoices,
// payment method, cancellation). Short-lived by design: minted on click, never
// baked into the page.
export async function createBillingPortalSession(): Promise<BillingPortalRead> {
  const res = await adminFetch(`/api/billing/portal`, { method: "POST" });
  return jsonOrThrow(res);
}

// FEAT-2 (MVP 5.5) — in-app plan change. Owner-only on the server. None of
// these grants a plan: Paddle answers each change with a webhook, and the
// Billing page polls `/api/billing/me` until the mirror shows it.

/** The paid subscription as Paddle has it now: dates and the next payment. */
export async function fetchBillingSubscription(): Promise<BillingSubscriptionRead> {
  const res = await adminFetch(`/api/billing/subscription`);
  return jsonOrThrow(res);
}

async function postBilling<T>(path: string, body?: unknown): Promise<T> {
  const res = await adminFetch(path, {
    method: "POST",
    ...(body === undefined
      ? {}
      : {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
  });
  return jsonOrThrow(res);
}

/** What a change to `plan` would charge and when — priced by Paddle, changes nothing. */
export function previewPlanChange(plan: string): Promise<PlanChangePreviewRead> {
  return postBilling(`/api/billing/change-plan/preview`, { plan });
}

/** Upgrade now (charged to the saved card) or schedule a downgrade. */
export function changePlan(plan: string): Promise<PlanChangeResultRead> {
  return postBilling(`/api/billing/change-plan`, { plan });
}

/** "Keep my plan" — undo a scheduled downgrade. */
export function undoPlanChange(): Promise<PlanChangeResultRead> {
  return postBilling(`/api/billing/change-plan/undo`);
}

/** Cancel at the end of the paid period. */
export function cancelSubscription(): Promise<PlanChangeResultRead> {
  return postBilling(`/api/billing/cancel`);
}

/** "Keep my plan" — undo a scheduled cancellation. */
export function undoCancelSubscription(): Promise<PlanChangeResultRead> {
  return postBilling(`/api/billing/cancel/undo`);
}

// MVP 2.0 #5 — workspace-wide pending follow-ups (session required). Returns
// invitations sent > `threshold_days` ago whose candidates haven't started
// yet, parent test alive. The Resend button in the Homepage panel reuses the
// existing `sendInvitation()` helper above — no separate "resend" endpoint;
// `_upsert_invitation` already bumps `last_resend_at` on the UPDATE branch.
export async function fetchPendingFollowUps(): Promise<PendingFollowUpsResponse> {
  const res = await adminFetch(`/api/admin/pending-follow-ups`, {
    credentials: "include",
  });
  return jsonOrThrow(res);
}

// MVP 2.0 #2 — workspace-wide test list (session required). Returns 401 if
// the session cookie is absent / expired. candidate_token/url are present for
// every test (Test.candidate_token is NOT NULL). This is the dashboard's sole
// source of truth — the recruiter dashboard is UUID-native (no localStorage
// token cache); recruiter routes resolve by test_id UUID.
//
// MVP 3.0: `jobs: JobRef[]` replaces the single `job_id`. The list is
// ordered protected-first (Unassigned wins), then alphabetical by name.
// Empty list shouldn't happen — orphan protection auto-attaches Unassigned.
// MVP 4.5: aliased to the generated schema instead of hand-declared. The
// hand-written copy was the only reason adding a backend field required a
// manual edit here as well as a `npm run gen:api`.
export type WorkspaceTestItem = Schemas["WorkspaceTestItem"];

export type WorkspaceTestListResponse = Schemas["WorkspaceTestListResponse"];

export async function fetchWorkspaceTests(): Promise<WorkspaceTestListResponse> {
  const res = await adminFetch(`/api/admin/tests`, { credentials: "include" });
  return jsonOrThrow(res);
}

export async function fetchTestsInJob(
  jobId: string,
): Promise<WorkspaceTestListResponse> {
  const res = await adminFetch(`/api/admin/jobs/${jobId}/tests`, {
    credentials: "include",
  });
  return jsonOrThrow(res);
}


// ── Questions library wrappers ──────────────────────────────────────────

export interface LibraryQuestionFilters {
  type?: QuestionType;
  q?: string;
  tag_ids?: string[];
  difficulty?: Difficulty[];
  test_ids?: string[];
  // Mutually exclusive with `test_ids` — the backend 422s if both are set.
  no_test?: boolean;
  // Archive shelf. Omitted/false → active questions only (also the picker's
  // view). True → the library's "Archived" tab.
  archived?: boolean;
  // Opt-in read-union with the platform-seeded Global Library (MVP 4.0):
  // also return read-only seeded questions flagged `is_seeded`. Leave unset
  // everywhere except the Library tab — the attach picker and bulk flows
  // stay tenant-only (a seed is attached BY REFERENCE through its own doors,
  // EP-16, never copied into the tenant's rows).
  include_seeded?: boolean;
  // Sheet #188 (2026-10-25): with `include_seeded`, the platform rows ONLY —
  // the "Platform library" segment. A built-in the company has copied is then
  // listed (`in_my_library`); in the union it shows once, as the copy, so a
  // client-side `is_seeded` filter over the union lost it.
  seeded_only?: boolean;
  // Sheet #188 (2026-10-21): also return the Global Library questions one of
  // the caller's tests uses by reference — `is_seeded`, read-only, with the
  // test's chip — so a template's questions show in the recruiter's own
  // library once. The Active tab's axis; a no-op with `include_seeded` or
  // `archived`.
  include_referenced?: boolean;
  // Technology TAG-NAME filter (case-insensitive, e.g. "python") — the
  // Library tab's facet. Name-based because seeded tags and tenant tags
  // share no UUIDs.
  technology?: string;
  // FEAT-6: coding language(s), OR semantics; only code questions match
  // ("python" also matches every pre-FEAT-6 row). Repeated `?language=`.
  language?: string[];
  // CP-6 plan filter. Omitted/false → also return seeded questions this plan
  // cannot open, flagged `locked` (the pricing promise is that higher-package
  // questions are SHOWN but locked). True → only what the plan reaches. A no-op
  // while billing is dark, which is why the control is hidden then.
  available_only?: boolean;
}

export async function listLibraryQuestions(
  filters: LibraryQuestionFilters = {},
): Promise<LibraryListResponse> {
  const params = new URLSearchParams();
  if (filters.type) params.set("type", filters.type);
  if (filters.q) params.set("q", filters.q);
  // Repeated `?tag=` params — FastAPI parses to `list[UUID]` natively.
  // AND semantics on the backend: question must carry every tag.
  if (filters.tag_ids?.length) {
    for (const tid of filters.tag_ids) params.append("tag", tid);
  }
  // OR semantics across difficulty + test_id (matches backend).
  if (filters.difficulty?.length) {
    for (const d of filters.difficulty) params.append("difficulty", d);
  }
  if (filters.test_ids?.length) {
    for (const tid of filters.test_ids) params.append("test_id", tid);
  }
  if (filters.no_test) params.set("no_test", "true");
  if (filters.archived) params.set("archived", "true");
  if (filters.include_seeded) params.set("include_seeded", "true");
  if (filters.seeded_only) params.set("seeded_only", "true");
  if (filters.include_referenced) params.set("include_referenced", "true");
  if (filters.technology) params.set("technology", filters.technology);
  if (filters.language?.length) {
    for (const lang of filters.language) params.append("language", lang);
  }
  if (filters.available_only) params.set("available_only", "true");
  const qs = params.toString();
  const res = await adminFetch(`/api/admin/questions${qs ? `?${qs}` : ""}`);
  return jsonOrThrow(res);
}

export interface SeededTechnology {
  name: string;
  count: number;
}

export interface SeededTechnologiesResponse {
  technologies: SeededTechnology[];
  // Distinct live seeds — NOT the sum of the per-technology counts. 88 corpus
  // questions carry two technology tags, so adding the facet up over-reports
  // the library by that much (EP-4).
  total: number;
}

// Distinct technology tag names + live-seed counts in the platform library —
// powers the Library tab's badge count + technology facet.
export async function fetchSeededTechnologies(): Promise<SeededTechnologiesResponse> {
  const res = await adminFetch(`/api/admin/questions/seeded-technologies`);
  return jsonOrThrow(res);
}

/**
 * One-click "Add to my library": fork a platform-seeded question into the
 * caller's own library. Bare POST — the backend copies the source content
 * verbatim and ensure-or-creates same-named tags inside the caller's company
 * (a seed's own tag UUIDs are cross-tenant and must never be sent as
 * `tag_ids`). Returns the new company-owned copy.
 */
export async function forkSeedQuestion(
  seedQuestionId: string,
): Promise<LibraryQuestionDetail> {
  const res = await adminFetch(`/api/admin/questions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ seed_from_question_id: seedQuestionId }),
  });
  return jsonOrThrow(res);
}

export async function fetchLibraryQuestion(
  id: string,
): Promise<LibraryQuestionDetail> {
  const res = await adminFetch(`/api/admin/questions/${id}`);
  return jsonOrThrow(res);
}

export interface QuestionTitleConflict {
  id: string;
  title: string;
  type: QuestionType;
}

export interface QuestionTitleConflictsResponse {
  matches: QuestionTitleConflict[];
  exact_count: number;
}

export async function fetchQuestionTitleConflicts(
  args: { title: string; exclude_question_id?: string | null },
): Promise<QuestionTitleConflictsResponse> {
  const params = new URLSearchParams({ title: args.title });
  if (args.exclude_question_id) {
    params.set("exclude_question_id", args.exclude_question_id);
  }
  const res = await adminFetch(
    `/api/admin/questions/title-conflicts?${params.toString()}`,
  );
  return jsonOrThrow(res);
}

/**
 * Thrown when `create_question` rejects a save because the `attach_to` array
 * contains test ids that no longer match any test on the server (e.g. a test
 * deleted between the list fetch and the save). The caller's job is to (a) drop
 * the same tokens from any in-component state (e.g. `attachTokens` in
 * QuestionEdit) and (b) decide whether to auto-retry with the cleaned set.
 */
export class StaleAdminTokensError extends Error {
  readonly staleTokens: string[];
  readonly cleanedTokens: string[];
  constructor(staleTokens: string[], cleanedTokens: string[]) {
    super(`Removed ${staleTokens.length} stale test reference(s); retry.`);
    this.name = "StaleAdminTokensError";
    this.staleTokens = staleTokens;
    this.cleanedTokens = cleanedTokens;
  }
}

export async function createLibraryQuestion(
  body:
    | McqQuestionInput
    | McqMultiQuestionInput
    | TrueFalseQuestionInput
    | OpenEndedQuestionInput
    | FileUploadQuestionInput
    | CodeQuestionInput,
  attachTo: string[] = [],
  // Set when the editor is forking a platform SEED (use-as-template flow):
  // lets the backend translate the seed's tag names into caller-owned tags
  // when the draft carries no explicit tag picks. Own-question forks omit it
  // (their hydrated tag_ids are already valid tenant UUIDs).
  seedFromQuestionId?: string | null,
): Promise<LibraryQuestionDetail> {
  const res = await adminFetch(`/api/admin/questions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      question: body,
      attach_to: attachTo,
      ...(seedFromQuestionId
        ? { seed_from_question_id: seedFromQuestionId }
        : {}),
    }),
  });
  if (res.status === 404 && attachTo.length > 0) {
    // Peek at the body before jsonOrThrow swallows the structured detail.
    let stale: string[] | null = null;
    try {
      const parsed = await res.clone().json();
      const candidate = parsed?.detail?.stale_admin_tokens;
      if (Array.isArray(candidate) && candidate.length > 0) {
        stale = candidate.filter((t): t is string => typeof t === "string");
      }
    } catch {
      // Fall through — jsonOrThrow will surface the original 404 message.
    }
    if (stale && stale.length > 0) {
      const staleSet = new Set(stale);
      const cleaned = attachTo.filter((t) => !staleSet.has(t));
      throw new StaleAdminTokensError(stale, cleaned);
    }
  }
  return jsonOrThrow(res);
}

export async function updateLibraryQuestion(
  id: string,
  body:
    | McqQuestionInput
    | McqMultiQuestionInput
    | TrueFalseQuestionInput
    | OpenEndedQuestionInput
    | FileUploadQuestionInput
    | CodeQuestionInput,
): Promise<LibraryQuestionDetail> {
  const res = await adminFetch(`/api/admin/questions/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

export async function deleteLibraryQuestion(id: string): Promise<void> {
  const res = await adminFetch(`/api/admin/questions/${id}`, { method: "DELETE" });
  // 404 on an already-deleted question is benign — same posture as
  // deleteTest/deleteJob/deleteTag. A double-click or a stale list (the question
  // was deleted in a parallel session/tab) converges instead of bubbling an
  // unhandled rejection to Sentry; the caller's invalidateQueries removes the
  // ghost card either way.
  await okOr404(res);
}

// Library-only archive (mirrors archiveTest/archiveJob). Hides the question
// from the active library + attach picker without touching tests that already
// use it. Reversible via unarchiveLibraryQuestion.
export async function archiveLibraryQuestion(id: string): Promise<void> {
  const res = await adminFetch(`/api/admin/questions/${id}/archive`, {
    method: "POST",
  });
  await okOrThrow(res);
}

export async function unarchiveLibraryQuestion(id: string): Promise<void> {
  const res = await adminFetch(`/api/admin/questions/${id}/unarchive`, {
    method: "POST",
  });
  await okOrThrow(res);
}

// Bulk delete / archive / unarchive of the recruiter's OWN library questions
// (UX-2). Partial-success: each requested id lands in `accepted` or `rejected`
// (with a reason) — one bad id never fails the batch. Hand-typed (like
// attachQuestionsBulk) so typecheck doesn't hinge on regenerating
// api.generated.ts. Company-scoped via the session cookie → adminFetch (not
// the bare fetch attachQuestionsBulk uses for its admin_token route). The
// server caps a batch at 200 ids.
export type QuestionBulkRejectReason = "not_found" | "deleted";
export interface QuestionBulkResponse {
  accepted: string[];
  rejected: { id: string; reason: QuestionBulkRejectReason }[];
}

async function questionBulkAction(
  action: "delete-bulk" | "archive-bulk" | "unarchive-bulk",
  ids: string[],
): Promise<QuestionBulkResponse> {
  const res = await adminFetch(`/api/admin/questions/${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question_ids: ids }),
  });
  return jsonOrThrow(res);
}

export const deleteLibraryQuestionsBulk = (ids: string[]) =>
  questionBulkAction("delete-bulk", ids);
export const archiveLibraryQuestionsBulk = (ids: string[]) =>
  questionBulkAction("archive-bulk", ids);
export const unarchiveLibraryQuestionsBulk = (ids: string[]) =>
  questionBulkAction("unarchive-bulk", ids);

export async function attachQuestionToTest(
  adminToken: string,
  questionId: string,
): Promise<{ position: number; already_attached: boolean }> {
  const res = await adminFetch(
    `/api/admin/${adminToken}/questions/${questionId}/attach`,
    { method: "POST" },
  );
  return jsonOrThrow(res);
}

// Bulk-attach several library questions to a test in one request. Partial
// success: each id lands in `accepted` (with its new position) or `rejected`
// (with a reason). Hand-typed so typecheck doesn't depend on regenerating
// api.generated.ts. Through `adminFetch` like every admin call (FEAT-34
// verify, 2026-10-06: these three were bare `fetch`, so a 401, a role 403 and
// the plan wall's 402 reached the editor without refreshing the session).
export type AttachBulkRejectReason =
  | "not_found"
  | "already_attached"
  | "deleted"
  | "archived"
  // EP-16 + CP-6: a platform question above this workspace's plan. Reported
  // separately from `not_found` on purpose — the row is right there in the
  // picker with a lock badge, so "not found" would contradict the screen.
  | "locked"
  // FEAT-12 (pre-ship R1): a language this server cannot run yet — a server
  // capability, not a plan, so never folded into `locked`.
  | "language_unavailable";
export interface AttachBulkResponse {
  accepted: { id: string; position: number }[];
  rejected: { id: string; reason: AttachBulkRejectReason }[];
}

export async function attachQuestionsBulk(
  adminToken: string,
  questionIds: string[],
): Promise<AttachBulkResponse> {
  const res = await adminFetch(`/api/admin/${adminToken}/questions/attach-bulk`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question_ids: questionIds }),
  });
  return jsonOrThrow(res);
}

export async function detachQuestionFromTest(
  adminToken: string,
  questionId: string,
): Promise<void> {
  const res = await adminFetch(
    `/api/admin/${adminToken}/questions/${questionId}/detach`,
    { method: "DELETE" },
  );
  await okOrThrow(res);
}

export async function reorderTestQuestions(
  adminToken: string,
  order: string[],
): Promise<void> {
  const res = await adminFetch(`/api/admin/${adminToken}/questions/reorder`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ order }),
  });
  await okOrThrow(res);
}

export async function fetchTestQuestions(
  adminToken: string,
): Promise<TestQuestionsResponse> {
  const res = await adminFetch(`/api/admin/${adminToken}/questions`);
  return jsonOrThrow(res);
}

// ── Preview as candidate (MVP 4.0 item 6) ───────────────────────────────
// Recruiter-only "experience this test as a candidate" payload. The snapshot
// is the SAME answer-key-free shape the real candidate flow receives
// (QuestionResponse["snapshot"] === CandidateQuestionSnapshot), so the leaf
// components (MCQ/MCQMulti/CodeQuestion) render it unchanged. Hand-typed
// (mirrors CompanySettings above) so typecheck doesn't wait on gen:api.
export type CandidatePreviewSnapshot = QuestionResponse["snapshot"];

export interface CandidatePreviewQuestion {
  position: number;
  snapshot: CandidatePreviewSnapshot;
}

export interface CandidatePreviewResponse {
  test_id: string;
  test_name: string;
  timing_mode: string;
  total_time_limit_seconds: number;
  sections: Array<Record<string, unknown>> | null;
  question_count: number;
  has_mcq: boolean;
  has_code: boolean;
  questions: CandidatePreviewQuestion[];
  // EF-9: this test includes a questionnaire (IPIP instructions per statement).
  // FEAT-11: `soft_skills_only` is what the "no clock" chrome keys on — a
  // MIXED test really is timed for its technical block.
  soft_skills?: boolean;
  soft_skills_only?: boolean;
  statement_count?: number;
  likert_instructions?: string | null;
}

// ── EF-9 questionnaires ────────────────────────────────
export type SoftSkillsCatalogue = Schemas["SoftSkillsCatalogueResponse"];
export type SoftSkillInstrument = Schemas["SoftSkillInstrumentOut"];
export type SoftSkillsProfile = Schemas["SoftSkillsProfile"];
export type SoftSkillsDetail = Schemas["SoftSkillsDetail"];
export type SoftSkillItem = Schemas["SoftSkillItem"];

export type SoftSkillStatements = Schemas["SoftSkillStatementsResponse"];

/** The read-only IPIP catalogue for the create page's instrument picker. */
export async function fetchSoftSkillInstruments(): Promise<SoftSkillsCatalogue> {
  const res = await adminFetch(`/api/admin/soft-skills/instruments`);
  return jsonOrThrow(res);
}

/**
 * FEAT-11 — the statements of ONE questionnaire, for the read-only preview.
 * Fetched per instrument on demand rather than folded into the catalogue
 * above: the picker would otherwise carry up to 163 statements it never shows.
 */
export async function fetchSoftSkillStatements(
  instrumentKey: string,
): Promise<SoftSkillStatements> {
  const res = await adminFetch(
    `/api/admin/soft-skills/instruments/${encodeURIComponent(instrumentKey)}/statements`,
  );
  return jsonOrThrow(res);
}

export async function fetchCandidatePreview(
  adminToken: string,
): Promise<CandidatePreviewResponse> {
  const res = await adminFetch(`/api/admin/${adminToken}/candidate-preview`);
  return jsonOrThrow(res);
}

// One library question projected through the SAME answer-key-free candidate
// boundary — powers the "Preview" button on the question library (seed + own
// cards). Distinct from the per-TEST preview above: a single question, no test.
export async function fetchQuestionCandidatePreview(
  questionId: string,
): Promise<CandidatePreviewQuestion> {
  const res = await adminFetch(
    `/api/admin/questions/${questionId}/candidate-preview`,
  );
  return jsonOrThrow(res);
}

// ── Ready-made test templates (MVP 4.0 item 2) ──────────────────────────
// Two sources, one card shape (CP-8): "general" = role-scoped predefined tests
// under the Global Library company; "custom" = templates THIS company saved
// from its own tests (frozen config, shared questions, never a live test).
// Both lists are unparameterised (tens, not thousands) — the gallery derives
// its role/level/technology facets client-side. Cloning a general template
// copies the config and forks every question into the caller's own library;
// cloning a custom one copies the config and re-uses the same questions. Both
// return createTest/duplicateTest's exact response shape.

export type TemplateListItem =
  components["schemas"]["TemplateListItem"];
export type TemplateSource = TemplateListItem["source"];
export type SaveAsTemplateRequest =
  components["schemas"]["SaveAsTemplateRequest"];
export type UpdateCustomTemplateRequest =
  components["schemas"]["UpdateCustomTemplateRequest"];

export interface TemplateListResponse {
  templates: TemplateListItem[];
}

export async function fetchTemplates(
  source: TemplateSource = "general",
): Promise<TemplateListResponse> {
  const res = await adminFetch(`/api/admin/templates?source=${source}`);
  return jsonOrThrow(res);
}

// "Save as template" (CP-8) — freezes one of the workspace's tests into a
// custom template. `testId` is the dual-mode slug (UUID or legacy token).
// Returns the gallery card so the Custom tab can show it without a refetch.
export async function saveTestAsTemplate(
  testId: string,
  body: SaveAsTemplateRequest,
): Promise<TemplateListItem> {
  const res = await adminFetch(`/api/admin/tests/${testId}/save-as-template`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

// Custom templates only — a general (Global Library) id 404s on both.
export async function updateCustomTemplate(
  templateId: string,
  body: UpdateCustomTemplateRequest,
): Promise<TemplateListItem> {
  const res = await adminFetch(`/api/admin/templates/${templateId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

export async function deleteCustomTemplate(templateId: string): Promise<void> {
  const res = await adminFetch(`/api/admin/templates/${templateId}`, {
    method: "DELETE",
  });
  // An already-deleted template (double-click, stale tab) is gone either way.
  await okOr404(res);
}

// Same CandidatePreviewResponse as the per-test preview above, so
// PreviewStepper renders both unchanged.
export async function fetchTemplatePreview(
  templateId: string,
): Promise<CandidatePreviewResponse> {
  const res = await adminFetch(
    `/api/admin/templates/${templateId}/candidate-preview`,
  );
  return jsonOrThrow(res);
}

export async function cloneTemplate(
  templateId: string,
  jobIds?: string[],
): Promise<CreateTestResponse> {
  const res = await adminFetch(`/api/admin/templates/${templateId}/clone`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(jobIds && jobIds.length ? { job_ids: jobIds } : {}),
  });
  return jsonOrThrow(res);
}

// ── Template import / export from a file (EF-12) ─────────────────────────
//
// Hand-written mirror of `backend.api.admin.TemplateFile` (like the question
// importers' row types — `api.generated.ts` is not regenerated for this).
// ONE shape for both directions: the export returns exactly what the import
// accepts, so a downloaded file re-imports. Field names are the create /
// settings API's own; lib/templateFile.ts parses JSON or YAML into this
// shape and writes it back out.

export const TEMPLATE_FILE_FORMAT = "candidcheck.template.v1";

export type TemplateFileTestCase = {
  name: string;
  input: string;
  expected_output: string;
};

export type TemplateFileSection = {
  name: string;
  time_limit_seconds: number | null;
  question_count: number;
  shuffle: boolean;
  pool_enabled: boolean;
  draw_count: number | null;
  // EP-17: 1..100, default 1 — counted only while section_weights_enabled.
  weight: number;
};

/** EF-11 — `FileUploadRules` (backend/files/answer_files.py) as the export
 *  writes it. The server owns which extensions are allowed and how large a
 *  file may be; this only carries them. */
export type TemplateFileUploadRules = {
  allowed_extensions: string[];
  max_bytes: number;
  relax_anticheat: boolean;
};

type TemplateFileAttach = {
  answer_required: boolean;
  time_limit_seconds: number | null;
};

type TemplateFileAuthored = TemplateFileAttach & {
  title: string;
  prompt_md: string;
  difficulty: "junior" | "mid" | "senior";
  /** Tag NAMES, ensure-or-created in the workspace on import. */
  tags: string[];
};

export type TemplateFileQuestion =
  | (TemplateFileAuthored & {
      type: "mcq";
      correct_answer: string;
      other_answers: string[];
      shuffle_answers: boolean;
    })
  | (TemplateFileAuthored & {
      type: "mcq_multi";
      correct_answers: string[];
      other_answers: string[];
      shuffle_answers: boolean;
      // FEAT-27: optional — a file written before it carries none (= off).
      partial_credit?: boolean;
    })
  | (TemplateFileAuthored & {
      type: "true_false";
      correct_answer: "True" | "False";
    })
  | (TemplateFileAuthored & {
      type: "open_ended";
      model_answer: string | null;
    })
  | (TemplateFileAuthored & {
      type: "file_upload";
      file_upload_rules: TemplateFileUploadRules;
    })
  | (TemplateFileAuthored & {
      type: "code";
      /** FEAT-6 Build 2: the language the question is set in. The export
       *  always writes it; a file written before the field existed omits
       *  it and imports as Python. */
      language?: Language;
      /** SQL only: the schema + seed rows the candidate's query runs against. */
      setup_code?: string | null;
      starter_code: string;
      example_tests: TemplateFileTestCase[];
      hidden_tests: TemplateFileTestCase[];
    })
  // A question already in this workspace's library, shared (never copied).
  // The file may omit `type` — `question_id` alone identifies the form.
  | (TemplateFileAttach & { type: "reference"; question_id: string });

export type TemplateFile = {
  format: typeof TEMPLATE_FILE_FORMAT;
  name: string;
  description: string | null;
  role: string | null;
  level: "junior" | "mid" | "senior" | null;
  invite_intro: string | null;
  total_time_limit_seconds: number;
  timing_mode: "global" | "per_section" | "per_question";
  shared_clock: boolean;
  break_between_sections: boolean;
  // EP-17: score as weighted sections (per_section only).
  section_weights_enabled: boolean;
  sections: TemplateFileSection[] | null;
  show_score_to_candidate: boolean;
  candidate_feedback_enabled: boolean;
  pass_mark_enabled: boolean | null;
  pass_threshold_pct: number | null;
  shuffle_questions: boolean;
  question_pool_enabled: boolean;
  pool_draw_count: number;
  auto_resend_enabled: boolean;
  allow_back_navigation: boolean;
  cheat_proof_challenges: boolean;
  show_company_welcome: boolean;
  /** FEAT-25: the three core checks; absent in an older file = on. */
  anticheat_camera_enabled: boolean;
  anticheat_fullscreen_enabled: boolean;
  anticheat_activity_enabled: boolean;
  pause_enabled: boolean;
  pause_resume_window_hours: number;
  /** EP-20: the export writes both — `screening_questions` is `null` (never
   *  `[]`) when the template has none. Missing here until 2026-09-21, which
   *  flagged every server-exported file as "Unknown field" in the modal. */
  screening_enabled: boolean;
  screening_questions: TemplateFileScreeningQuestion[] | null;
  questions: TemplateFileQuestion[];
};

/** EP-20 — `ScreeningQuestion` (backend/screening.py) as the export writes it.
 *  The server owns the rules (slug key, non-empty options, …); this carries. */
export type TemplateFileScreeningQuestion = {
  key: string;
  prompt: string;
  options: string[];
  disqualifying: string[];
};

/**
 * Create a custom template from a parsed template file (Core, `bulk_import`).
 * The body is the parsed object as-is — the server is the authoritative
 * validator and answers 422 with the offending path; all-or-nothing.
 * Returns the gallery card so the Custom tab can show it without a refetch.
 */
export async function importTemplate(
  body: Record<string, unknown>,
): Promise<TemplateListItem> {
  const res = await adminFetch(`/api/admin/templates/import`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

/** One of the workspace's CUSTOM templates in the file shape (a Global
 *  Library id 404s). Full question definitions, never ids. */
export async function fetchTemplateExport(
  templateId: string,
): Promise<TemplateFile> {
  const res = await adminFetch(`/api/admin/templates/${templateId}/export`);
  return jsonOrThrow(res);
}

// ── Tags library wrappers ───────────────────────────────────────────────

export async function listTags(): Promise<TagListResponse> {
  const res = await adminFetch(`/api/admin/tags`);
  return jsonOrThrow(res);
}

export async function createTag(
  body: CreateTagRequest,
  opts: { upsertOnDuplicate?: boolean } = {},
): Promise<TagOut> {
  const qs = opts.upsertOnDuplicate ? "?upsert_on_duplicate=true" : "";
  const res = await adminFetch(`/api/admin/tags${qs}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

export async function updateTag(
  tagId: string,
  body: UpdateTagRequest,
): Promise<TagOut> {
  const res = await adminFetch(`/api/admin/tags/${tagId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

export async function deleteTag(tagId: string): Promise<void> {
  const res = await adminFetch(`/api/admin/tags/${tagId}`, { method: "DELETE" });
  // 404 on already-deleted is benign — same posture as deleteTest.
  await okOr404(res);
}

// ── Jobs wrappers ───────────────────────────────────────────────────────
//
// PR 3 cleanup: the `?admin_tokens=` localStorage-scoping plumbing is gone.
// All endpoints below are session-required; the server returns workspace-
// global stats unconditionally.

export async function listJobs(): Promise<JobListResponse> {
  const res = await adminFetch(`/api/admin/jobs`, { credentials: "include" });
  return jsonOrThrow(res);
}

export async function fetchJob(jobId: string): Promise<JobOut> {
  const res = await adminFetch(`/api/admin/jobs/${jobId}`, {
    credentials: "include",
  });
  return jsonOrThrow(res);
}

export async function fetchJobStats(jobId: string): Promise<JobStatsResponse> {
  const res = await adminFetch(`/api/admin/jobs/${jobId}/stats`, {
    credentials: "include",
  });
  return jsonOrThrow(res);
}

export async function createJob(body: CreateJobRequest): Promise<JobOut> {
  const res = await adminFetch(`/api/admin/jobs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

export interface DuplicateJobResponse {
  job: JobOut;
  // How many existing tests were linked into the new job (they now live in
  // both jobs — no new test rows). Drives the success toast.
  copied_test_count: number;
}

// Duplicate a job → a new job with the same name ("<name> (Copy)") +
// description, linked to the SAME existing tests (M2M, so they appear in both
// jobs). 409 if the target is the protected Unassigned bucket (the FE disables
// the button there, so this only fires on a stale tab / forged request).
export async function duplicateJob(
  jobId: string,
): Promise<DuplicateJobResponse> {
  const res = await adminFetch(`/api/admin/jobs/${jobId}/duplicate`, {
    method: "POST",
  });
  return jsonOrThrow(res);
}

export async function updateJob(
  jobId: string,
  body: UpdateJobRequest,
): Promise<JobOut> {
  const res = await adminFetch(`/api/admin/jobs/${jobId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

// CP-5 draft/published. Separate endpoints rather than a PATCH field: the
// job PATCH rejects a body that carries none of its own fields. Publishing is
// idempotent and re-stamps `published_at`; unpublishing 409s on the protected
// Unassigned bucket, which must never be filed under Drafts.
export async function publishJob(jobId: string): Promise<void> {
  const res = await adminFetch(`/api/admin/jobs/${jobId}/publish`, {
    method: "POST",
  });
  await okOrThrow(res);
}

export async function unpublishJob(jobId: string): Promise<void> {
  const res = await adminFetch(`/api/admin/jobs/${jobId}/unpublish`, {
    method: "POST",
  });
  await okOrThrow(res);
}

export async function archiveJob(jobId: string): Promise<void> {
  const res = await adminFetch(`/api/admin/jobs/${jobId}/archive`, { method: "POST" });
  await okOrThrow(res);
}

export async function unarchiveJob(jobId: string): Promise<void> {
  const res = await adminFetch(`/api/admin/jobs/${jobId}/unarchive`, { method: "POST" });
  await okOrThrow(res);
}

export async function deleteJob(jobId: string): Promise<void> {
  const res = await adminFetch(`/api/admin/jobs/${jobId}`, { method: "DELETE" });
  // Tolerant of 404 like deleteTest/deleteTag — frontend optimistic retries
  // should converge instead of erroring on second click.
  await okOr404(res);
}

// ── Attach + detach existing tests to/from a job ───────────────────────
//
// MVP 3.0: tests can live in multiple jobs (test_job junction). "Attach"
// is now additive — adds a link without removing existing ones. Detach
// removes one link; if that was the test's last alive link, the server
// auto-attaches Unassigned (surfaced via `auto_attached_unassigned`).
// Partial-success contract mirrors invite-bulk: every row lands in either
// `accepted` or `rejected`; a single bad UUID doesn't 422 the whole batch.

export type AttachTestRejectionReason =
  | "already_in_job"
  | "deleted"
  | "archived"
  | "not_found";

export interface AttachTestAccepted {
  id: string;
  name: string;
}

export interface AttachTestRejected {
  id: string;
  reason: AttachTestRejectionReason;
}

export interface AttachTestsToJobResponse {
  accepted: AttachTestAccepted[];
  rejected: AttachTestRejected[];
}

export async function attachTestsToJob(
  jobId: string,
  testIds: string[],
): Promise<AttachTestsToJobResponse> {
  const res = await adminFetch(`/api/admin/jobs/${jobId}/attach-tests`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ test_ids: testIds }),
  });
  return jsonOrThrow(res);
}

export type DetachTestRejectionReason = "not_in_job" | "deleted" | "not_found";

export interface DetachTestAccepted {
  id: string;
  name: string;
  // True iff this was the test's last alive job link and the server
  // auto-attached Unassigned to keep the "every test has ≥1 job" invariant.
  // FE uses this to swap the toast: "Removed from <job>" vs "Removed from
  // <job> — moved to Unassigned".
  auto_attached_unassigned: boolean;
}

export interface DetachTestRejected {
  id: string;
  reason: DetachTestRejectionReason;
}

export interface DetachTestsFromJobResponse {
  accepted: DetachTestAccepted[];
  rejected: DetachTestRejected[];
}

export async function detachTestsFromJob(
  jobId: string,
  testIds: string[],
): Promise<DetachTestsFromJobResponse> {
  const res = await adminFetch(`/api/admin/jobs/${jobId}/detach-tests`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ test_ids: testIds }),
  });
  return jsonOrThrow(res);
}

// ── Team management (MVP 3.0; FEAT-39: whoever holds a users box) ─────────
//
// Hand-typed (not from the codegen schema) so the Team page isn't blocked on a
// gen:api run — mirrors the WorkspaceTestItem pattern above. Every endpoint is
// gated server-side (403 for a role holding none of Emilia's three users
// boxes); the Team nav item is hidden for them, and the page shows a notice
// on a typed URL.

export interface TeamMember {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  // One of the four team roles; typed loosely because a newer server may send
  // one this build does not know (it is then shown as written).
  role: TeamRole | (string & {});
  is_verified: boolean;
  last_login_at: string | null;
  is_self: boolean;
  // EP-18. Effective, not raw — an owner or admin always reads false.
  // `job_ids` are the alive recruitments a restricted person may see; EMPTY
  // while restricted means they see NOTHING (the opposite of the EP-9
  // notification filter).
  job_access_restricted: boolean;
  job_ids: string[];
  // FEAT-39: limited AS A ROLE (the owner ticked "Access just to your own
  // job" for it) — the access dialog then offers no "All recruitments".
  restricted_by_role?: boolean;
  // FEAT-28: whether the CALLER may edit / remove / reset / restrict this
  // person. The server's own rank rule — the page offers what it says, and the
  // server re-checks every action.
  can_manage: boolean;
  // 10-07 S4: the ACTIVE API keys and webhooks this person owns — what a
  // removal, or a demotion below admin, revokes. Optional: a server from
  // before S4 omits them (read as 0).
  api_key_count?: number;
  webhook_count?: number;
}

export interface PendingInvite {
  id: string;
  email: string;
  role: string;
  created_at: string;
  expires_at: string;
  // FEAT-28: whether the caller may resend / revoke it.
  can_manage: boolean;
}

// FEAT-50b: one request to join, from the sign-up's used-domain refusal.
// `status` is derived server-side: `pending`, or `expired` (shown as such,
// cannot be approved — only declined, which clears it). Decided requests
// leave the list.
export interface JoinRequestRow {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  created_at: string;
  expires_at: string;
  status: "pending" | "expired";
  // May the caller approve (= hand out the Recruiter role) / decline it?
  can_approve: boolean;
  can_decline: boolean;
}

export interface TeamResponse {
  members: TeamMember[];
  invitations: PendingInvite[];
  // FEAT-50b: the open requests to join (pending + expired).
  join_requests?: JoinRequestRow[];
  // FEAT-28: the roles the CALLER may hand out — in an invitation, and in a
  // role change (an owner's `grantable_roles` also holds "owner").
  invitable_roles: string[];
  grantable_roles: string[];
  // FEAT-39: may the caller change a teammate's role or recruitment access
  // (Emilia's "Permissions of users")?
  can_change_permissions?: boolean;
  // FEAT-39: what each role may do HERE, resolved — for the dialogs' lines.
  role_permissions?: Record<string, string[]>;
}

export async function fetchTeam(): Promise<TeamResponse> {
  const res = await adminFetch(`/api/admin/team`, { credentials: "include" });
  return jsonOrThrow(res);
}

// The team mutations carry structured `detail.code`s the UI maps to copy
// (ALREADY_A_MEMBER, EMAIL_BELONGS_TO_OTHER_WORKSPACE, CANNOT_REMOVE_SELF, …).
// Throw an Error whose message IS the code so the page can switch on it; fall
// back to "API <status>" when the body has no code.
async function teamActionOrThrow(res: Response): Promise<void> {
  if (res.ok) return;
  let detail: unknown;
  try {
    detail = ((await res.clone().json()) as { detail?: unknown }).detail;
  } catch {
    /* fall through to status-based message */
  }
  // S14 (verify 2026-10-07): the plan wall's 402 carries a sentence for
  // people — kept, with its status + detail, for `planWallError`.
  if (isPlanRequired(res.status, detail)) {
    throw new ApiError(planRequiredMessage(detail), res.status, detail);
  }
  let code: string | null = null;
  if (typeof detail === "string") code = detail;
  else if (detail && typeof detail === "object") {
    code = (detail as { code?: string }).code ?? null;
  }
  throw new Error(code ?? `API ${res.status}`);
}

// FEAT-28: the invitation carries the role (never "owner" — the server
// refuses it; an owner is made by promoting a member).
export async function inviteTeamMember(
  email: string,
  role: Exclude<TeamRole, "owner"> = "member",
): Promise<void> {
  const res = await adminFetch(`/api/admin/team/invite`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, role }),
  });
  await teamActionOrThrow(res);
}

export async function resendTeamInvitation(invitationId: string): Promise<void> {
  const res = await adminFetch(
    `/api/admin/team/invitations/${invitationId}/resend`,
    { method: "POST" },
  );
  await teamActionOrThrow(res);
}

export async function revokeTeamInvitation(invitationId: string): Promise<void> {
  const res = await adminFetch(
    `/api/admin/team/invitations/${invitationId}/revoke`,
    { method: "POST" },
  );
  await teamActionOrThrow(res);
}

// FEAT-50b: approving a request to join IS the ordinary team invitation —
// the same seat check (SEAT_LIMIT_REACHED with the plan's own message), the
// same global-email guard (ALREADY_A_MEMBER, EMAIL_BELONGS_TO_OTHER_WORKSPACE),
// plus JOIN_REQUEST_EXPIRED / JOIN_REQUEST_ALREADY_DECIDED. Returns the
// server's status: `invited`, or `already_invited` when a live invitation to
// that address was already pending (nothing is sent twice).
export async function approveJoinRequest(
  requestId: string,
  role: Exclude<TeamRole, "owner"> = "member",
): Promise<"invited" | "already_invited"> {
  const res = await adminFetch(
    `/api/admin/team/join-requests/${requestId}/approve`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role }),
    },
  );
  await teamActionOrThrow(res);
  const body = (await res.json()) as { status?: string };
  return body.status === "already_invited" ? "already_invited" : "invited";
}

// FEAT-50b: declining closes the request; nothing is sent to the person.
export async function declineJoinRequest(requestId: string): Promise<void> {
  const res = await adminFetch(
    `/api/admin/team/join-requests/${requestId}/decline`,
    { method: "POST" },
  );
  await teamActionOrThrow(res);
}

export async function removeTeamMember(recruiterId: string): Promise<void> {
  const res = await adminFetch(`/api/admin/team/members/${recruiterId}`, {
    method: "DELETE",
  });
  await teamActionOrThrow(res);
}

// Owner-only: email a teammate the standard password-reset link (same flow as
// the self-service "Forgot password" on /login). Surfaces "API 429" / "API 404"
// as Error.message via teamActionOrThrow.
export async function resetTeamMemberPassword(
  recruiterId: string,
): Promise<void> {
  const res = await adminFetch(
    `/api/admin/team/members/${recruiterId}/reset-password`,
    { method: "POST" },
  );
  await teamActionOrThrow(res);
}

// Edit a teammate's name / email / role (owner, or an admin on a recruiter or
// hiring manager). Send only the changed fields. Surfaces the same structured
// codes (EMAIL_TAKEN, LAST_OWNER, CANNOT_EDIT_SELF, and FEAT-28's
// ROLE_OUT_OF_REACH / ROLE_NOT_GRANTABLE) as Error.message via teamActionOrThrow.
export async function updateTeamMember(
  recruiterId: string,
  body: {
    first_name?: string;
    last_name?: string;
    email?: string;
    role?: TeamRole;
  },
): Promise<void> {
  const res = await adminFetch(`/api/admin/team/members/${recruiterId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  await teamActionOrThrow(res);
}

// EP-18 — owner-only: limit a teammate to selected recruitments, or lift it.
// Whole-set replace of BOTH fields: `restricted: true` with `[]` is legal and
// means they see nothing. Surfaces UNKNOWN_JOB_IDS / OWNER_CANNOT_BE_RESTRICTED
// / CANNOT_EDIT_SELF as Error.message via teamActionOrThrow.
export async function updateTeamMemberJobAccess(
  recruiterId: string,
  body: { restricted: boolean; job_ids: string[] },
): Promise<TeamMember> {
  const res = await adminFetch(
    `/api/admin/team/members/${recruiterId}/job-access`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  await teamActionOrThrow(res);
  return (await res.json()) as TeamMember;
}

// ── Roles & permissions (FEAT-39, owner only) ────────────────────────────

/** `matrix[role][box]` — ticked or not. */
export type RoleMatrix = Record<string, Record<string, boolean>>;

export interface RolePermissionsRead {
  roles: TeamRole[];
  boxes: string[];
  locked_roles: string[];
  matrix: RoleMatrix;
  defaults: RoleMatrix;
}

export async function fetchRolePermissions(): Promise<RolePermissionsRead> {
  const res = await adminFetch(`/api/admin/role-permissions`);
  return jsonOrThrow(res);
}

/** Whole-row replace for the roles given — every box of a listed role. */
export async function saveRolePermissions(
  matrix: RoleMatrix,
): Promise<RolePermissionsRead> {
  const res = await adminFetch(`/api/admin/role-permissions`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ matrix }),
  });
  return jsonOrThrow(res);
}

// ── SessionStorage helpers (frontend-only) ──────────────────────────────

const ATTEMPT_KEY_PREFIX = "interview-attempt:";
const SUBMITTED_ATTEMPT_KEY_PREFIX = "interview-submitted-attempt-id:";
const CANDIDATE_META_PREFIX = "interview-candidate-meta:";

// night-p5 (2026-10-08): sessionStorage can be ABSENT or THROW — "block all
// cookies" in Chrome, Safari with site data blocked, a sandboxed iframe — and
// every helper below used it bare. `saveAttemptId` then threw inside /start's
// success path (the attempt existed server-side, the tab never learned its
// id) and every later mount read null and bounced the candidate to the gate,
// where the consumed link answered 410. Keep the values in memory for the
// life of the tab's JavaScript instead: sessionStorage's own scope (one tab,
// never shared) minus survival across a reload — which that candidate did not
// have anyway. Memory is written ONLY when storage refuses the write, so a
// working storage stays the single source of truth (and `sessionStorage.clear()`
// in a test cannot leave a stale shadow behind).
const memoryStore = new Map<string, string>();

function tabStoreGet(key: string): string | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw !== null) return raw;
  } catch {
    // blocked — fall through to memory
  }
  return memoryStore.get(key) ?? null;
}

function tabStoreSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
    memoryStore.delete(key);
    return;
  } catch {
    // blocked (or full): the in-memory copy is the only one this tab has
  }
  memoryStore.set(key, value);
}

function tabStoreRemove(key: string): void {
  memoryStore.delete(key);
  try {
    sessionStorage.removeItem(key);
  } catch {
    // nothing to clear there
  }
}

export function saveAttemptId(token: string, attemptId: string): void {
  tabStoreSet(`${ATTEMPT_KEY_PREFIX}${token}`, attemptId);
}

/**
 * Canonical UUID shape. The backend types `X-Attempt-Id` as a `UUID`
 * (backend/api/candidate.py), so anything else is rejected with a 422 before any
 * lookup happens — it can never become a valid attempt.
 */
const ATTEMPT_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Reads the in-flight attempt id, treating a malformed value as absent.
 *
 * Verified 2026-08-26: a non-UUID under this key trapped the candidate for good.
 * The gate resumed from it (FRONTEND-REACT-8), Question sent it as the header,
 * FastAPI answered 422, no branch classified a 422 — so they sat on "Failed to
 * load the question. Please refresh the page." and refreshing reproduced it
 * every single time. Screening it here fixes every consumer at once rather than
 * per-route, and the value is dropped so storage self-heals instead of
 * re-failing on the next mount.
 */
export function loadAttemptId(token: string): string | null {
  const raw = tabStoreGet(`${ATTEMPT_KEY_PREFIX}${token}`); // night-p5
  if (raw === null) return null;
  if (ATTEMPT_ID_RE.test(raw)) return raw;
  tabStoreRemove(`${ATTEMPT_KEY_PREFIX}${token}`);
  return null;
}

export function clearAttemptId(token: string): void {
  tabStoreRemove(`${ATTEMPT_KEY_PREFIX}${token}`); // night-p5
}

/**
 * The stored attempt is gone (voided, erased, or junk): drop its id and the
 * answers B5 kept for it in the tab, and run the Gate again. `from: "bounce"`
 * is the Gate's contract — a bounce is never auto-resumed (the gate-resume
 * loop, candidateFlowGuards.test.ts).
 */
export function bounceToGate(token: string, navigate: NavigateFunction): void {
  const attemptId = loadAttemptId(token);
  clearAttemptId(token);
  if (attemptId) clearAttemptAnswerDrafts(attemptId);
  navigate(`/t/${token}`, { replace: true, state: { from: "bounce" } });
}

const START_NONCE_PREFIX = "interview-start-nonce:";
const START_NONCE_RE = /^[0-9a-f]{32}$/;

/**
 * S1 (2026-09-27 pre-ship audit): the random nonce this TAB sends with its
 * `POST /start` for this link — minted on the first click and re-used by every
 * retry until a start succeeds (`clearStartNonce`), so a click whose response
 * was lost, or a reload of the Gate, can prove it is the same start and get
 * the attempt back. The server keeps only its sha256; a request without it —
 * someone else on the candidate's network with the same browser who knows
 * their email — is refused instead of handed the live attempt id. Storage
 * that throws (a locked-down browser) keeps the nonce in memory for the tab.
 */
export function startNonce(token: string): string {
  // Pre-ship 2026-10-08: through the tab store, so a browser that blocks
  // storage keeps the SAME nonce across retries in this tab (night-p5's
  // fallback) — a fresh nonce per retry could never replay a lost response.
  const key = `${START_NONCE_PREFIX}${token}`;
  const stored = tabStoreGet(key);
  if (stored !== null && START_NONCE_RE.test(stored)) return stored;
  const nonce = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
    b.toString(16).padStart(2, "0"),
  ).join("");
  tabStoreSet(key, nonce);
  return nonce;
}

export function clearStartNonce(token: string): void {
  tabStoreRemove(`${START_NONCE_PREFIX}${token}`);
}

// Read-only handle kept alive across refreshes of /done so the score fetch
// can re-run if the candidate reloads the page (e.g., to check whether a
// pending code-question score has materialised). Separate key so an
// accidental re-use in /question or /answer routes can't blow past the
// post-submit guard — those routes only look at the in-flight key above.
export function saveSubmittedAttemptId(token: string, attemptId: string): void {
  tabStoreSet(`${SUBMITTED_ATTEMPT_KEY_PREFIX}${token}`, attemptId); // night-p5
}

export function loadSubmittedAttemptId(token: string): string | null {
  return tabStoreGet(`${SUBMITTED_ATTEMPT_KEY_PREFIX}${token}`); // night-p5
}

// Remembers that THIS attempt already ended on the terminal time-limit 410, so
// /done can keep showing the calm time-up card when a later mount can't get the
// 410 again. It normally can: the deadline check re-fires on every /submit. But
// /submit is rate-limited (5/min per attempt+IP) and the limiter runs BEFORE the
// route, so the 6th reload inside a minute returns a 429 whose body carries no
// `detail` — parseTimeLimitReached rightly declines it, and without this the
// screen would fall back to the red "We couldn't finalize your submission" card
// that BUG-6 exists to remove. Only consulted after a failed submit, so the
// live-resolved message still wins whenever the 410 does arrive.
const TIME_UP_KEY_PREFIX = "interview-time-up:";

export function saveTimeUpState(
  token: string,
  attemptId: string,
  message: string | null,
): void {
  tabStoreSet(
    `${TIME_UP_KEY_PREFIX}${token}`, // night-p5
    JSON.stringify({ attemptId, message }),
  );
}

/** The remembered time-up state, but only if it belongs to `attemptId` — a
 *  shared link can start a fresh attempt in the same tab, and that one must
 *  never inherit the previous candidate's terminal screen. */
export function loadTimeUpState(
  token: string,
  attemptId: string,
): TimeLimitReached | null {
  try {
    const raw = tabStoreGet(`${TIME_UP_KEY_PREFIX}${token}`); // night-p5
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { attemptId?: unknown; message?: unknown };
    if (parsed.attemptId !== attemptId) return null;
    return {
      timeLimitMessage:
        typeof parsed.message === "string" ? parsed.message : null,
    };
  } catch {
    return null;
  }
}

export function saveCandidateMeta(token: string, meta: CandidateMeta): void {
  tabStoreSet(`${CANDIDATE_META_PREFIX}${token}`, JSON.stringify(meta)); // night-p5
}

export function loadCandidateMeta(token: string): CandidateMeta | null {
  try {
    const raw = tabStoreGet(`${CANDIDATE_META_PREFIX}${token}`); // night-p5
    if (!raw) return null;
    return JSON.parse(raw) as CandidateMeta;
  } catch {
    return null;
  }
}

export function clearCandidateMeta(token: string): void {
  tabStoreRemove(`${CANDIDATE_META_PREFIX}${token}`); // night-p5
}

// ── Back office (creator-only, cross-tenant) — MVP 4.0 ────────────────────
// Every call is superuser-gated server-side (403 otherwise). adminFetch carries
// the session cookie and nulls the cached session on a 401.
export type BackofficeOverview = Schemas["BackofficeOverviewResponse"];
export type BackofficeCompanies = Schemas["CompaniesResponse"];
export type BackofficeRecruiters = Schemas["RecruitersResponse"];
export type BackofficeAntiCheat = Schemas["AntiCheatResponse"];
export type PlatformProblemReports = Schemas["ProblemReportsResponse"];
export type PlatformProblemReportRow = Schemas["ProblemReportRow"];
export type ProblemReportStatusFilter = "open" | "resolved" | "all";

// Serialise a flat params object to a query string, skipping empty values so a
// blank search / unset filter doesn't pin a noisy cache key or query param.
function buildParams(params: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      // FastAPI reads a list param as REPEATED keys (`?status=a&status=b`).
      // `set(String(array))` would send "a,b" as one value and 422 — added for
      // EP-23's multi-select status axis; no earlier caller passes an array.
      for (const item of v) {
        if (item === undefined || item === null || item === "") continue;
        sp.append(k, String(item));
      }
      continue;
    }
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export interface CompanyListParams {
  q?: string;
  suspended?: boolean;
  approval?: "pending" | "approved" | "declined";
  // FEAT-51: `real` (the default) hides the operator's test companies;
  // `trial` keeps only rows whose free trial is in force (or not).
  kind?: "real" | "test" | "all";
  trial?: boolean;
  sort?: "recent" | "name" | "oldest";
  limit?: number;
  offset?: number;
}
export interface RecruiterListParams {
  q?: string;
  company_id?: string;
  role?: TeamRole;
  is_verified?: boolean;
  suspended?: boolean;
  removed?: boolean;
  platform_role?: "none" | "support" | "superuser" | "viewer";
  limit?: number;
  offset?: number;
}
export interface OperatorListParams {
  q?: string;
  role?: "support" | "superuser" | "viewer";
  limit?: number;
  offset?: number;
}
export interface BillingListParams {
  q?: string;
  plan?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export async function fetchBackofficeOverview(): Promise<BackofficeOverview> {
  return jsonOrThrow(await adminFetch(`/api/backoffice/overview`));
}

export async function fetchBackofficeCompanies(
  params: CompanyListParams = {},
): Promise<BackofficeCompanies> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/companies${buildParams(params)}`),
  );
}

/** FEAT-50a — trial protection's monitoring share: over the last
 *  `window_days` UTC days, how many sign-up attempts there were (workspaces
 *  created + refusals) and what share came from a domain that already had a
 *  workspace. Counts and a percentage only — no domain or address. Hand-typed
 *  (not `gen:api`, which drags unrelated drift into api.generated.ts). */
export interface BackofficeSignupRefusals {
  window_days: number;
  workspaces_created: number;
  refused: number;
  refused_free_mail: number;
  refused_disposable: number;
  refused_domain_has_workspace: number;
  attempts: number;
  used_domain_share_pct: number;
}

export async function fetchBackofficeSignupRefusals(
  days = 30,
): Promise<BackofficeSignupRefusals> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/signup-refusals?days=${days}`),
  );
}

/** EP-1 — one workspace's full administrative record: CP-2 legal details, the
 *  effective plan and why, usage against the caps, and the OWNER's name +
 *  email as the decision-maker contact. Aliased off the generated schema (not
 *  hand-declared) so a backend field can never go missing here silently. */
export type BackofficeCompanyDetail = Schemas["CompanyDetailRead"];
export type BackofficeCompanyOwner = Schemas["CompanyOwnerRead"];

export async function fetchBackofficeCompanyDetail(
  companyId: string,
): Promise<BackofficeCompanyDetail> {
  return jsonOrThrow(await adminFetch(`/api/backoffice/companies/${companyId}`));
}

export async function fetchBackofficeRecruiters(
  params: RecruiterListParams = {},
): Promise<BackofficeRecruiters> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/recruiters${buildParams(params)}`),
  );
}

// --- Recruiter detail + plan management (CP-3, superuser-only) ------------
//
// The plan is a property of the COMPANY, which is why the read hangs off a
// recruiter but both writes are company-scoped. Server contract:
// backend/api/backoffice.py.
export type BackofficeRecruiterDetail =
  Schemas["RecruiterDetailRead"];
export type BackofficeCompanyPlanResult = Schemas["CompanyPlanResult"];
export type OverridablePlan = Schemas["SetCompanyPlanIn"]["plan"];

export async function fetchBackofficeRecruiter(
  recruiterId: string,
): Promise<BackofficeRecruiterDetail> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/recruiters/${recruiterId}`),
  );
}

export async function fetchBackofficeAntiCheat(
  params: { limit?: number; offset?: number } = {},
): Promise<BackofficeAntiCheat> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/anticheat${buildParams(params)}`),
  );
}

// Billing oversight (superuser-only). `plan` is the EFFECTIVE plan resolved
// server-side — an operator override (CP-3) first, then the company's Paddle
// mirror, then the grandfathered default. Hand-written types (like the Recovery
// block below); swap for Schemas[...] after `npm run gen:api` if desired.
export interface BackofficeBillingRow {
  company_id: string;
  company_name: string;
  plan: string;
  status: string;
  // "override" when an operator granted this plan; null when it came from
  // Paddle or the default. Without it the list implies every plan was paid for.
  plan_source: string | null;
  owner_email: string | null;
  recruiters: number;
  last_active_at: string | null;
  // FEAT-2 (MVP 5.5): a self-serve downgrade still inside the paid period —
  // `plan` stays the tier paid for until `pending_effective_at`.
  pending_to_plan?: string | null;
  pending_effective_at?: string | null;
}
export interface BackofficeBilling {
  items: BackofficeBillingRow[];
  total: number;
  truncated: boolean;
  has_more: boolean;
  limit: number;
  offset: number;
}
export interface BackofficeBillingPlanStat {
  plan: string;
  companies: number;
}
export interface BackofficeBillingStats {
  by_plan: BackofficeBillingPlanStat[];
  total_companies: number;
}

export async function fetchBackofficeBilling(
  params: BillingListParams = {},
): Promise<BackofficeBilling> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/billing${buildParams(params)}`),
  );
}

export async function fetchBackofficeBillingStats(): Promise<BackofficeBillingStats> {
  return jsonOrThrow(await adminFetch(`/api/backoffice/billing/stats`));
}

// AI (LLM) usage & cost oversight (read-only, superuser-only). A metadata-only
// ledger view: platform totals + per-company spend over a window. Cost is the
// provider's exact figure (OpenRouter) or a price-map estimate — see backend/
// api/backoffice.py. Hand-written types (like the Billing block above).
export type LlmUsageWindow = "7d" | "30d" | "90d" | "all";

export interface BackofficeLlmUsageTotals {
  calls: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_usd: number;
  has_estimates: boolean;
  missing_cost: boolean;
  /** How many calls in the window have no recorded cost (excluded from cost_usd). */
  missing_cost_calls: number;
}
export interface BackofficeLlmUsageCompanyRow {
  company_id: string | null;
  company_name: string;
  calls: number;
  prompt_tokens: number;
  completion_tokens: number;
  cost_usd: number;
  has_estimates: boolean;
}
/** Provider-reported spend for the configured key (OpenRouter). Null when the
 * provider has no such API, no key is set, or the lookup failed — the page then
 * renders the ledger alone. This is the only figure comparable to the provider
 * dashboard: the ledger below counts recruiter actions on THIS deployment. */
export interface BackofficeProviderAccount {
  provider: string;
  usage_total_usd: number | null;
  usage_monthly_usd: number | null;
  usage_weekly_usd: number | null;
  usage_daily_usd: number | null;
}
export interface BackofficeLlmUsage {
  window: LlmUsageWindow;
  window_days: number | null;
  window_start: string | null;
  llm_configured: boolean;
  llm_model: string;
  totals: BackofficeLlmUsageTotals;
  /** Ledger cost across ALL time — the like-for-like counterpart to the
   * provider's lifetime `usage_total_usd`. Independent of the window. */
  all_time_cost_usd: number;
  /** Ledger cost for the current calendar month (UTC, month-to-date) — the
   * like-for-like counterpart to the provider's `usage_monthly_usd`. */
  month_to_date_cost_usd: number;
  by_company: BackofficeLlmUsageCompanyRow[];
  truncated: boolean;
  total: number;
  has_more: boolean;
  limit: number;
  offset: number;
  provider_account: BackofficeProviderAccount | null;
}

export async function fetchBackofficeLlmUsage(
  window: LlmUsageWindow = "30d",
  params: { limit?: number; offset?: number } = {},
): Promise<BackofficeLlmUsage> {
  return jsonOrThrow(
    await adminFetch(
      `/api/backoffice/llm-usage${buildParams({ window, ...params })}`,
    ),
  );
}

// FEAT-49 (MVP 8.5): the landing funnel — what candidcheck.com's job-offer
// builder produced per UTC day: builds (and how many ended `ready`), unique
// visitors (an HMAC of the address and the day — the server never stores the
// address, so a visitor on two days is two visitors), re-picks (level /
// counts / "Shuffle") and the workspaces created through the landing's inline
// sign-up (`company.signup_source = 'landing'`). Counts only. Hand-written
// types, like the blocks around it.
export interface BackofficeLandingFunnelDay {
  /** YYYY-MM-DD (UTC). */
  date: string;
  builds: number;
  builds_ready: number;
  visitors: number;
  repicks: number;
  signups: number;
}

export interface BackofficeLandingFunnel {
  window_days: number;
  /** Zero-filled, oldest first; the last row is today. */
  days: BackofficeLandingFunnelDay[];
  totals: Omit<BackofficeLandingFunnelDay, "date">;
  /** The per-IP daily build limit as configured — per machine on prod (×2). */
  builds_per_day_per_ip: number;
  landing_builder_enabled: boolean;
}

export async function fetchBackofficeLandingFunnel(
  days = 30,
): Promise<BackofficeLandingFunnel> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/landing-funnel${buildParams({ days })}`),
  );
}

// OPS-32: the back-office Overview's intelligence cards (superuser-only,
// metadata and counts only — company names, plan labels, percentages, money
// totals; never a candidate, an answer or a payload). The twelve weekly rows
// are ISO weeks (Monday, UTC) and ignore the window; the window bounds the
// KPI tiles and the "in the window" columns. Definitions: docs/features/
// ops-32-intelligence.md §2. Hand-written types, like the blocks above.
export interface BackofficeIntelligenceWeek {
  /** Monday of the ISO week, YYYY-MM-DD (UTC). The last row is the current, partial week. */
  week_start: string;
  /** Cohort: companies CREATED this week, and how many of them have reached each milestone so far. */
  signups: number;
  activated_test: number;
  activated_invitation: number;
  activated_completion: number;
  /** Any recorded act this week (a test / job / question, an invitation, an attempt, an AI call, a code run). */
  active_companies: number;
  /** Recruiters who sent, created or shared something this week — a proxy, logins are not journaled. */
  active_recruiters: number;
  invited: number;
  completed: number;
  /** The recruiter's own verdict (backend/risk.py) over the week's submitted attempts. */
  flagged: number;
  ai_cost_usd: number;
  ai_has_estimates: boolean;
  /** Runner pools only, Σ cpu × the Fly rate — always an estimate. */
  code_run_cost_usd: number;
}
export type BackofficeStuckStage = "no_test" | "no_invitation";
export interface BackofficeStuckRow {
  company_id: string;
  company_name: string;
  created_at: string;
  stage: BackofficeStuckStage;
}
export interface BackofficeActivationStuck {
  no_test: number;
  no_invitation: number;
  rows: BackofficeStuckRow[];
}
export interface BackofficeActiveNow {
  recruiters_7d: number;
  recruiters_30d: number;
  companies_7d: number;
  companies_30d: number;
}
export interface BackofficeIntelligenceTotals {
  invited: number;
  started: number;
  completed: number;
  flagged: number;
  ai_cost_usd: number;
  ai_has_estimates: boolean;
  code_run_cost_usd: number;
}
export type BackofficeCapPeriod = "month" | "trial";
export interface BackofficeCandidateCapRow {
  company_id: string;
  company_name: string;
  plan: string;
  period: BackofficeCapPeriod;
  used: number;
  cap: number;
  pct: number;
}
export interface BackofficeSeatCapRow {
  company_id: string;
  company_name: string;
  plan: string;
  pool: "recruiters" | "managers";
  used: number;
  cap: number;
  pct: number;
}
export interface BackofficeTrialEndingRow {
  company_id: string;
  company_name: string;
  ends_at: string;
  days_left: number;
}
export interface BackofficeAiAllowanceRow {
  company_id: string;
  company_name: string;
  period: BackofficeCapPeriod;
  used_pct: number;
  allowance_source: "company" | "global" | "trial" | "none";
}
export interface BackofficeCapPressure {
  threshold_pct: number;
  candidates: BackofficeCandidateCapRow[];
  candidates_total: number;
  seats: BackofficeSeatCapRow[];
  seats_total: number;
  trials_ending: BackofficeTrialEndingRow[];
  trials_ending_total: number;
  ai_allowance: BackofficeAiAllowanceRow[];
  ai_allowance_total: number;
}
export interface BackofficeWatchRow {
  company_id: string;
  company_name: string;
  plan: string;
  plan_status: string;
  override_note: string | null;
  suspended: boolean;
  tests: number;
  invited: number;
  completed: number;
  code_runs: number;
  ai_used_pct: number | null;
  last_activity_at: string | null;
  open_problem_reports: number;
}
export interface BackofficeWatchlist {
  pilots: BackofficeWatchRow[];
  demo: BackofficeWatchRow | null;
}
export interface BackofficeProblemReportsCard {
  open: number;
  open_over_7d: number;
  oldest_open_at: string | null;
  resolved_in_window: number;
  candidate_reports_in_window: number;
}
export interface BackofficeIntelligence {
  generated_at: string;
  window: LlmUsageWindow;
  window_days: number | null;
  window_start: string | null;
  /** Monday of the first of the twelve weeks, YYYY-MM-DD. */
  weeks_start: string;
  weeks: BackofficeIntelligenceWeek[];
  activation_stuck: BackofficeActivationStuck;
  active_now: BackofficeActiveNow;
  window_totals: BackofficeIntelligenceTotals;
  cap_pressure: BackofficeCapPressure;
  watchlist: BackofficeWatchlist;
  problem_reports: BackofficeProblemReportsCard;
}

export async function fetchBackofficeIntelligence(
  window: LlmUsageWindow = "30d",
): Promise<BackofficeIntelligence> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/intelligence${buildParams({ window })}`),
  );
}

// FEAT-12 Build 6: the code runner's execution meter (read-only, superuser-
// only) — the AI meter's twin over the metadata-only `code_execution_usage`
// ledger: company + language + timings, never source or a candidate. Every
// cost is an ESTIMATE (Σ CPU-seconds × the Fly price, computed server-side at
// read time; the warm machine's floor is NOT in it). Hand-written types like
// the LLM block above; the company detail's `code_execution` is generated.
export interface BackofficeCodeRunsTotals {
  runs: number;
  tests: number;
  compile_s: number;
  run_s: number;
  cpu_s: number;
  timeouts: number;
  compile_errors: number;
  runner_errors: number;
  /** Rows that never reached the runner (no CPU figure) — outside every second and the cost. */
  unpriced_runs: number;
  est_cost_usd: number;
  cost_source: "estimated";
}
export interface BackofficeCodeRunsLanguageRow extends BackofficeCodeRunsTotals {
  language: string;
  name: string;
  pool: string;
  p50_wall_ms: number | null;
  p95_wall_ms: number | null;
}
export interface BackofficeCodeRunsPurposeRow {
  purpose: "run" | "grade" | "author" | "check";
  runs: number;
  tests: number;
  cpu_s: number;
  est_cost_usd: number;
}
export interface BackofficeCodeRunsCompanyRow {
  company_id: string | null;
  company_name: string;
  runs: number;
  tests: number;
  seconds: number;
  cpu_s: number;
  timeouts: number;
  est_cost_usd: number;
}
/** FEAT-18: Runs in the candidate's BROWSER (Python, JavaScript, TypeScript,
 *  SQL), as the page reported them — a log, in none of the runner figures. */
export interface BackofficeCodeRunsBrowserRow {
  language: string;
  name: string;
  runs: number;
  tests: number;
  /** Runs where every example test passed. */
  passed_runs: number;
}
export interface BackofficeCodeRunsUsage {
  window: LlmUsageWindow;
  window_days: number | null;
  window_start: string | null;
  runner_configured: boolean;
  rate_usd_per_cpu_second: number;
  totals: BackofficeCodeRunsTotals;
  by_language: BackofficeCodeRunsLanguageRow[];
  by_purpose: BackofficeCodeRunsPurposeRow[];
  by_company: BackofficeCodeRunsCompanyRow[];
  browser_runs: BackofficeCodeRunsBrowserRow[];
  truncated: boolean;
  total: number;
  has_more: boolean;
  limit: number;
  offset: number;
}
export type BackofficeRunnerPoolState =
  | "warm"
  | "warming"
  | "asleep"
  | "unreachable"
  | "sandbox_unavailable"
  | "unconfigured";
export interface BackofficeRunnerPoolStatus {
  pool: string;
  configured: boolean;
  url_host: string | null;
  state: BackofficeRunnerPoolState;
  ok: boolean | null;
  warming: boolean | null;
  inflight: number | null;
  max_inflight: number | null;
  image: string | null;
  machine: string | null;
  region: string | null;
  started_at: string | null;
  latency_ms: number | null;
  languages: { id: string; version: string }[];
  /** True when `languages` is the backend's last-known-good list (the live fetch failed). */
  cached: boolean;
  error: string | null;
}
/** Both pools, probed live. ⚠ Fetching this WAKES a sleeping pool — it is its
 * own route and query so a window or page change never triggers it. */
export interface BackofficeRunnerStatus {
  runner_configured: boolean;
  pools: BackofficeRunnerPoolStatus[];
  unsettled: string[];
  probed_at: string;
}

export async function fetchBackofficeCodeRunsUsage(
  window: LlmUsageWindow = "30d",
  params: { limit?: number; offset?: number } = {},
): Promise<BackofficeCodeRunsUsage> {
  return jsonOrThrow(
    await adminFetch(
      `/api/backoffice/code-execution-usage${buildParams({ window, ...params })}`,
    ),
  );
}

export async function fetchBackofficeRunnerStatus(): Promise<BackofficeRunnerStatus> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/code-execution-usage/runner-status`),
  );
}

// Traffit (ATS) OUTBOUND API-call usage — per-company call volume + failures +
// last error over a window, from the metadata-only `ats_api_call` ledger (see
// backend/api/backoffice.py). Same window vocabulary as AI usage; hand-written
// types (like the AI-usage block above).
export interface BackofficeAtsUsageTotals {
  calls: number;
  failures: number;
  companies: number;
}
export interface BackofficeAtsUsageCompanyRow {
  company_id: string | null;
  company_name: string;
  calls: number;
  failures: number;
  last_error: string | null;
  last_status: number | null;
  last_call_at: string | null;
}
export interface BackofficeAtsUsage {
  window: LlmUsageWindow;
  window_days: number | null;
  enabled_company_count: number;
  totals: BackofficeAtsUsageTotals;
  by_company: BackofficeAtsUsageCompanyRow[];
  truncated: boolean;
  total: number;
  has_more: boolean;
  limit: number;
  offset: number;
}

export async function fetchBackofficeAtsUsage(
  window: LlmUsageWindow = "30d",
  params: { limit?: number; offset?: number } = {},
): Promise<BackofficeAtsUsage> {
  return jsonOrThrow(
    await adminFetch(
      `/api/backoffice/ats-usage${buildParams({ window, ...params })}`,
    ),
  );
}

// Integrations observability (OPS-10) — webhook + public-API adoption and health
// across every consumer, per company, over a window. Metadata only: the server
// never returns a delivery payload (candidate PII), a signing secret, or a URL
// path (the path IS the shared secret for every consumer we support), so there
// is deliberately no field here to render one. Hand-written types like the
// AI-usage / Traffit blocks above.
//
// `provider` is a plain string, NOT WebhookKind: it is derived server-side from
// the URL host (so a Make endpoint stored as "generic" reports correctly), and
// legacy kinds such as "teams" still exist and must stay visible. Label lookups
// must fall back to the raw value.
export interface BackofficeIntegrationsTotals {
  deliveries: number;
  delivered: number;
  failed: number;
  exhausted: number;
  in_flight: number;
  retried: number;
  exhausted_endpoint_removed: number;
  companies_with_deliveries: number;
  endpoints_active: number;
  endpoints_revoked: number;
  api_keys_active: number;
  backlog: number;
}
export interface BackofficeIntegrationKindRow {
  provider: string;
  endpoints_active: number;
  endpoints_revoked: number;
  companies: number;
  deliveries: number;
  exhausted: number;
}
export interface BackofficeIntegrationEventRow {
  event_type: string;
  deliveries: number;
  exhausted: number;
}
export interface BackofficeIntegrationCompanyRow {
  company_id: string;
  company_name: string;
  suspended: boolean;
  providers: string[];
  endpoints_active: number;
  endpoints_revoked: number;
  first_connected_at: string | null;
  api_keys_active: number;
  api_key_last_used_at: string | null;
  deliveries: number;
  delivered: number;
  failed: number;
  exhausted: number;
  in_flight: number;
  last_event_at: string | null;
  last_delivered_at: string | null;
  last_error: string | null;
  last_status: number | null;
  last_error_at: string | null;
}
export type IntegrationAttentionReason = "exhausted" | "failing" | "backlog";
export interface BackofficeIntegrationAttentionRow {
  endpoint_id: string;
  company_id: string;
  company_name: string;
  provider: string;
  /** Bare host. Never a path — see the note above. */
  host: string;
  description: string | null;
  reason: IntegrationAttentionReason;
  deliveries: number;
  delivered: number;
  failed: number;
  exhausted: number;
  backlog: number;
  last_error: string | null;
  last_status: number | null;
  last_attempt_at: string | null;
  last_delivered_at: string | null;
  created_at: string | null;
}
export interface BackofficeIntegrations {
  window: LlmUsageWindow;
  window_days: number | null;
  window_start: string | null;
  connected_company_count: number;
  totals: BackofficeIntegrationsTotals;
  by_kind: BackofficeIntegrationKindRow[];
  by_event: BackofficeIntegrationEventRow[];
  by_company: BackofficeIntegrationCompanyRow[];
  needs_attention: BackofficeIntegrationAttentionRow[];
  needs_attention_truncated: boolean;
  truncated: boolean;
  total: number;
  has_more: boolean;
  limit: number;
  offset: number;
}

export async function fetchBackofficeIntegrations(
  window: LlmUsageWindow = "30d",
  params: { limit?: number; offset?: number } = {},
): Promise<BackofficeIntegrations> {
  return jsonOrThrow(
    await adminFetch(
      `/api/backoffice/integrations${buildParams({ window, ...params })}`,
    ),
  );
}

// FEAT-47e — the Integrations tab's third view: the MCP connector across
// every company. Counts only: the server returns no argument, no output, no
// person and no token (spec § 4.5 stores none; the invite replay body is
// never selected), so a row is a company plus what the assistant's own
// metadata document calls itself ("ChatGPT", "Claude").
export interface BackofficeMcpConnectorTotals {
  flag_on: boolean;
  companies_switched_on: number;
  companies_with_connections: number;
  connections_active: number;
  connections_revoked: number;
  people_connected: number;
  calls: number;
  ok: number;
  refused: number;
  throttled: number;
  crashed: number;
  running: number;
}
export interface BackofficeMcpConnectorToolRow {
  tool: string;
  rate_class: string;
  calls: number;
  ok: number;
  refused: number;
  throttled: number;
  crashed: number;
  mean_ms: number | null;
  max_ms: number | null;
}
export interface BackofficeMcpConnectorClientRow {
  client_name: string;
  kind: string;
  connections_active: number;
  connections_revoked: number;
  calls: number;
}
export interface BackofficeMcpConnectorCompanyRow {
  company_id: string;
  company_name: string;
  suspended: boolean;
  switched_on: boolean;
  connections_active: number;
  connections_revoked: number;
  people_connected: number;
  calls: number;
  ok: number;
  refused: number;
  throttled: number;
  crashed: number;
  last_call_at: string | null;
  first_connected_at: string | null;
}
export interface BackofficeMcpConnector {
  window: LlmUsageWindow;
  window_days: number | null;
  window_start: string | null;
  totals: BackofficeMcpConnectorTotals;
  by_tool: BackofficeMcpConnectorToolRow[];
  by_client: BackofficeMcpConnectorClientRow[];
  by_company: BackofficeMcpConnectorCompanyRow[];
  truncated: boolean;
  total: number;
  has_more: boolean;
  limit: number;
  offset: number;
}

export async function fetchBackofficeMcpConnector(
  window: LlmUsageWindow = "30d",
  params: { limit?: number; offset?: number } = {},
): Promise<BackofficeMcpConnector> {
  return jsonOrThrow(
    await adminFetch(
      `/api/backoffice/mcp-connector${buildParams({ window, ...params })}`,
    ),
  );
}

export async function fetchPlatformProblemReports(
  status: ProblemReportStatusFilter = "all",
  params: {
    q?: string;
    company_id?: string;
    limit?: number;
    offset?: number;
  } = {},
): Promise<PlatformProblemReports> {
  return jsonOrThrow(
    await adminFetch(
      `/api/backoffice/problem-reports${buildParams({ status, ...params })}`,
    ),
  );
}

// Triage toggle. Resolving emails the reporter unless `notify: false` — take the
// body from the generated schema so a backend rename breaks the build here
// instead of silently 422-ing at runtime.
export async function updatePlatformProblemReport(
  id: string,
  body: Schemas["UpdateProblemReportStatusIn"],
): Promise<PlatformProblemReportRow> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/problem-reports/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

// Operator reply + (by default) resolve — emails the recruiter back (MVP 4.0).
export async function replyToPlatformProblem(
  id: string,
  body: Schemas["ReplyProblemReportIn"],
): Promise<PlatformProblemReportRow> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/problem-reports/${id}/reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

// EP-15: delete a report's screenshot (the object, then the row; audited).
export async function deletePlatformProblemAttachment(
  id: string,
): Promise<PlatformProblemReportRow> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/problem-reports/${id}/attachment`, {
      method: "DELETE",
    }),
  );
}

// ── Recovery + platform operators (creator-only) — MVP 4.0 ────────────────
// Hand-written types mirror the backend Pydantic models in backend/api/
// backoffice.py. Run `npm run gen:api` once the backend is up to regenerate
// api.generated.ts; these can then be swapped for Schemas[...] if desired.

export type RecoveryEntity = "question" | "test" | "job" | "recruiter";

export interface BackofficeCompanyOption {
  id: string;
  name: string;
}
export interface BackofficeCompanyDirectory {
  items: BackofficeCompanyOption[];
  truncated: boolean;
}
export interface DeletedTestRow {
  id: string;
  name: string;
  deleted_at: string | null;
  archived_at: string | null;
  question_count: number;
}
export interface DeletedQuestionRow {
  id: string;
  title: string;
  type: string;
  difficulty: string;
  deleted_at: string | null;
  attached_test_count: number;
}
export interface DeletedJobRow {
  id: string;
  name: string;
  deleted_at: string | null;
}
export interface DeletedRecruiterRow {
  id: string;
  email: string;
  name: string;
  role: string;
  deleted_at: string | null;
  last_login_at: string | null;
}
export interface DeletedItemsCounts {
  tests: number;
  questions: number;
  jobs: number;
  recruiters: number;
}
export interface DeletedItems {
  company_id: string;
  company_name: string;
  /** Which list below is populated; the other three are empty. */
  entity: DeletedItemsEntity;
  /** Per-entity totals under the CURRENT filters — drives the tab badges. */
  counts: DeletedItemsCounts;
  tests: DeletedTestRow[];
  questions: DeletedQuestionRow[];
  jobs: DeletedJobRow[];
  recruiters: DeletedRecruiterRow[];
  total: number;
  has_more: boolean;
  limit: number;
  offset: number;
  /** `q` was dropped: teammate name/email are hidden at this access level, so
   * searching them would leak the values the redaction hides. */
  search_unavailable: boolean;
}
export interface RestoreResult {
  id: string;
  entity_type: string;
  entity_label: string;
  restored: boolean;
  // Questions that came back WITH the entity rather than as their own restore.
  // Only ever non-zero for a test whose delete predates the removal of the
  // smart cascade (BUG-2); 0 for every other entity.
  restored_questions: number;
}
export interface BackofficeAuditRow {
  id: string;
  actor_email: string;
  action: string;
  entity_type: string;
  entity_id: string;
  target_company_id: string | null;
  target_company_name: string | null;
  reason: string;
  detail: Record<string, unknown> | null;
  created_at: string;
}
export interface BackofficeAuditResponse {
  items: BackofficeAuditRow[];
  truncated: boolean;
  total: number;
  has_more: boolean;
  limit: number;
  offset: number;
}
export interface OperatorRow {
  id: string;
  email: string;
  name: string;
  company_name: string;
  platform_role: string;
  role: string;
  is_verified: boolean;
  last_login_at: string | null;
  created_at: string;
}
export interface OperatorsResponse {
  items: OperatorRow[];
  total: number;
  has_more: boolean;
  limit: number;
  offset: number;
}

// POST /operators now grants an EXISTING recruiter in place OR emails a brand-
// new person an operator invitation. The result discriminates the two.
export interface GrantOperatorResult {
  outcome: "granted" | "invited";
  operator: OperatorRow | null;
  email: string;
}

export interface CompanyLifecycleResult {
  id: string;
  name: string;
  suspended_at: string | null;
  suspended_reason: string | null;
  approved_at: string | null;
  declined_at: string | null;
  approval_state: "pending" | "approved" | "declined";
  traffit_enabled: boolean;
  identity_verification_enabled?: boolean;
  mcp_connector_enabled?: boolean;
  is_test?: boolean;
}
export interface RecruiterLifecycleResult {
  id: string;
  email: string;
  suspended: boolean;
}

// Mutations carry a structured `detail` (code + human message + collision ids)
// the UI surfaces inline. RecoveryError preserves it past throwIfError.
export class RecoveryError extends Error {
  status: number;
  code: string | null;
  detail: Record<string, unknown> | null;
  constructor(
    message: string,
    status: number,
    code: string | null,
    detail: Record<string, unknown> | null,
  ) {
    super(message);
    this.name = "RecoveryError";
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

// Friendly fallbacks for back-office error codes the server returns as a bare
// `{code}` (no `message`). When the server DOES send a `message` (e.g.
// NAME_COLLISION names the colliding item), that richer text wins.
const RECOVERY_ERROR_MESSAGES: Record<string, string> = {
  ALREADY_ACTIVE: "This item is already active — nothing to restore.",
  CONFIRM_REQUIRED: "Please confirm before continuing.",
  LAST_SUPERUSER: "Can't remove the last superuser.",
  ALREADY_OPERATOR:
    "This recruiter is already an operator — change their role instead.",
  ALREADY_THAT_ROLE: "This operator already has that role.",
  TARGET_UNVERIFIED:
    "The recruiter must verify their email before becoming a superuser.",
  UNASSIGNED_JOB_MISSING:
    "This company has no Unassigned job to re-home the test under.",
  NAME_COLLISION:
    "A live item with that name already exists. Rename it first, then restore.",
  COMPANY_SUSPENDED: "This company is suspended.",
  ALREADY_SUSPENDED: "This company is already suspended.",
  NOT_SUSPENDED: "This company is not suspended.",
  CANNOT_SUSPEND_PLATFORM: "The platform workspace can't be suspended.",
  CANNOT_SUSPEND_SELF: "You can't suspend your own account.",
  LAST_OWNER:
    "This is the company's only owner — add another owner before suspending.",
  ACCOUNT_SUSPENDED:
    "That account is suspended — restore it via Recovery first.",
  RECRUITER_REMOVED:
    "That account was removed — restore it via Recovery first.",
  // Global Library: the two refusals of a question delete. Without these the
  // toast printed the raw code.
  REFERENCED_BY_TEMPLATE:
    "In use by a template — remove it from the template first.",
  REFERENCED_BY_TENANT_TESTS:
    "In use by a customer's test — unpublish it instead of deleting.",
};

async function recoveryError(res: Response): Promise<RecoveryError> {
  let code: string | null = null;
  let message = `HTTP ${res.status}`;
  let detail: Record<string, unknown> | null = null;
  try {
    const body = (await res.json()) as { detail?: unknown };
    const d = body?.detail;
    if (typeof d === "string") {
      message = d;
    } else if (d && typeof d === "object") {
      detail = d as Record<string, unknown>;
      code = (detail.code as string | undefined) ?? null;
      message =
        (detail.message as string | undefined) ??
        (code ? RECOVERY_ERROR_MESSAGES[code] : undefined) ??
        code ??
        message;
    }
  } catch {
    /* fall through to the generic message */
  }
  return new RecoveryError(message, res.status, code, detail);
}

const RESTORE_PATH_SEGMENT: Record<RecoveryEntity, string> = {
  question: "questions",
  test: "tests",
  job: "jobs",
  recruiter: "recruiters",
};

export async function fetchBackofficeCompanyDirectory(
  q?: string,
): Promise<BackofficeCompanyDirectory> {
  const qs = q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/company-directory${qs}`),
  );
}

export type DeletedItemsEntity = "tests" | "questions" | "jobs" | "recruiters";
export type DeletedWithin = "7d" | "30d" | "90d" | "all";

export interface DeletedItemsParams {
  entity?: DeletedItemsEntity;
  q?: string;
  deleted_within?: DeletedWithin;
  limit?: number;
  offset?: number;
}

export async function fetchCompanyDeletedItems(
  companyId: string,
  params: DeletedItemsParams = {},
): Promise<DeletedItems> {
  return jsonOrThrow(
    await adminFetch(
      `/api/backoffice/companies/${companyId}/deleted-items${buildParams(params)}`,
    ),
  );
}

export async function restoreDeletedItem(
  companyId: string,
  entity: RecoveryEntity,
  entityId: string,
  reason: string,
): Promise<RestoreResult> {
  const res = await adminFetch(
    `/api/backoffice/companies/${companyId}/${RESTORE_PATH_SEGMENT[entity]}/${entityId}/restore`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason, confirm: true }),
    },
  );
  if (!res.ok) throw await recoveryError(res);
  return (await res.json()) as RestoreResult;
}

export async function fetchRecoveryAudit(
  companyId?: string,
  limit = 50,
  offset = 0,
): Promise<BackofficeAuditResponse> {
  const params = new URLSearchParams();
  if (companyId) params.set("company_id", companyId);
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/recovery-audit?${params.toString()}`),
  );
}

// ── Superuser Global Library authoring console (MVP 4.0) ────────────────────
//
// Superuser-only /api/backoffice/library/* — author the seeded questions +
// templates that every tenant consumes. Reuses adminFetch (session cookie) and
// the structured recoveryError parser (endpoints return {code, ...} details).

export type BoStatusFilter = "draft" | "published" | "all";
export type BoQuestionInput =
  | McqQuestionInput
  | McqMultiQuestionInput
  | CodeQuestionInput
  // FEAT-14 — a Global Library written question, scored by hand in the tenant.
  | OpenEndedQuestionInput
  // EDIT only — a true/false row a platform template import brought in (FEAT-10).
  | TrueFalseQuestionInput;

export interface BoLibraryQuestionListItem {
  id: string;
  type: QuestionType;
  difficulty: Difficulty;
  title: string;
  prompt_preview: string;
  tags: { id: string; name: string }[];
  published: boolean;
  seed_origin: string | null;
  seed_key: string | null;
  used_by_template_count: number;
  updated_at: string;
  // CP-6 tier: null = available on every plan, else the plan needed to open it.
  min_plan: string | null;
}
export interface BoLibraryQuestionListResponse {
  items: BoLibraryQuestionListItem[];
  total: number;
  has_more: boolean;
  limit: number;
  offset: number;
}

export interface BoLibraryQuestionInput {
  question: BoQuestionInput;
  tags: string[];
  // CP-6. Console-authored questions default to available on every plan — the
  // corpus rule buckets by technology, and a console seed_key has none.
  min_plan?: string | null;
}

// CP-6: which slice of the library the console is looking at.
export type BoTierFilter = "free" | "tiered" | "all";

export async function boListQuestions(params: {
  q?: string;
  type?: string;
  difficulty?: string;
  technology?: string;
  status?: BoStatusFilter;
  tier?: BoTierFilter;
  limit?: number;
  offset?: number;
}): Promise<BoLibraryQuestionListResponse> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/library/questions${buildParams(params)}`),
  );
}

export async function boGetQuestion(id: string): Promise<LibraryQuestionDetail> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/library/questions/${id}`),
  );
}

async function boWrite<T>(
  path: string,
  method: string,
  body?: unknown,
): Promise<T> {
  const res = await adminFetch(`/api/backoffice/library${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw await recoveryError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function boCreateQuestion(
  body: BoLibraryQuestionInput,
): Promise<LibraryQuestionDetail> {
  return boWrite("/questions", "POST", body);
}
export function boUpdateQuestion(
  id: string,
  body: BoLibraryQuestionInput,
): Promise<LibraryQuestionDetail> {
  return boWrite(`/questions/${id}`, "PATCH", body);
}
export function boPublishQuestion(id: string): Promise<LibraryQuestionDetail> {
  return boWrite(`/questions/${id}/publish`, "POST");
}
export function boUnpublishQuestion(id: string): Promise<LibraryQuestionDetail> {
  return boWrite(`/questions/${id}/unpublish`, "POST");
}
/**
 * CP-6: move one Global Library question between plan tiers. `minPlan = null`
 * frees it.
 *
 * Its own endpoint rather than a field on the question PATCH: that PATCH stamps
 * `seed_origin="console"`, permanently taking the row out of loader management.
 * Curating a tier must not have that consequence. Rejects with a 409
 * REFERENCED_BY_TEMPLATE while a live template embeds the question — template
 * members are available on every plan by construction.
 */
export function boSetQuestionTier(
  id: string,
  minPlan: string | null,
  reason: string,
): Promise<LibraryQuestionDetail> {
  return boWrite(`/questions/${id}/tier`, "POST", {
    min_plan: minPlan,
    reason,
    confirm: true,
  });
}
export function boDeleteQuestion(id: string, reason: string): Promise<void> {
  return boWrite(`/questions/${id}`, "DELETE", { reason, confirm: true });
}

// FEAT-20: delete several Global Library questions in one request. The single
// delete's two 409s become per-id reasons, so one protected question is skipped
// instead of failing the batch; `not_found` covers an id that is gone already.
// One reason is logged for the whole batch.
export type BoBulkDeleteRejectReason =
  | "not_found"
  | "referenced_by_template"
  | "referenced_by_tenant_tests";
export interface BoBulkDeleteQuestionsResponse {
  accepted: string[];
  rejected: { id: string; reason: BoBulkDeleteRejectReason }[];
}
export function boDeleteQuestionsBulk(
  ids: string[],
  reason: string,
): Promise<BoBulkDeleteQuestionsResponse> {
  return boWrite("/questions/delete-bulk", "POST", {
    question_ids: ids,
    reason,
    confirm: true,
  });
}
export async function boListTags(): Promise<{ tags: { id: string; name: string }[] }> {
  return jsonOrThrow(await adminFetch(`/api/backoffice/library/tags`));
}

// Bulk-import questions into the Global Library console (UX-1). Reuses the tenant
// importers' parsers + modals; only the POST target differs — imported rows land
// as console-owned DRAFTS under the Global Library. Same {accepted,rejected,total}
// response shape as the tenant importers.
export function boImportQuestionsBulk(body: {
  rows: QuestionImportRowInput[];
}): Promise<QuestionImportResponse> {
  return boWrite("/questions/import-bulk", "POST", body);
}
export function boImportCodeQuestionsBulk(body: {
  rows: CodeQuestionImportRowInput[];
}): Promise<QuestionImportResponse> {
  return boWrite("/questions/import-bulk-code", "POST", body);
}

// --- Templates ---

/** BUG-12: "general" = the Global Library gallery every tenant sees; "custom"
 *  = one a company saved from its own test. The console can READ both and
 *  write only the first. */
export type BoTemplateKind = "general" | "custom";
export type BoTemplateScope = "general" | "custom" | "all";

export interface BoTemplateListItem {
  id: string;
  name: string;
  role: string;
  level: string;
  published: boolean;
  kind: BoTemplateKind;
  company_id: string | null;
  company_name: string | null;
  seed_origin: string | null;
  seed_key: string | null;
  timing_mode: string;
  total_time_limit_seconds: number;
  question_count: number;
  mcq_count: number;
  // FEAT-14 — written questions used to be counted as MCQ here.
  open_ended_count: number;
  code_count: number;
  section_count: number;
  updated_at: string;
}
export interface BoTemplateListResponse {
  items: BoTemplateListItem[];
  total: number;
  has_more: boolean;
  limit: number;
  offset: number;
}
export interface BoTemplateMember {
  question_id: string;
  position: number;
  title: string;
  type: QuestionType;
  difficulty: Difficulty;
  published: boolean;
  answer_required: boolean;
  time_limit_seconds: number | null;
}
export interface BoTemplateDetail {
  id: string;
  name: string;
  role: string;
  level: string;
  description: string | null;
  published: boolean;
  kind: BoTemplateKind;
  company_id: string | null;
  company_name: string | null;
  seed_origin: string | null;
  seed_key: string | null;
  timing_mode: string;
  total_time_limit_seconds: number;
  sections: Record<string, unknown>[] | null;
  show_score_to_candidate: boolean;
  pass_mark_enabled: boolean;
  pass_threshold_pct: number;
  shuffle_questions: boolean;
  question_pool_enabled: boolean;
  pool_draw_count: number;
  allow_back_navigation: boolean;
  auto_resend_enabled: boolean;
  updated_at: string;
  questions: BoTemplateMember[];
}
export interface BoTemplateQuestionRef {
  question_id: string;
  answer_required?: boolean;
  time_limit_seconds?: number | null;
}
export interface BoTemplateInput {
  name: string;
  role: string;
  level: string;
  description?: string | null;
  total_time_limit_seconds: number;
  timing_mode: string;
  sections?: Record<string, unknown>[] | null;
  show_score_to_candidate: boolean;
  pass_mark_enabled: boolean;
  pass_threshold_pct: number;
  shuffle_questions: boolean;
  question_pool_enabled: boolean;
  pool_draw_count: number;
  allow_back_navigation: boolean;
  auto_resend_enabled: boolean;
  questions: BoTemplateQuestionRef[];
}

export async function boListTemplates(params: {
  q?: string;
  role?: string;
  level?: string;
  status?: BoStatusFilter;
  /** Omitted = "general", the pre-BUG-12 behaviour. */
  scope?: BoTemplateScope;
  company_id?: string;
  limit?: number;
  offset?: number;
}): Promise<BoTemplateListResponse> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/library/templates${buildParams(params)}`),
  );
}
export async function boGetTemplate(id: string): Promise<BoTemplateDetail> {
  return jsonOrThrow(await adminFetch(`/api/backoffice/library/templates/${id}`));
}
export async function boPreviewTemplate(
  id: string,
): Promise<CandidatePreviewResponse> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/library/templates/${id}/candidate-preview`),
  );
}
export function boCreateTemplate(body: BoTemplateInput): Promise<BoTemplateDetail> {
  return boWrite("/templates", "POST", body);
}
export function boUpdateTemplate(
  id: string,
  body: BoTemplateInput,
): Promise<BoTemplateDetail> {
  return boWrite(`/templates/${id}`, "PATCH", body);
}
export function boPublishTemplate(id: string): Promise<BoTemplateDetail> {
  return boWrite(`/templates/${id}/publish`, "POST");
}
export function boUnpublishTemplate(id: string): Promise<BoTemplateDetail> {
  return boWrite(`/templates/${id}/unpublish`, "POST");
}
export function boDeleteTemplate(id: string, reason: string): Promise<void> {
  return boWrite(`/templates/${id}`, "DELETE", { reason, confirm: true });
}
/** FEAT-10 — an EF-12 template file becomes a GLOBAL template. Lands as a
 *  draft, with any questions the file authors, so nothing reaches a tenant's
 *  gallery until Publish. Same body the recruiter importer takes. */
export function boImportTemplate(
  body: Record<string, unknown>,
): Promise<BoTemplateDetail> {
  return boWrite("/templates/import", "POST", body);
}

export async function fetchOperators(
  params: OperatorListParams = {},
): Promise<OperatorsResponse> {
  return jsonOrThrow(
    await adminFetch(`/api/backoffice/operators${buildParams(params)}`),
  );
}

// Every operator mutation is the same shape: adminFetch with a JSON body that
// always carries `confirm: true`, RecoveryError on failure (preserves the
// structured {code, message} the dialog surfaces), then an OperatorRow back.
async function operatorRequest(
  path: string,
  method: "POST" | "PATCH" | "DELETE",
  body: Record<string, unknown>,
): Promise<OperatorRow> {
  const res = await adminFetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...body, confirm: true }),
  });
  if (!res.ok) throw await recoveryError(res);
  return (await res.json()) as OperatorRow;
}

// Grant/invite is its own call (not operatorRequest) because its result is a
// GrantOperatorResult discriminator, not an OperatorRow.
export async function grantOperator(body: {
  recruiter_email?: string;
  recruiter_id?: string;
  role: "support" | "superuser" | "viewer";
  reason: string;
  message?: string;
}): Promise<GrantOperatorResult> {
  const res = await adminFetch(`/api/backoffice/operators`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...body, confirm: true }),
  });
  if (!res.ok) throw await recoveryError(res);
  return (await res.json()) as GrantOperatorResult;
}

// Reversible suspend/reactivate lifecycle (superuser-only). All share the
// reason+confirm body + RecoveryError shape. Restore of a suspended RECRUITER
// goes through the existing `restoreDeletedItem(companyId, "recruiter", ...)`.
async function lifecycleRequest<T>(path: string, reason: string): Promise<T> {
  const res = await adminFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason, confirm: true }),
  });
  if (!res.ok) throw await recoveryError(res);
  return (await res.json()) as T;
}

export async function suspendCompany(
  companyId: string,
  reason: string,
): Promise<CompanyLifecycleResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/suspend`,
    reason,
  );
}

export async function reactivateCompany(
  companyId: string,
  reason: string,
): Promise<CompanyLifecycleResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/reactivate`,
    reason,
  );
}

// Operator-controlled Traffit entitlement (MVP 4.0 item 7). Enable un-hides the
// Traffit surface; disable hard-stops it AND revokes the active connection.
export async function enableCompanyTraffit(
  companyId: string,
  reason: string,
): Promise<CompanyLifecycleResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/traffit/enable`,
    reason,
  );
}

export async function disableCompanyTraffit(
  companyId: string,
  reason: string,
): Promise<CompanyLifecycleResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/traffit/disable`,
    reason,
  );
}

// EF-3: the operator's per-company identity-verification switch
// (superuser-only). "Enterprise, on request, charged per candidate" is this
// switch — enabling lets the company's recruiters turn the check on per test.
export async function enableCompanyIdentity(
  companyId: string,
  reason: string,
): Promise<CompanyLifecycleResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/identity/enable`,
    reason,
  );
}

export async function disableCompanyIdentity(
  companyId: string,
  reason: string,
): Promise<CompanyLifecycleResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/identity/disable`,
    reason,
  );
}

// FEAT-47: the operator's per-company switch of the MCP (AI assistant)
// connector (superuser-only). Both it and the global MCP_CONNECTOR_ENABLED
// must hold before anyone in the company can connect an assistant.
export async function enableCompanyMcpConnector(
  companyId: string,
  reason: string,
): Promise<CompanyLifecycleResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/mcp-connector/enable`,
    reason,
  );
}

export async function disableCompanyMcpConnector(
  companyId: string,
  reason: string,
): Promise<CompanyLifecycleResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/mcp-connector/disable`,
    reason,
  );
}

// FEAT-51: the operator's test-company flag — the twin of the Traffit pair.
export async function markCompanyTest(
  companyId: string,
  reason: string,
): Promise<CompanyLifecycleResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/test-company/mark`,
    reason,
  );
}

export async function unmarkCompanyTest(
  companyId: string,
  reason: string,
): Promise<CompanyLifecycleResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/test-company/unmark`,
    reason,
  );
}

/** FEAT-51: the hard delete of a TEST company — every row, irreversible.
 *  `name` is the company's exact name typed by the operator (the server
 *  compares it byte for byte after a strip); refused on an unflagged company
 *  (409 NOT_A_TEST_COMPANY), a system workspace (400), a wrong name (422). */
export interface DeleteCompanyResult {
  id: string;
  name: string;
  deleted: Record<string, number>;
  cleared: Record<string, number>;
  recruiters_signed_out: number;
  files_deleted: number | null;
  audit_id: string;
}

export async function deleteCompany(
  companyId: string,
  name: string,
  reason: string,
): Promise<DeleteCompanyResult> {
  const res = await adminFetch(`/api/backoffice/companies/${companyId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason, confirm: true, name }),
  });
  if (!res.ok) throw await recoveryError(res);
  return (await res.json()) as DeleteCompanyResult;
}

/** FEAT-51: a company's own statistics, read cross-tenant — the SAME body
 *  the company's /admin/statistics returns (`admin._build_statistics`). */
export async function fetchBackofficeCompanyStatistics(
  companyId: string,
  window: StatsWindow = "30d",
  testId?: string | null,
): Promise<StatisticsResponse> {
  const qs = new URLSearchParams({ window });
  if (testId) qs.set("test_id", testId);
  return jsonOrThrow(
    await adminFetch(
      `/api/backoffice/companies/${companyId}/statistics?${qs.toString()}`,
    ),
  );
}

// Manual signup approval (MVP 4.0 admission control). Approve admits a pending
// (or re-admits a declined) company; decline rejects it. `reason` is optional
// for approve (the backend defaults it), required for decline.
export async function approveCompany(
  companyId: string,
  reason: string,
): Promise<CompanyLifecycleResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/approve`,
    reason,
  );
}

export async function declineCompany(
  companyId: string,
  reason: string,
): Promise<CompanyLifecycleResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/decline`,
    reason,
  );
}

/** Grant a company a plan, overriding whatever Paddle says (CP-3).
 *
 * Writes `company_plan_override`, never `company_subscription` — that table has
 * one writer, the Paddle webhook. Shares `lifecycleRequest`'s body shape
 * (`{reason, confirm}`) and its RecoveryError parsing, with `plan` added.
 */
export async function setCompanyPlan(
  companyId: string,
  plan: OverridablePlan,
  reason: string,
): Promise<BackofficeCompanyPlanResult> {
  const res = await adminFetch(`/api/backoffice/companies/${companyId}/plan`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ plan, reason, confirm: true }),
  });
  if (!res.ok) throw await recoveryError(res);
  return (await res.json()) as BackofficeCompanyPlanResult;
}

/** Drop the override and hand the company back to Paddle. Idempotent. */
export async function clearCompanyPlan(
  companyId: string,
  reason: string,
): Promise<BackofficeCompanyPlanResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/plan/clear`,
    reason,
  );
}

// FEAT-9: the operator's view of a company's monthly AI allowance — WITH the
// figures (`used_usd` / `allowance_usd`), which the customer's meter never
// carries. Rides on `RecruiterDetailRead.ai_allowance`.
export type BackofficeAiAllowance = Schemas["AiAllowanceOperatorRead"];
export type BackofficeCompanyAiAllowanceResult =
  Schemas["CompanyAiAllowanceResult"];

/** Give a workspace its own monthly AI allowance (a hidden USD budget; 0 =
 * AI refused), replacing the platform default. Reason + confirm, like every
 * back-office write; same RecoveryError parsing as the plan override. */
export async function setCompanyAiAllowance(
  companyId: string,
  allowanceUsd: number,
  reason: string,
): Promise<BackofficeCompanyAiAllowanceResult> {
  const res = await adminFetch(
    `/api/backoffice/companies/${companyId}/ai-allowance`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ allowance_usd: allowanceUsd, reason, confirm: true }),
    },
  );
  if (!res.ok) throw await recoveryError(res);
  return (await res.json()) as BackofficeCompanyAiAllowanceResult;
}

/** Drop the workspace's own AI allowance and follow the platform default again. Idempotent. */
export async function clearCompanyAiAllowance(
  companyId: string,
  reason: string,
): Promise<BackofficeCompanyAiAllowanceResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/ai-allowance/clear`,
    reason,
  );
}

// FEAT-35: the operator's view of a company's free-trial clock — the raw
// columns, read even when an override or a paid row masks them, plus why
// they are masked and whether the two actions are offered. Rides on
// `CompanyDetailRead.trial_clock`.
export type BackofficeTrialClock = Schemas["TrialClockRead"];
export type BackofficeCompanyTrialResult = Schemas["CompanyTrialResult"];

/** Set the day a company's free trial ends (UTC; it ends at 23:59:59 of that
 * day). A future day on an ended or never-started trial reopens the walled
 * workspace. `endsOn` is `YYYY-MM-DD`. Reason + confirm, like every
 * back-office write. */
export async function setCompanyTrialEnd(
  companyId: string,
  endsOn: string,
  reason: string,
): Promise<BackofficeCompanyTrialResult> {
  const res = await adminFetch(`/api/backoffice/companies/${companyId}/trial`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ends_on: endsOn, reason, confirm: true }),
  });
  if (!res.ok) throw await recoveryError(res);
  return (await res.json()) as BackofficeCompanyTrialResult;
}

/** End a company's running free trial now — the server walls the workspace
 * on its next request. Nothing is deleted. */
export async function endCompanyTrial(
  companyId: string,
  reason: string,
): Promise<BackofficeCompanyTrialResult> {
  return lifecycleRequest(
    `/api/backoffice/companies/${companyId}/trial/end`,
    reason,
  );
}

export async function suspendRecruiter(
  recruiterId: string,
  reason: string,
): Promise<RecruiterLifecycleResult> {
  return lifecycleRequest(
    `/api/backoffice/recruiters/${recruiterId}/suspend`,
    reason,
  );
}

export async function changeOperatorRole(
  recruiterId: string,
  role: "support" | "superuser" | "viewer",
  reason: string,
): Promise<OperatorRow> {
  return operatorRequest(`/api/backoffice/operators/${recruiterId}`, "PATCH", {
    role,
    reason,
  });
}

export async function revokeOperator(
  recruiterId: string,
  reason: string,
): Promise<OperatorRow> {
  return operatorRequest(`/api/backoffice/operators/${recruiterId}`, "DELETE", {
    reason,
  });
}
